<template>
  <div>
    <AIVDialog
      v-model="isVisible"
      :title="getDialogTitle"
      width="90%"
      height="85vh"
      class="shift-modal"
    >
      <el-form
        v-if="!showAddressSelection && !showCaregiverTaskAssignment"
        ref="shiftForm"
        :model="shift"
        label-position="top"
        label-width="120px"
        class="full-width-form"
      >
        <!-- Shift Type Toggle - Show when creating new shift OR editing a future recurring instance -->
        <div
          v-if="!shiftBeingEdited?.id || isEditingRecurringInstance"
          class="shift-type-toggle-section"
        >
          <div class="button-toggle-row">
            <el-button
              :type="!isRecurringMode ? 'primary' : 'default'"
              @click="handleSingleModeClick"
              class="toggle-button"
            >
              Single Shift
            </el-button>
            <el-button
              :type="isRecurringMode ? 'primary' : 'default'"
              @click="handleRecurringModeClick"
              class="toggle-button"
            >
              Recurring Shift
            </el-button>
          </div>
        </div>

        <!-- Payer Type Toggle -->
        <div class="payer-toggle-section">
          <div class="button-toggle-row">
            <el-button
              v-if="!isPayerLocked || shift.payer === 'VR'"
              :type="shift.payer === 'VR' ? 'primary' : 'default'"
              @click="shift.payer = 'VR'"
              class="toggle-button"
            >
              VR
            </el-button>
            <el-button
              v-if="!isPayerLocked || shift.payer === 'DSPD'"
              :type="shift.payer === 'DSPD' ? 'primary' : 'default'"
              @click="shift.payer = 'DSPD'"
              class="toggle-button"
            >
              DSPD
            </el-button>
            <el-button
              v-if="!isPayerLocked || shift.payer === 'Non Service'"
              :type="shift.payer === 'Non Service' ? 'primary' : 'default'"
              @click="shift.payer = 'Non Service'"
              class="toggle-button"
            >
              Non Service
            </el-button>
          </div>
        </div>

        <!-- DSPD/PP: Shift Type Toggle (shown right below payer toggle when DSPD or Private) -->
        <div v-if="shift.payer === 'DSPD' || shift.payer === 'Private'" class="payer-toggle-section">
          <div class="button-toggle-row">
            <el-button
              :type="shift.shift_type === '1-on-1' ? 'primary' : 'default'"
              @click="shift.shift_type = '1-on-1'"
              class="toggle-button"
            >
              1-on-1
            </el-button>
            <el-button
              :type="shift.shift_type === 'Group' ? 'primary' : 'default'"
              @click="shift.shift_type = 'Group'"
              class="toggle-button"
            >
              Group
            </el-button>
          </div>
        </div>

        <!-- Date Fields - Single Shift -->
        <div v-if="shiftMode === 'single'" class="date-fields-section">
          <div class="date-fields-flex">
            <div class="date-field-col">
              <div class="caregiver-header">
                <label class="field-label">Date</label>
                <el-checkbox v-model="isVirtualShift" class="include-admins-toggle">
                  Virtual Shift
                </el-checkbox>
              </div>
              <el-input
                type="date"
                v-model="shift.date"
                placeholder="Date"
                class="w-100"
              />
            </div>
            <div class="date-field-col">
              <label class="field-label">Start Time</label>
              <AIVTimeSelect
                v-model="shift.scheduled_start"
                :allowExactMinutes="true"
                @ok-clicked="handleTimeOkClick('scheduled_start')"
              />
            </div>
            <div class="date-field-col">
              <label class="field-label">End Time</label>
              <AIVTimeSelect
                v-model="shift.scheduled_end"
                :allowExactMinutes="true"
                @ok-clicked="handleTimeOkClick('scheduled_end')"
              />
            </div>
          </div>
        </div>

        <!-- Date Fields - Recurring Shift -->
        <div v-if="shiftMode === 'recurring'" class="date-fields-section">
          <div class="date-fields-flex">
            <div class="date-field-col">
              <div class="caregiver-header">
                <label class="field-label">Start Date</label>
                <el-checkbox v-model="isVirtualShift" class="include-admins-toggle">
                  Virtual Shift
                </el-checkbox>
              </div>
              <el-input
                type="date"
                v-model="shift.recurring_start_date"
                placeholder="Start Date"
                class="w-100"
              />
            </div>
            <div class="date-field-col">
              <label class="field-label">End Date (optional)</label>
              <el-input
                type="date"
                v-model="shift.recurring_end_date"
                placeholder="End Date"
                class="w-100"
              />
            </div>
            <div class="date-field-col">
              <label class="field-label">Start Time</label>
              <AIVTimeSelect
                v-model="shift.scheduled_start"
                :allowExactMinutes="true"
                @ok-clicked="handleTimeOkClick('scheduled_start')"
              />
            </div>
            <div class="date-field-col">
              <label class="field-label">End Time</label>
              <AIVTimeSelect
                v-model="shift.scheduled_end"
                :allowExactMinutes="true"
                @ok-clicked="handleTimeOkClick('scheduled_end')"
              />
            </div>
          </div>

          <!-- Days of the Week Selection -->
          <div class="weekdays-selection">
            <div class="weekdays-row">
              <label class="field-label weekdays-label">Days of the Week</label>
              <div class="weekday-checkboxes">
                <el-checkbox-group v-model="shift.selectedWeekdays">
                  <el-checkbox
                    v-for="day in weekdayOptions"
                    :key="day.value"
                    :label="day.value"
                  >
                    {{ day.label }}
                  </el-checkbox>
                </el-checkbox-group>
              </div>
            </div>
          </div>
        </div>

        <!-- Timezone Display (hidden when client timezone matches browser) -->
        <div v-if="shift.clientId && !inferringTimezone && (showTimezoneWarning || !shift.timezone)" class="timezone-display-row" style="margin-bottom: 10px;">
          <div class="date-fields-flex">
            <div class="date-field-col" style="max-width: 350px;">
              <label class="field-label">Timezone</label>
              <div v-if="inferredTimezone" class="timezone-readonly" style="padding: 8px 12px; background-color: #656565; border-radius: 4px; color: #fff;">
                {{ formatTimezoneLabel(inferredTimezone) }}
              </div>
              <AIVSelect
                v-else
                :value="shift.timezone"
                @input="shift.timezone = $event"
                :options="timezoneOptions"
                placeholder="Select timezone (required)"
                :custom-style="{ backgroundColor: '#656565', minWidth: '100%' }"
              />
            </div>
          </div>
          <div v-if="showTimezoneWarning" style="margin-top: 8px; margin-left: 4px; padding: 8px 12px; border-left: 3px solid #f56c6c; background: rgba(245, 108, 108, 0.08); border-radius: 0 4px 4px 0;">
            <div style="color: #f56c6c; font-weight: bold; font-size: 13px;">⚠ Your browser is in {{ shortTzName(browserTimezone) }}, but this client is in {{ shortTzName(shift.timezone) }}.</div>
            <div v-if="timezoneConversionHint" style="color: #e6a23c; font-size: 12px; margin-top: 4px; line-height: 1.5;">
              {{ timezoneConversionHint }}
            </div>
          </div>
        </div>

        <!-- VR: Client, Service Codes, Caregiver Row -->
        <div v-if="shift.payer === 'VR'" class="client-caregiver-row">
          <div class="client-section">
            <div class="client-header">
              <label class="field-label">Client</label>
            </div>
            <el-tooltip
              :content="getClientTooltipContent"
              :disabled="!shouldShowClientTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.clientId"
                  @input="shift.clientId = $event"
                  :options="
                    filteredClients.map((client) => ({
                      id: client.id,
                      name: client.name,
                    }))
                  "
                  placeholder="Select client"
                  :disabled="isClientDropdownDisabled"
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
          <div class="service-codes-section">
            <div class="client-header">
              <label class="field-label">Service Codes</label>
            </div>
            <el-tooltip
              :content="getServiceCodesTooltipContent"
              :disabled="!shouldShowServiceCodesTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.selectedServiceCodes"
                  @input="updateSelectedServiceCodes"
                  :options="
                    availableServiceCodes.map((sc) => ({
                      id: sc.id,
                      name: sc.code || sc.name,
                    }))
                  "
                  :placeholder="getServiceCodesPlaceholder"
                  :multiSelect="true"
                  :disabled="isServiceCodesDropdownDisabled"
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
          <div class="caregiver-section">
            <div class="caregiver-header">
              <label class="field-label">
                Caregiver{{ isCaregiverMultiSelect ? "s" : "" }}
              </label>
              <el-checkbox
                v-if="!isEmploymentSpecialist"
                v-model="includeAdmins"
                @change="onIncludeAdminsChange"
                class="include-admins-toggle"
              >
                Include Admins
              </el-checkbox>
              <el-checkbox
                v-if="!isEmploymentSpecialist"
                v-model="showAllCaregivers"
                @change="onShowAllCaregiversChange"
                class="include-admins-toggle"
              >
                Show all caregivers
              </el-checkbox>
            </div>
            <el-tooltip
              :content="getCaregiverTooltipContent"
              :disabled="!shouldShowCaregiverTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.caregiverId"
                  @input="shift.caregiverId = $event"
                  :options="filteredCaregivers"
                  :placeholder="getCaregiverPlaceholder"
                  :multiSelect="isCaregiverMultiSelect"
                  :disabled="
                    isCaregiverDropdownDisabled || isLoadingAvailableCaregivers
                  "
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
        </div>

        <!-- Non Service: Service Code + Caregiver Row -->
        <div v-if="shift.payer === 'Non Service'" class="shift-details-row">
          <div class="field-section">
            <div class="client-header">
              <label class="field-label">Service Code</label>
            </div>
            <el-tooltip
              :content="getServiceCodesTooltipContent"
              :disabled="!shouldShowServiceCodesTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.selectedServiceCodes"
                  @input="updateSelectedServiceCodes"
                  :options="
                    availableServiceCodes.map((sc) => ({
                      id: sc.id,
                      name: sc.name,
                    }))
                  "
                  :placeholder="getServiceCodesPlaceholder"
                  :multiSelect="false"
                  :disabled="isServiceCodesDropdownDisabled"
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
          <div class="field-section">
            <div class="caregiver-header">
              <label class="field-label">
                Caregiver{{ isCaregiverMultiSelect ? "s" : "" }}
              </label>
              <el-checkbox
                v-if="!isEmploymentSpecialist"
                v-model="includeAdmins"
                @change="onIncludeAdminsChange"
                class="include-admins-toggle"
              >
                Include Admins
              </el-checkbox>
              <el-checkbox
                v-if="!isEmploymentSpecialist"
                v-model="showAllCaregivers"
                @change="onShowAllCaregiversChange"
                class="include-admins-toggle"
              >
                Show all caregivers
              </el-checkbox>
            </div>
            <el-tooltip
              :content="getCaregiverTooltipContent"
              :disabled="!shouldShowCaregiverTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.caregiverId"
                  @input="shift.caregiverId = $event"
                  :options="filteredCaregivers"
                  :placeholder="getCaregiverPlaceholder"
                  :multiSelect="isCaregiverMultiSelect"
                  :disabled="
                    isCaregiverDropdownDisabled || isLoadingAvailableCaregivers
                  "
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
        </div>

        <!-- DSPD 1-on-1: Client, Service Code, Caregiver Row -->
        <div v-if="shift.payer === 'DSPD' && shift.shift_type === '1-on-1'" class="client-caregiver-row">
          <div class="client-section">
            <div class="client-header">
              <label class="field-label">Client</label>
            </div>
            <el-tooltip
              :content="getClientTooltipContent"
              :disabled="!shouldShowClientTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.clientId"
                  @input="shift.clientId = $event"
                  :options="
                    filteredClients.map((client) => ({
                      id: client.id,
                      name: client.name,
                    }))
                  "
                  placeholder="Select client"
                  :disabled="isClientDropdownDisabled"
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
          <div class="service-codes-section">
            <div class="client-header">
              <label class="field-label">Service Code</label>
            </div>
            <el-tooltip
              :content="getServiceCodesTooltipContent"
              :disabled="!shouldShowServiceCodesTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.selectedServiceCodes"
                  @input="updateSelectedServiceCodes"
                  :options="
                    availableServiceCodes.map((sc) => ({
                      id: sc.id,
                      name: sc.code || sc.name,
                    }))
                  "
                  :placeholder="getServiceCodesPlaceholder"
                  :multiSelect="false"
                  :disabled="isServiceCodesDropdownDisabled"
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
          <div class="caregiver-section">
            <div class="caregiver-header">
              <label class="field-label">Caregiver</label>
              <el-checkbox
                v-if="!isEmploymentSpecialist"
                v-model="includeAdmins"
                @change="onIncludeAdminsChange"
                class="include-admins-toggle"
              >
                Include Admins
              </el-checkbox>
              <el-checkbox
                v-if="!isEmploymentSpecialist"
                v-model="showAllCaregivers"
                @change="onShowAllCaregiversChange"
                class="include-admins-toggle"
              >
                Show all caregivers
              </el-checkbox>
            </div>
            <el-tooltip
              :content="getCaregiverTooltipContent"
              :disabled="!shouldShowCaregiverTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.caregiverId"
                  @input="shift.caregiverId = $event"
                  :options="filteredCaregivers"
                  :placeholder="getCaregiverPlaceholder"
                  :multiSelect="false"
                  :disabled="
                    isCaregiverDropdownDisabled || isLoadingAvailableCaregivers
                  "
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
        </div>

        <!-- Shadow caregivers — non-group shifts only. Always the
             "shadow" role (no picker). Not offered on recurring create /
             whole-series edits (shadows are per-occurrence). -->
        <div
          v-if="showAdditionalCaregivers"
          class="shift-details-row additional-caregivers-section"
        >
          <div class="field-section">
            <div class="caregiver-header">
              <label class="field-label">Shadow Caregivers</label>
              <el-button
                type="text"
                size="mini"
                class="add-additional-caregiver"
                @click="addAdditionalCaregiver"
              >
                <i class="fa fa-plus" style="margin-right: 4px"></i> Add
              </el-button>
            </div>
            <div
              v-for="(extra, idx) in additionalCaregivers"
              :key="'extra-cg-' + idx"
              class="additional-caregiver-row"
            >
              <AIVSelect
                :value="extra.id"
                @input="extra.id = $event"
                :options="additionalCaregiverOptions(idx)"
                placeholder="Select shadow caregiver"
                :searchable="true"
                class="additional-caregiver-select"
                :custom-style="{ backgroundColor: '#656565', minWidth: '100%' }"
              />
              <button
                class="btn btn-sm btn-danger additional-caregiver-remove"
                type="button"
                @click="removeAdditionalCaregiver(idx)"
                title="Remove"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- DSPD/PP Group: Service Code + Group Name Row -->
        <div v-if="(shift.payer === 'DSPD' || shift.payer === 'Private') && shift.shift_type === 'Group'" class="shift-details-row">
          <div class="field-section">
            <div class="client-header">
              <label class="field-label">Service Code</label>
            </div>
            <el-tooltip
              :content="getServiceCodesTooltipContent"
              :disabled="!shouldShowServiceCodesTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.selectedServiceCodes"
                  @input="updateSelectedServiceCodes"
                  :options="
                    availableServiceCodes.map((sc) => ({
                      id: sc.id,
                      name: sc.code || sc.name,
                    }))
                  "
                  :placeholder="getServiceCodesPlaceholder"
                  :multiSelect="false"
                  :disabled="isServiceCodesDropdownDisabled"
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
          <div class="field-section">
            <div class="client-header">
              <label class="field-label">Group Name</label>
            </div>
            <el-input
              v-model="shift.group_name"
              placeholder="Enter group name"
              size="small"
            />
          </div>
        </div>

        <!-- DSPD/PP Group: Clients + Caregiver (shown after service code selected) -->
        <div v-if="(shift.payer === 'DSPD' || shift.payer === 'Private') && shift.shift_type === 'Group' && shift.selectedServiceCodes && shift.selectedServiceCodes.length > 0" class="client-caregiver-row">
          <div class="client-section">
            <div class="client-header">
              <label class="field-label">Clients</label>
            </div>
            <el-tooltip
              :content="getClientTooltipContent"
              :disabled="!shouldShowClientTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.clientId"
                  @input="shift.clientId = $event"
                  :options="
                    dspdGroupFilteredClients.map((client) => ({
                      id: client.id,
                      name: client.name,
                    }))
                  "
                  placeholder="Select clients"
                  :multiSelect="true"
                  :disabled="isClientDropdownDisabled"
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
            <div v-if="shift.clientId && shift.clientId.length > 0" class="selected-clients-chips">
              <el-tag
                v-for="clientId in shift.clientId"
                :key="clientId"
                closable
                size="small"
                type="info"
                @close="removeSelectedClient(clientId)"
                class="client-chip"
              >
                {{ getClientNameById(clientId) }}
              </el-tag>
            </div>
            <small class="client-help-text">Only clients with an active authorization for the selected service code are shown.</small>
          </div>
          <div class="caregiver-section">
            <div class="caregiver-header">
              <label class="field-label">Caregivers</label>
              <el-checkbox
                v-if="!isEmploymentSpecialist"
                v-model="includeAdmins"
                @change="onIncludeAdminsChange"
                class="include-admins-toggle"
              >
                Include Admins
              </el-checkbox>
              <el-checkbox
                v-if="!isEmploymentSpecialist"
                v-model="showAllCaregivers"
                @change="onShowAllCaregiversChange"
                class="include-admins-toggle"
              >
                Show all caregivers
              </el-checkbox>
            </div>
            <el-tooltip
              :content="getCaregiverTooltipContent"
              :disabled="!shouldShowCaregiverTooltip"
              placement="top"
            >
              <div>
                <AIVSelect
                  :value="shift.caregiverId"
                  @input="shift.caregiverId = $event"
                  :options="filteredCaregivers"
                  :placeholder="getCaregiverPlaceholder"
                  :multiSelect="true"
                  :disabled="
                    isCaregiverDropdownDisabled || isLoadingAvailableCaregivers
                  "
                  :custom-style="{
                    backgroundColor: '#656565',
                    minWidth: '100%',
                  }"
                />
              </div>
            </el-tooltip>
          </div>
        </div>

        <!-- Separate Clock Addresses Toggle -->
        <div
          v-if="
            !isVirtualShift &&
            selectedClientAddress &&
            shift.payer !== 'Non Service' &&
            !(shift.shift_type === 'Group' && !showAddressSelection)
          "
          class="separate-addresses-toggle-section"
        >
          <el-checkbox
            v-model="useSeparateClockAddresses"
            class="separate-addresses-toggle"
          >
            Use Separate Addresses for Clock In/Out
          </el-checkbox>
        </div>

        <!-- Client Address Section -->
        <div
          v-if="
            !isVirtualShift &&
            selectedClientAddress &&
            shift.payer !== 'Non Service' &&
            !useSeparateClockAddresses &&
            !(shift.shift_type === 'Group' && !showAddressSelection)
          "
          :class="[
            'client-address-section',
            {
              'no-bottom-margin': useAlternateLocation,
            },
          ]"
        >
          <div class="address-header">
            <label v-if="!useAlternateLocation" class="field-label"
              >Client Address</label
            >
            <div class="address-header-actions">
              <el-button
                v-if="hasMultipleAddresses && !useAlternateLocation"
                type="text"
                size="small"
                @click="openClientMapLink"
                class="map-link-button"
              >
                <i class="fa-solid fa-location-dot"></i> View on Map
              </el-button>
              <el-checkbox
                v-if="!useAlternateLocation"
                v-model="useAlternateLocation"
                class="alternate-location-toggle"
              >
                Use Alternate Location
              </el-checkbox>
            </div>
          </div>

          <!-- Address selector dropdown when multiple addresses are available -->
          <div
            v-if="hasMultipleAddresses && !useAlternateLocation"
            class="address-selector"
          >
            <AIVSelect
              :value="selectedAddressIndex"
              @input="selectedAddressIndex = $event"
              :options="
                availableAddresses.map((addr, idx) => ({
                  id: idx,
                  name: formatClientAddress(addr),
                }))
              "
              placeholder="Select address"
              :custom-style="{
                backgroundColor: '#656565',
                minWidth: '100%',
              }"
            />
          </div>

          <div
            v-if="!hasMultipleAddresses && !useAlternateLocation"
            class="address-content"
          >
            <div class="address-text">
              {{ formatClientAddress(selectedClientAddress) }}
            </div>
            <el-button
              type="text"
              size="small"
              @click="openClientMapLink"
              class="map-link-button"
            >
              <i class="fa-solid fa-location-dot"></i> View on Map
            </el-button>
          </div>
        </div>

        <!-- Geocode Field - Required when address has no geocode -->
        <div
          v-if="
            !isVirtualShift &&
            selectedClientAddress &&
            !hasClientGeocode &&
            !useAlternateLocation &&
            !useSeparateClockAddresses &&
            shift.payer !== 'Non Service' &&
            !(shift.shift_type === 'Group' && !showAddressSelection)
          "
          class="geocode-input-row geocode-section-spacing"
        >
          <div class="geocode-input-field">
            <AIVRow
              :record.sync="clientGeocode"
              :fields="[
                {
                  label: 'Geocode',
                  key: 'geocode',
                  type: 'text',
                  required: true,
                  placeholder: 'Enter geocode',
                },
              ]"
            />
          </div>
          <el-button
            type="primary"
            size="small"
            @click="generateClientGeocodeFromAddress"
            class="generate-geocode-button"
          >
            <i class="fa-solid fa-location-crosshairs"></i> Generate Geocode
          </el-button>
        </div>

        <!-- Alternate Location Fields -->
        <div
          v-if="
            !isVirtualShift &&
            useAlternateLocation &&
            !(shift.shift_type === 'Group' && !showAddressSelection)
          "
          class="vr-alternate-fields"
        >
          <!-- Mobile-only checkbox before Address1 -->
          <el-checkbox
            v-model="useAlternateLocation"
            class="alternate-location-toggle-mobile-top"
          >
            Use Alternate Location
          </el-checkbox>

          <div class="address1-address2-row">
            <div class="address1-section">
              <label class="d-block">Address1</label>
              <AIVAddressAutocomplete
                :value="vrAlternateLocation.address1"
                @input="val => $set(vrAlternateLocation, 'address1', val)"
                @place-selected="place => applyPlaceToAddressObj(vrAlternateLocation, place)"
              />
            </div>
            <div class="address2-section">
              <div class="address2-header">
                <label class="field-label">Address2</label>
              </div>
              <AIVRow
                :record.sync="vrAlternateLocation"
                :fields="[{ label: '', key: 'address2' }]"
              />
              <el-checkbox
                v-model="useAlternateLocation"
                class="alternate-location-toggle-repositioned"
              >
                Use Alternate Location
              </el-checkbox>
            </div>
          </div>
          <div class="city-state-zip-row">
            <AIVRow
              :record.sync="vrAlternateLocation"
              :fields="[
                { label: 'City', key: 'city' },
                { label: 'State', key: 'state' },
                { label: 'ZIP', key: 'zip' },
              ]"
            />
          </div>
          <div class="geocode-input-row">
            <div class="geocode-input-field">
              <AIVRow
                :record.sync="vrAlternateLocation"
                :fields="[
                  {
                    label: 'Geocode',
                    key: 'geocode',
                    placeholder: 'Enter geocode',
                  },
                ]"
              />
            </div>
            <el-button
              type="primary"
              size="small"
              @click="generateVRGeocodeFromAddress"
              class="generate-geocode-button"
            >
              <i class="fa-solid fa-location-crosshairs"></i> Generate Geocode
            </el-button>
          </div>

          <!-- VR Alternate Address Display -->
          <div class="vr-address-display">
            <div class="vr-address-header">
              <label class="field-label">Alternate Location Address</label>
            </div>
            <div class="address-content">
              <div class="address-text">
                {{ formatVRAddress(vrAlternateLocation) }}
              </div>
              <el-button
                v-if="vrAlternateLocation.address1 || vrAlternateLocation.city"
                type="text"
                size="small"
                @click="openVRMapLink"
                class="map-link-button"
              >
                <i class="fa-solid fa-location-dot"></i> View on Map
              </el-button>
            </div>
          </div>
        </div>

        <!-- Clock In Address Section -->
        <div
          v-if="
            !isVirtualShift &&
            useSeparateClockAddresses &&
            selectedClientAddress &&
            shift.payer !== 'Non Service' &&
            !(shift.shift_type === 'Group' && !showAddressSelection)
          "
          class="client-address-section"
        >
          <div class="address-header">
            <label class="field-label">Clock In Address</label>
            <div class="address-header-actions">
              <el-button
                v-if="!clockInUseAlternateAddress"
                type="text"
                size="small"
                @click="openClockInSelectedMapLink"
                class="map-link-button"
              >
                <i class="fa-solid fa-location-dot"></i> View on Map
              </el-button>
              <el-checkbox
                v-model="clockInUseAlternateAddress"
                class="use-alternate-address-toggle"
              >
                Use Alternate Address
              </el-checkbox>
            </div>
          </div>

          <!-- Address selector dropdown when multiple addresses are available -->
          <div
            v-if="!clockInUseAlternateAddress && hasMultipleAddresses"
            class="address-selector"
          >
            <AIVSelect
              :value="clockInClientAddressId"
              @input="clockInClientAddressId = $event"
              :options="
                availableAddresses.map((addr) => ({
                  id: addr.id,
                  name: formatClientAddress(addr),
                }))
              "
              placeholder="Select clock-in address"
              :custom-style="{
                backgroundColor: '#656565',
                minWidth: '100%',
              }"
            />
          </div>

          <!-- Display single address when only one exists -->
          <div
            v-if="!clockInUseAlternateAddress && !hasMultipleAddresses"
            class="address-content"
          >
            <div class="address-text">
              {{ formatClientAddress(selectedClientAddress) }}
            </div>
          </div>

          <!-- Alternate address fields for clock-in -->
          <div
            v-if="clockInUseAlternateAddress"
            class="alternate-address-fields"
          >
            <div class="address1-address2-row">
              <div class="address1-section">
                <label class="d-block">Address1</label>
                <AIVAddressAutocomplete
                  :value="clockInLocation.address1"
                  @input="val => $set(clockInLocation, 'address1', val)"
                  @place-selected="place => applyPlaceToAddressObj(clockInLocation, place)"
                />
              </div>
              <div class="address2-section">
                <AIVRow
                  :record.sync="clockInLocation"
                  :fields="[{ label: 'Address2', key: 'address2' }]"
                />
              </div>
            </div>
            <div class="city-state-zip-row">
              <AIVRow
                :record.sync="clockInLocation"
                :fields="[
                  { label: 'City', key: 'city' },
                  { label: 'State', key: 'state' },
                  { label: 'ZIP', key: 'zip' },
                ]"
              />
            </div>
            <div class="geocode-input-row">
              <div class="geocode-input-field">
                <AIVRow
                  :record.sync="clockInLocation"
                  :fields="[
                    {
                      label: 'Geocode',
                      key: 'geocode',
                      placeholder: 'Enter geocode',
                    },
                  ]"
                />
              </div>
              <el-button
                type="primary"
                size="small"
                @click="generateClockInGeocodeFromAddress"
                class="generate-geocode-button"
              >
                <i class="fa-solid fa-location-crosshairs"></i> Generate Geocode
              </el-button>
            </div>

            <!-- Clock In Address Display -->
            <div class="vr-address-display">
              <div class="vr-address-header">
                <label class="field-label">Clock In Address</label>
              </div>
              <div class="address-content">
                <div class="address-text">
                  {{ formatClockInAddress(clockInLocation) }}
                </div>
                <el-button
                  v-if="clockInLocation.zip"
                  type="text"
                  size="small"
                  @click="openClockInMapLink"
                  class="map-link-button"
                >
                  <i class="fa-solid fa-location-dot"></i> View on Map
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Clock Out Address Section -->
        <div
          v-if="
            !isVirtualShift &&
            useSeparateClockAddresses &&
            selectedClientAddress &&
            shift.payer !== 'Non Service' &&
            !(shift.shift_type === 'Group' && !showAddressSelection)
          "
          class="client-address-section"
        >
          <div class="address-header">
            <label class="field-label">Clock Out Address</label>
            <div class="address-header-actions">
              <el-button
                v-if="!clockOutUseAlternateAddress"
                type="text"
                size="small"
                @click="openClockOutSelectedMapLink"
                class="map-link-button"
              >
                <i class="fa-solid fa-location-dot"></i> View on Map
              </el-button>
              <el-checkbox
                v-model="clockOutUseAlternateAddress"
                class="use-alternate-address-toggle"
              >
                Use Alternate Address
              </el-checkbox>
            </div>
          </div>

          <!-- Address selector dropdown when multiple addresses are available -->
          <div
            v-if="!clockOutUseAlternateAddress && hasMultipleAddresses"
            class="address-selector"
          >
            <AIVSelect
              :value="clockOutClientAddressId"
              @input="clockOutClientAddressId = $event"
              :options="
                availableAddresses.map((addr) => ({
                  id: addr.id,
                  name: formatClientAddress(addr),
                }))
              "
              placeholder="Select clock-out address"
              :custom-style="{
                backgroundColor: '#656565',
                minWidth: '100%',
              }"
            />
          </div>

          <!-- Display single address when only one exists -->
          <div
            v-if="!clockOutUseAlternateAddress && !hasMultipleAddresses"
            class="address-content"
          >
            <div class="address-text">
              {{ formatClientAddress(selectedClientAddress) }}
            </div>
          </div>

          <!-- Alternate address fields for clock-out -->
          <div
            v-if="clockOutUseAlternateAddress"
            class="alternate-address-fields"
          >
            <div class="address1-address2-row">
              <div class="address1-section">
                <label class="d-block">Address1</label>
                <AIVAddressAutocomplete
                  :value="clockOutLocation.address1"
                  @input="val => $set(clockOutLocation, 'address1', val)"
                  @place-selected="place => applyPlaceToAddressObj(clockOutLocation, place)"
                />
              </div>
              <div class="address2-section">
                <AIVRow
                  :record.sync="clockOutLocation"
                  :fields="[{ label: 'Address2', key: 'address2' }]"
                />
              </div>
            </div>
            <div class="city-state-zip-row">
              <AIVRow
                :record.sync="clockOutLocation"
                :fields="[
                  { label: 'City', key: 'city' },
                  { label: 'State', key: 'state' },
                  { label: 'ZIP', key: 'zip' },
                ]"
              />
            </div>
            <div class="geocode-input-row">
              <div class="geocode-input-field">
                <AIVRow
                  :record.sync="clockOutLocation"
                  :fields="[
                    {
                      label: 'Geocode',
                      key: 'geocode',
                      placeholder: 'Enter geocode',
                    },
                  ]"
                />
              </div>
              <el-button
                type="primary"
                size="small"
                @click="generateClockOutGeocodeFromAddress"
                class="generate-geocode-button"
              >
                <i class="fa-solid fa-location-crosshairs"></i> Generate Geocode
              </el-button>
            </div>

            <!-- Clock Out Address Display -->
            <div class="vr-address-display">
              <div class="vr-address-header">
                <label class="field-label">Clock Out Address</label>
              </div>
              <div class="address-content">
                <div class="address-text">
                  {{ formatClockOutAddress(clockOutLocation) }}
                </div>
                <el-button
                  v-if="clockOutLocation.zip"
                  type="text"
                  size="small"
                  @click="openClockOutMapLink"
                  class="map-link-button"
                >
                  <i class="fa-solid fa-location-dot"></i> View on Map
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Virtual Link (shown only when isVirtualShift is true) -->
        <div v-if="isVirtualShift" class="full-width-field">
          <label class="field-label">Virtual Link</label>
          <el-input
            v-model="shift.virtual_link"
            placeholder="https://zoom.us/j/..."
            class="w-100"
          />
        </div>

        <div class="full-width-field">
          <label class="field-label">Notes</label>
          <el-input
            v-model="shift.shift_notes"
            type="textarea"
            :rows="3"
            class="w-100"
          />
        </div>

      </el-form>

      <!-- Address Selection View for Group Shifts -->
      <div v-if="showAddressSelection" class="address-selection-view">
        <div
          v-for="clientId in shift.clientId"
          :key="clientId"
          class="client-address-group"
        >
          <!-- Client Name and Toggle Row -->
          <div class="client-header-row">
            <div class="client-name-label">
              {{ getClientNameById(clientId) }}
            </div>
            <div
              v-if="
                getClientData(clientId).fetchedClientAddresses.length > 0 &&
                shift.payer !== 'Non Service'
              "
              class="separate-addresses-toggle-section"
            >
              <el-checkbox
                v-model="getClientData(clientId).useSeparateClockAddresses"
                class="separate-addresses-toggle"
              >
                Use Separate Addresses for Pickup and Dropoff
              </el-checkbox>
            </div>
          </div>

          <!-- Client Address Section -->
          <div
            v-if="
              getClientSelectedAddress(clientId) &&
              shift.payer !== 'Non Service' &&
              !getClientData(clientId).useSeparateClockAddresses
            "
            :class="[
              'client-address-section',
              {
                'no-bottom-margin':
                  getClientData(clientId).useAlternateLocation,
              },
            ]"
          >
            <div class="address-header">
              <label
                v-if="!getClientData(clientId).useAlternateLocation"
                class="field-label"
                >Client Address</label
              >
              <div class="address-header-actions">
                <el-button
                  v-if="
                    getClientData(clientId).fetchedClientAddresses.length > 1 &&
                    !getClientData(clientId).useAlternateLocation
                  "
                  type="text"
                  size="small"
                  @click="openClientMapLinkForGroup(clientId)"
                  class="map-link-button"
                >
                  <i class="fa-solid fa-location-dot"></i> View on Map
                </el-button>
                <el-checkbox
                  v-if="!getClientData(clientId).useAlternateLocation"
                  v-model="getClientData(clientId).useAlternateLocation"
                  class="alternate-location-toggle"
                >
                  Use Alternate Location
                </el-checkbox>
              </div>
            </div>

            <!-- Address Selector Dropdown -->
            <div
              v-if="
                getClientData(clientId).fetchedClientAddresses.length > 1 &&
                !getClientData(clientId).useAlternateLocation
              "
              class="address-selector"
            >
              <AIVSelect
                :value="getClientData(clientId).selectedAddressIndex"
                @input="updateClientAddressIndex(clientId, $event)"
                :options="
                  getClientData(clientId).fetchedClientAddresses.map(
                    (addr, idx) => ({
                      id: idx,
                      name: formatClientAddress(addr),
                    }),
                  )
                "
                placeholder="Select address"
                :custom-style="{
                  backgroundColor: '#656565',
                  minWidth: '100%',
                }"
              />
            </div>

            <!-- Address Display (only when single address) -->
            <div
              v-if="
                getClientData(clientId).fetchedClientAddresses.length === 1 &&
                !getClientData(clientId).useAlternateLocation
              "
              class="address-content"
            >
              <div class="address-text">
                {{ formatClientAddress(getClientSelectedAddress(clientId)) }}
              </div>
              <el-button
                type="text"
                size="small"
                @click="openClientMapLinkForGroup(clientId)"
                class="map-link-button"
              >
                <i class="fa-solid fa-location-dot"></i> View on Map
              </el-button>
            </div>
          </div>

          <!-- Geocode Field - Required when address has no geocode -->
          <div
            v-if="
              getClientSelectedAddress(clientId) &&
              !hasClientAddressGeocode(clientId) &&
              !getClientData(clientId).useAlternateLocation &&
              !getClientData(clientId).useSeparateClockAddresses &&
              shift.payer !== 'Non Service'
            "
            class="geocode-input-row geocode-section-spacing"
          >
            <div class="geocode-input-field">
              <AIVRow
                :record.sync="getClientData(clientId).clientGeocode"
                :fields="[
                  {
                    label: 'Geocode',
                    key: 'geocode',
                    type: 'text',
                    required: true,
                    placeholder: 'Enter geocode',
                  },
                ]"
              />
            </div>
            <el-button
              type="primary"
              size="small"
              @click="generateClientGeocodeFromAddressForGroup(clientId)"
              class="generate-geocode-button"
            >
              <i class="fa-solid fa-location-crosshairs"></i> Generate Geocode
            </el-button>
          </div>

          <!-- Alternate Location Fields -->
          <div
            v-if="getClientData(clientId).useAlternateLocation"
            class="vr-alternate-fields"
          >
            <!-- Mobile-only checkbox before Address1 -->
            <el-checkbox
              v-model="getClientData(clientId).useAlternateLocation"
              class="alternate-location-toggle-mobile-top"
            >
              Use Alternate Location
            </el-checkbox>

            <div class="address1-address2-row">
              <div class="address1-section">
                <label class="d-block">Address1</label>
                <AIVAddressAutocomplete
                  :value="getClientData(clientId).vrAlternateLocation.address1"
                  @input="val => $set(getClientData(clientId).vrAlternateLocation, 'address1', val)"
                  @place-selected="place => applyPlaceToAddressObj(getClientData(clientId).vrAlternateLocation, place)"
                />
              </div>
              <div class="address2-section">
                <div class="address2-header">
                  <label class="field-label">Address2</label>
                </div>
                <AIVRow
                  :record.sync="getClientData(clientId).vrAlternateLocation"
                  :fields="[{ label: '', key: 'address2' }]"
                />
                <el-checkbox
                  v-model="getClientData(clientId).useAlternateLocation"
                  class="alternate-location-toggle-repositioned"
                >
                  Use Alternate Location
                </el-checkbox>
              </div>
            </div>
            <div class="city-state-zip-row">
              <AIVRow
                :record.sync="getClientData(clientId).vrAlternateLocation"
                :fields="[
                  { label: 'City', key: 'city' },
                  { label: 'State', key: 'state' },
                  { label: 'ZIP', key: 'zip' },
                ]"
              />
            </div>
            <div class="geocode-input-row">
              <div class="geocode-input-field">
                <AIVRow
                  :record.sync="getClientData(clientId).vrAlternateLocation"
                  :fields="[
                    {
                      label: 'Geocode',
                      key: 'geocode',
                      type: 'text',
                      required: true,
                      placeholder: 'Enter geocode',
                    },
                  ]"
                />
              </div>
              <el-button
                type="primary"
                size="small"
                @click="generateAlternateGeocodeForGroup(clientId)"
                class="generate-geocode-button"
              >
                <i class="fa-solid fa-location-crosshairs"></i> Generate Geocode
              </el-button>
            </div>

            <!-- Group VR Alternate Address Display -->
            <div class="vr-address-display">
              <div class="vr-address-header">
                <label class="field-label">Alternate Location Address</label>
              </div>
              <div class="address-content">
                <div class="address-text">
                  {{ formatVRAddress(getClientData(clientId).vrAlternateLocation) }}
                </div>
                <el-button
                  v-if="
                    getClientData(clientId).vrAlternateLocation.address1 ||
                    getClientData(clientId).vrAlternateLocation.city
                  "
                  type="text"
                  size="small"
                  @click="openGroupVRMapLink(clientId)"
                  class="map-link-button"
                >
                  <i class="fa-solid fa-location-dot"></i> View on Map
                </el-button>
              </div>
            </div>
          </div>

          <!-- Clock In Address Section -->
          <div
            v-if="
              getClientData(clientId).useSeparateClockAddresses &&
              getClientSelectedAddress(clientId) &&
              shift.payer !== 'Non Service'
            "
            class="client-address-section"
          >
            <div class="address-header">
              <label class="field-label">Clock In Address</label>
              <div class="address-header-actions">
                <el-button
                  v-if="!getClientData(clientId).clockInUseAlternateAddress"
                  type="text"
                  size="small"
                  @click="openClockInMapLinkForGroup(clientId)"
                  class="map-link-button"
                >
                  <i class="fa-solid fa-location-dot"></i> View on Map
                </el-button>
                <el-checkbox
                  v-model="getClientData(clientId).clockInUseAlternateAddress"
                  class="use-alternate-address-toggle"
                >
                  Use Alternate Address
                </el-checkbox>
              </div>
            </div>

            <!-- Clock In Address Selector -->
            <div
              v-if="
                !getClientData(clientId).clockInUseAlternateAddress &&
                getClientData(clientId).fetchedClientAddresses.length > 1
              "
              class="address-selector"
            >
              <AIVSelect
                :value="getClientData(clientId).clockInClientAddressId"
                @input="updateClientClockInAddressId(clientId, $event)"
                :options="
                  getClientData(clientId).fetchedClientAddresses.map(
                    (addr) => ({
                      id: addr.id,
                      name: formatClientAddress(addr),
                    }),
                  )
                "
                placeholder="Select clock-in address"
                :custom-style="{
                  backgroundColor: '#656565',
                  minWidth: '100%',
                }"
              />
            </div>

            <!-- Clock In Address Display (only when single address, dropdown handles multiple) -->
            <div
              v-if="
                !getClientData(clientId).clockInUseAlternateAddress &&
                getClientData(clientId).fetchedClientAddresses.length === 1
              "
              class="address-content"
            >
              <div class="address-text">
                {{ formatClientAddress(getClientSelectedAddress(clientId)) }}
              </div>
            </div>

            <!-- Clock In Alternate Address Fields -->
            <div
              v-if="getClientData(clientId).clockInUseAlternateAddress"
              class="alternate-address-fields"
            >
              <div class="address1-address2-row">
                <div class="address1-section">
                  <label class="d-block">Address1</label>
                  <AIVAddressAutocomplete
                    :value="getClientData(clientId).clockInLocation.address1"
                    @input="val => $set(getClientData(clientId).clockInLocation, 'address1', val)"
                    @place-selected="place => applyPlaceToAddressObj(getClientData(clientId).clockInLocation, place)"
                  />
                </div>
                <div class="address2-section">
                  <AIVRow
                    :record.sync="getClientData(clientId).clockInLocation"
                    :fields="[{ label: 'Address2', key: 'address2' }]"
                  />
                </div>
              </div>
              <div class="city-state-zip-row">
                <AIVRow
                  :record.sync="getClientData(clientId).clockInLocation"
                  :fields="[
                    { label: 'City', key: 'city' },
                    { label: 'State', key: 'state' },
                    { label: 'ZIP', key: 'zip' },
                  ]"
                />
              </div>
              <div class="geocode-input-row">
                <div class="geocode-input-field">
                  <AIVRow
                    :record.sync="getClientData(clientId).clockInLocation"
                    :fields="[
                      {
                        label: 'Geocode',
                        key: 'geocode',
                        type: 'text',
                        required: true,
                        placeholder: 'Enter geocode',
                      },
                    ]"
                  />
                </div>
                <el-button
                  type="primary"
                  size="small"
                  @click="generateClockInGeocodeForGroup(clientId)"
                  class="generate-geocode-button"
                >
                  <i class="fa-solid fa-location-crosshairs"></i> Generate
                  Geocode
                </el-button>
              </div>

              <!-- Clock In Alternate Address Display -->
              <div class="vr-address-display">
                <div class="vr-address-header">
                  <label class="field-label">Clock In Alternate Address</label>
                </div>
                <div class="address-content">
                  <div class="address-text">
                    {{ formatClockInAddress(getClientData(clientId).clockInLocation) }}
                  </div>
                  <el-button
                    v-if="
                      getClientData(clientId).clockInLocation.address1 ||
                      getClientData(clientId).clockInLocation.city
                    "
                    type="text"
                    size="small"
                    @click="openClockInAlternateMapLinkForGroup(clientId)"
                    class="map-link-button"
                  >
                    <i class="fa-solid fa-location-dot"></i> View on Map
                  </el-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Clock Out Address Section -->
          <div
            v-if="
              getClientData(clientId).useSeparateClockAddresses &&
              getClientSelectedAddress(clientId) &&
              shift.payer !== 'Non Service'
            "
            class="client-address-section"
          >
            <div class="address-header">
              <label class="field-label">Clock Out Address</label>
              <div class="address-header-actions">
                <el-button
                  v-if="!getClientData(clientId).clockOutUseAlternateAddress"
                  type="text"
                  size="small"
                  @click="openClockOutMapLinkForGroup(clientId)"
                  class="map-link-button"
                >
                  <i class="fa-solid fa-location-dot"></i> View on Map
                </el-button>
                <el-checkbox
                  v-model="getClientData(clientId).clockOutUseAlternateAddress"
                  class="use-alternate-address-toggle"
                >
                  Use Alternate Address
                </el-checkbox>
              </div>
            </div>

            <!-- Clock Out Address Selector -->
            <div
              v-if="
                !getClientData(clientId).clockOutUseAlternateAddress &&
                getClientData(clientId).fetchedClientAddresses.length > 1
              "
              class="address-selector"
            >
              <AIVSelect
                :value="getClientData(clientId).clockOutClientAddressId"
                @input="updateClientClockOutAddressId(clientId, $event)"
                :options="
                  getClientData(clientId).fetchedClientAddresses.map(
                    (addr) => ({
                      id: addr.id,
                      name: formatClientAddress(addr),
                    }),
                  )
                "
                placeholder="Select clock-out address"
                :custom-style="{
                  backgroundColor: '#656565',
                  minWidth: '100%',
                }"
              />
            </div>

            <!-- Clock Out Address Display (only when single address, dropdown handles multiple) -->
            <div
              v-if="
                !getClientData(clientId).clockOutUseAlternateAddress &&
                getClientData(clientId).fetchedClientAddresses.length === 1
              "
              class="address-content"
            >
              <div class="address-text">
                {{ formatClientAddress(getClientSelectedAddress(clientId)) }}
              </div>
            </div>

            <!-- Clock Out Alternate Address Fields -->
            <div
              v-if="getClientData(clientId).clockOutUseAlternateAddress"
              class="alternate-address-fields"
            >
              <div class="address1-address2-row">
                <div class="address1-section">
                  <AIVRow
                    :record.sync="getClientData(clientId).clockOutLocation"
                    :fields="[{ label: 'Address1', key: 'address1' }]"
                  />
                </div>
                <div class="address2-section">
                  <AIVRow
                    :record.sync="getClientData(clientId).clockOutLocation"
                    :fields="[{ label: 'Address2', key: 'address2' }]"
                  />
                </div>
              </div>
              <div class="city-state-zip-row">
                <AIVRow
                  :record.sync="getClientData(clientId).clockOutLocation"
                  :fields="[
                    { label: 'City', key: 'city' },
                    { label: 'State', key: 'state' },
                    { label: 'ZIP', key: 'zip' },
                  ]"
                />
              </div>
              <div class="geocode-input-row">
                <div class="geocode-input-field">
                  <AIVRow
                    :record.sync="getClientData(clientId).clockOutLocation"
                    :fields="[
                      {
                        label: 'Geocode',
                        key: 'geocode',
                        type: 'text',
                        required: true,
                        placeholder: 'Enter geocode',
                      },
                    ]"
                  />
                </div>
                <el-button
                  type="primary"
                  size="small"
                  @click="generateClockOutGeocodeForGroup(clientId)"
                  class="generate-geocode-button"
                >
                  <i class="fa-solid fa-location-crosshairs"></i> Generate
                  Geocode
                </el-button>
              </div>

              <!-- Clock Out Alternate Address Display -->
              <div class="vr-address-display">
                <div class="vr-address-header">
                  <label class="field-label">Clock Out Alternate Address</label>
                </div>
                <div class="address-content">
                  <div class="address-text">
                    {{ formatClockOutAddress(getClientData(clientId).clockOutLocation) }}
                  </div>
                  <el-button
                    v-if="
                      getClientData(clientId).clockOutLocation.address1 ||
                      getClientData(clientId).clockOutLocation.city
                    "
                    type="text"
                    size="small"
                    @click="openClockOutAlternateMapLinkForGroup(clientId)"
                    class="map-link-button"
                  >
                    <i class="fa-solid fa-location-dot"></i> View on Map
                  </el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Caregiver Task Assignment View for DSPD Group Shifts -->
      <div
        v-if="showCaregiverTaskAssignment"
        class="caregiver-task-assignment-view"
      >
        <div class="client-task-grid">
          <div
            v-for="clientId in shift.clientId"
            :key="clientId"
            class="client-task-assignment-group"
          >
            <div class="client-task-header">
              <h4>{{ getClientNameById(clientId) }}</h4>
            </div>

            <!-- Pick Up Task -->
            <div class="task-assignment-row">
              <label class="task-label">Pick Up</label>
              <AIVSelect
                :value="getClientTaskCaregivers(clientId, 'pickUp')"
                @input="updateClientTaskCaregivers(clientId, 'pickUp', $event)"
                :options="getCaregiverOptions()"
                placeholder="Select caregiver for pick up"
                :multiSelect="false"
                :custom-style="{
                  flex: 1,
                }"
              />
            </div>

            <!-- Drop Off Task -->
            <div class="task-assignment-row">
              <label class="task-label">Drop Off</label>
              <AIVSelect
                :value="getClientTaskCaregivers(clientId, 'dropOff')"
                @input="updateClientTaskCaregivers(clientId, 'dropOff', $event)"
                :options="getCaregiverOptions()"
                placeholder="Select caregiver for drop off"
                :multiSelect="false"
                :custom-style="{
                  flex: 1,
                }"
              />
            </div>

            <!-- Shift Notes Task -->
            <div class="task-assignment-row">
              <label class="task-label">Shift Notes</label>
              <AIVSelect
                :value="getClientTaskCaregivers(clientId, 'shiftNotes')"
                @input="
                  updateClientTaskCaregivers(clientId, 'shiftNotes', $event)
                "
                :options="getCaregiverOptions()"
                placeholder="Select caregiver for shift notes"
                :multiSelect="false"
                :custom-style="{
                  flex: 1,
                }"
              />
            </div>

            <!-- Medications Task (show if client has medications) -->
            <div
              v-if="clientMedications[clientId] && clientMedications[clientId].length > 0"
              class="task-assignment-row medications-task-row"
            >
              <label class="task-label">Medications</label>
              <AIVSelect
                :value="getClientTaskCaregivers(clientId, 'medications')"
                @input="updateClientTaskCaregivers(clientId, 'medications', $event)"
                :options="getCaregiverOptions()"
                placeholder="Select caregiver for medications"
                :multiSelect="false"
                :custom-style="{
                  flex: 1,
                }"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Reason for Change: outside the step views so it sits next to the
           Save button on every step of a group shift edit. Required only once
           the shift is a time record (see utils/editReason.js). -->
      <div v-if="showEditReasonField" class="full-width-field edit-reason-field">
        <label class="field-label">
          Reason for Change
          <span v-if="requiresEditReason" style="color: #f56c6c;">*</span>
          <span v-else class="text-muted">(optional)</span>
        </label>
        <el-input
          v-model="adminChangeReason"
          type="textarea"
          :rows="2"
          :placeholder="requiresEditReason
            ? 'Brief reason for this edit (e.g., time correction, caregiver swap, wrong service code)...'
            : 'Optional note for the shift history (e.g., client requested time change, caregiver swap)...'"
          class="w-100"
        />
      </div>

      <!-- Auth hours/units warning -->
      <div v-if="authWarning" class="auth-warning-banner">
        <div class="alert alert-warning">
          <i class="fa-solid fa-triangle-exclamation"></i>
          <span>{{ authWarningMessage }}</span>
        </div>
        <el-checkbox v-model="authAcknowledged" class="auth-acknowledge-checkbox">
          I acknowledge that this shift will exceed the authorization's remaining hours/units
        </el-checkbox>
      </div>

      <template slot="footer">
        <div class="text-center">
          <el-button
            v-if="showAddressSelection || showCaregiverTaskAssignment"
            @click="handleBack"
            >Back</el-button
          >
          <el-button :disabled="saving" @click="handleCancel">Cancel</el-button>
          <el-button
            v-if="allowDelete && shiftBeingEdited && shiftBeingEdited.id"
            type="danger"
            :disabled="saving"
            @click="handleDelete"
          >
            <i class="fa-solid fa-trash"></i> Delete
          </el-button>
          <el-button type="primary" :loading="saving" :disabled="saving" @click="handleSave">
            {{ saving ? 'Saving...' : getActionButtonText }}
          </el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Recurring Edit Explanation Modal -->
    <el-dialog
      :visible.sync="showRecurringEditExplanation"
      title="Edit Recurring Shift Series"
      width="600px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="recurring-edit-explanation">
        <p>
          <strong
            >You are about to edit this recurring shift series starting from
            this date and all future occurrences.</strong
          >
        </p>

        <p>Here's what will happen:</p>
        <ul>
          <li>
            The current recurring shift will end on
            <strong>{{ recurringEditCutoffDate }}</strong>
          </li>
          <li>
            A new recurring shift will be created starting on
            <strong>{{ recurringEditStartDate }}</strong> with your updated
            details
          </li>
          <li>Past shift occurrences will remain unchanged</li>
          <li>
            All future occurrences will have the new schedule and settings
          </li>
        </ul>

        <el-alert
          type="warning"
          :closable="false"
          show-icon
          style="margin-top: 15px"
        >
          <span slot="title"
            >This action cannot be undone. Make sure you want to edit the entire
            series from this date forward.</span
          >
        </el-alert>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelRecurringEdit">Cancel</el-button>
        <el-button type="primary" @click="confirmRecurringEdit"
          >OK, Continue</el-button
        >
      </span>
    </el-dialog>

    <!-- Exception Shifts Modal -->
    <el-dialog
      :visible.sync="showExceptionShiftsModal"
      title="Exception Shifts Found"
      width="800px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="exception-shifts-content">
        <p style="margin-bottom: 15px">
          <strong
            >Exception shifts found in the date range. Select which ones to
            keep:</strong
          >
        </p>

        <el-table
          :data="exceptionShifts"
          style="width: 100%; margin-top: 15px"
          max-height="400"
          ref="exceptionShiftsTable"
          @selection-change="handleExceptionSelectionChange"
        >
          <el-table-column type="selection" width="55" />
          <el-table-column prop="exception_date" label="Date" width="120">
            <template slot-scope="scope">
              {{ formatDate(scope.row.exception_date) }}
            </template>
          </el-table-column>
          <el-table-column label="Time" width="160">
            <template slot-scope="scope">
              {{ formatTime(scope.row.scheduled_start) }} -
              {{ formatTime(scope.row.scheduled_end) }}
            </template>
          </el-table-column>
          <el-table-column label="Caregivers" min-width="140">
            <template slot-scope="scope">
              {{ formatNames(scope.row.caregivers) }}
            </template>
          </el-table-column>
          <el-table-column label="Clients" min-width="140">
            <template slot-scope="scope">
              {{ formatNames(scope.row.clients) }}
            </template>
          </el-table-column>
        </el-table>

        <div style="margin-top: 15px; color: #ffffff">
          <p>
            <strong>Selected:</strong> Will be moved to the new recurring shift.
          </p>
          <p><strong>Unselected:</strong> Will be permanently deleted.</p>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <div
          style="
            display: flex;
            justify-content: space-between;
            width: 100%;
            align-items: center;
          "
        >
          <span style="color: #ffffff; font-size: 14px">
            {{ selectedExceptionShifts.length }} of
            {{ exceptionShifts.length }} selected
          </span>
          <div>
            <el-button @click="cancelSave">Cancel</el-button>
            <el-button type="primary" @click="handleExceptionShiftsSelection"
              >Save Selected</el-button
            >
          </div>
        </div>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { serviceCodesService } from "src/serviceHandlers/serviceCodesService";
import { nonServiceCodesService } from "src/serviceHandlers/nonServiceCodesService";
import { clientsService } from "src/serviceHandlers/clientsService";
import { caregiversService } from "src/serviceHandlers/caregiversService";
import { shiftsService } from "src/serviceHandlers/shiftsService";
import { authorizationsService } from "src/serviceHandlers/authorizationsService";
import AIVSelect from "@/components/AIVSelect.vue";
import AIVAddressAutocomplete from "@/components/AIVAddressAutocomplete.vue";
import { useLoginStore } from "src/stores/loginStore";
import { Checkbox, CheckboxGroup, Tooltip } from "element-ui";
import OpenLocationCode from "open-location-code";
import { validateGeocodeFormat } from "src/utils/geocodeValidator";
import { shiftRequiresEditReason } from "src/utils/editReason";
import { timezoneService } from "src/serviceHandlers/timezoneService";
import { parseDateOnly } from "@/utils/dateOnly";
import {
  TIMEZONE_OPTIONS,
  getBrowserTimezone,
  formatTimezoneLabel,
} from "src/utils/timezoneConstants";

export default {
  name: "ShiftModal",
  components: {
    AIVSelect,
    AIVAddressAutocomplete,
    "el-checkbox": Checkbox,
    "el-checkbox-group": CheckboxGroup,
    "el-tooltip": Tooltip,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shiftBeingEdited: {
      type: Object,
      default: null,
    },
    availableClients: {
      type: Array,
      default: () => [],
    },
    availableCaregivers: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    allowDelete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isRecurringMode: false, // false = single, true = recurring
      showRecurringEditExplanation: false, // Show explanation modal when editing recurring series
      isEditingRecurringSeries: false, // Track if user confirmed they want to edit the series
      showExceptionShiftsModal: false, // Show modal for handling exception shifts
      exceptionShifts: [], // List of exception shifts found in the date range
      selectedExceptionShifts: [], // List of selected exception shifts (to keep)
      pendingSaveData: null, // Store shift data while exception shifts modal is shown
      // Shadow/training caregivers on non-group shifts: [{ id, role }]
      additionalCaregivers: [],
      shift: {
        date: "",
        scheduled_start: "",
        scheduled_end: "",
        clientId: null,
        caregiverId: null,
        shift_type: "1-on-1",
        payer: "VR",
        shift_notes: "",
        group_name: "",
        virtual_link: "",
        selectedServiceCodes: [],
        recurring: false,
        recurrencePattern: "weekly",
        selectedWeekdays: [],
        customDates: [],
        recurring_start_date: "",
        recurring_end_date: null,
        timezone: null,
      },
      inferredTimezone: null,
      inferringTimezone: false,
      timezoneOptions: TIMEZONE_OPTIONS,
      shiftTypeOptions: [
        { id: "1-on-1", name: "1-on-1" },
        { id: "Group", name: "Group" },
      ],
      payerOptions: [
        { id: "DSPD", name: "DSPD" },
        { id: "VR", name: "VR" },
        // { id: "Private", name: "Private" }, // To be added later
        { id: "Non Service", name: "Non Service" },
      ],
      serviceCodes: [],
      nonServiceCodes: [],
      includeAdmins: false,
      // "Show all caregivers" override: reveals caregivers the availability
      // check would hide, each tagged with the reason. Save then asks the
      // scheduler to confirm the consequences before it goes through.
      showAllCaregivers: false,
      isVirtualShift: false,
      // VR alternate location data
      useAlternateLocation: false,
      vrAlternateLocation: {
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        geocode: "",
      },
      // Separate clock-in/out address data
      useSeparateClockAddresses: false,
      clockInUseAlternateAddress: false,
      clockOutUseAlternateAddress: false,
      clockInClientAddressId: null,
      clockOutClientAddressId: null,
      clockInLocation: {
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        geocode: "",
      },
      clockOutLocation: {
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        geocode: "",
      },
      // Client address data
      fetchedClientAddress: null,
      fetchedClientAddresses: [], // All addresses for the selected client
      selectedAddressIndex: 0, // Index of the selected address
      // Group shift address selection
      showAddressSelection: false, // Track if we're in address selection mode for group shifts
      clientAddressSelections: {}, // Map of clientId to selected addressId for group shifts
      groupClientData: {}, // Map of clientId to full address data for group shifts
      // DSPD group shift caregiver task assignment
      showCaregiverTaskAssignment: false, // Track if we're in caregiver task assignment mode
      clientTaskCaregivers: {}, // Map of clientId to { pickUp: [], dropOff: [], shiftNotes: [], medications: [] }
      clientMedications: {}, // Map of clientId to medications list
      clientMedicationsEnabled: {}, // Map of clientId to boolean (checkbox state)
      loadingClientMedications: false,
      // Geocode data for client address
      clientGeocode: {
        geocode: "",
      },
      // Available service codes for the selected client
      availableServiceCodes: [],
      // Filtered clients based on payer type
      filteredClients: [],
      // Filtered clients for DSPD Group (filtered by service code auth)
      dspdGroupFilteredClients: [],
      // Available caregivers for the selected time slot
      availableCaregiversForShift: [],
      isLoadingAvailableCaregivers: false,
      hasCheckedAvailability: false,
      authWarning: false,
      authWarningMessage: "",
      authAcknowledged: false,
      adminChangeReason: "",
      isPopulatingShift: false, // Flag to prevent watchers from firing during shift population
      sourceClientId: null, // Client ID from context (e.g. client calendar) to auto-populate after payer changes
      sourceCaregiverId: null, // Caregiver ID from context (e.g. caregiver calendar) to auto-populate after payer/type changes
      loginStore: useLoginStore(),
      weekdayOptions: [
        { value: 0, label: "Sun" },
        { value: 1, label: "Mon" },
        { value: 2, label: "Tue" },
        { value: 3, label: "Wed" },
        { value: 4, label: "Thu" },
        { value: 5, label: "Fri" },
        { value: 6, label: "Sat" },
      ],
      datePickerOptions: {
        disabledDate(time) {
          // Optionally disable past dates
          return time.getTime() < Date.now() - 24 * 60 * 60 * 1000;
        },
      },
    };
  },
  mounted() {
    // Service codes will be loaded when modal becomes visible
    // (see visible watcher below)
  },
  computed: {
    browserTimezone() {
      return getBrowserTimezone();
    },
    showTimezoneWarning() {
      return (
        this.shift.timezone &&
        this.shift.timezone !== this.browserTimezone
      );
    },
    timezoneConversionHint() {
      if (!this.showTimezoneWarning || !this.shift.scheduled_start || !this.shift.scheduled_end || !this.shift.date) return "";
      try {
        const dateStr = typeof this.shift.date === "string" ? this.shift.date.split("T")[0] : this.shift.date;
        const startLocal = new Date(`${dateStr}T${this.shift.scheduled_start}:00`);
        const endLocal = new Date(`${dateStr}T${this.shift.scheduled_end}:00`);
        if (isNaN(startLocal.getTime()) || isNaN(endLocal.getTime())) return "";
        const fmt = { hour: "numeric", minute: "2-digit", hour12: true };
        const browserFmt = { ...fmt, timeZone: this.browserTimezone };
        const shiftFmt = { ...fmt, timeZone: this.shift.timezone };
        const startBrowser = startLocal.toLocaleTimeString("en-US", browserFmt);
        const endBrowser = endLocal.toLocaleTimeString("en-US", browserFmt);
        const startClient = startLocal.toLocaleTimeString("en-US", shiftFmt);
        const endClient = endLocal.toLocaleTimeString("en-US", shiftFmt);
        return `You entered ${startBrowser} \u2013 ${endBrowser} ${this.shortTzName(this.browserTimezone)}, which is ${startClient} \u2013 ${endClient} ${this.shortTzName(this.shift.timezone)} for the client. Enter times based on when the shift occurs in your timezone.`;
      } catch { return ""; }
    },
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    filteredCaregivers() {
      // If date/time fields are filled and we've fetched available caregivers
      if (!this.isCaregiverDropdownDisabled && this.hasCheckedAvailability) {
        // Return the available caregivers for the specific shift time
        let caregivers = this.availableCaregiversForShift.map((caregiver) => {
          const name =
            caregiver.name || `${caregiver.first_name} ${caregiver.last_name}`;
          return {
            id: caregiver.id,
            // Someone only visible because of the override is labelled with the
            // reason, so the scheduler never picks them without seeing it.
            name: caregiver.unavailable
              ? `${name} — unavailable: ${caregiver.unavailable_reason || "fails availability check"}`
              : name,
            user_type: caregiver.user_type,
            unavailable: !!caregiver.unavailable,
            unavailable_reason: caregiver.unavailable_reason || null,
          };
        });

        // When editing a shift, ensure all currently assigned caregivers are in the list
        // even if they're not in the "available" list
        if (
          this.shiftBeingEdited &&
          this.shiftBeingEdited.caregivers &&
          this.shiftBeingEdited.caregivers.length > 0
        ) {
          // Add all assigned caregivers that aren't already in the list
          this.shiftBeingEdited.caregivers.forEach((assignedCaregiver) => {
            const isAlreadyInList = caregivers.some(
              (c) => c.id === assignedCaregiver.id,
            );

            if (!isAlreadyInList) {
              // Add the assigned caregiver to the list
              caregivers.unshift({
                id: assignedCaregiver.id,
                name: assignedCaregiver.name,
                user_type: assignedCaregiver.user_type,
              });
            }
          });
        }

        return caregivers;
      }
      // If date/time fields are not filled, return empty array
      // This prevents showing all caregivers before the user selects a time
      return [];
    },
    hasVRAlternateLocation() {
      return (
        this.vrAlternateLocation.address1 &&
        this.vrAlternateLocation.city &&
        this.vrAlternateLocation.state
      );
    },
    selectedClient() {
      // Return null for multi-select (group shifts with multiple clients)
      // This will hide address-related UI elements for group shifts
      if (Array.isArray(this.shift.clientId)) {
        return null;
      }

      // First try to get client from the shift being edited (which has address data)
      if (
        this.shiftBeingEdited &&
        this.shiftBeingEdited.clients &&
        this.shiftBeingEdited.clients.length > 0
      ) {
        const shiftClient = this.shiftBeingEdited.clients.find(
          (client) => client.id === this.shift.clientId,
        );
        if (shiftClient) return shiftClient;
      }

      // Fallback to filteredClients for new shifts
      return this.filteredClients.find(
        (client) => client.id === this.shift.clientId,
      );
    },
    availableAddresses() {
      // Return all available addresses for the selected client
      if (
        this.fetchedClientAddresses &&
        this.fetchedClientAddresses.length > 0
      ) {
        return this.fetchedClientAddresses;
      }

      if (!this.selectedClient) return [];

      // Check if client has multiple addresses array
      if (
        this.selectedClient.addresses &&
        this.selectedClient.addresses.length > 0
      ) {
        return this.selectedClient.addresses;
      }
      // For clients with single address, return as array
      else if (this.selectedClient.address) {
        return [this.selectedClient.address];
      }
      return [];
    },
    hasMultipleAddresses() {
      return this.availableAddresses.length > 1;
    },
    selectedClientAddress() {
      // Use selected address from available addresses
      if (this.availableAddresses && this.availableAddresses.length > 0) {
        // Ensure selectedAddressIndex is within bounds
        const index = Math.min(
          this.selectedAddressIndex,
          this.availableAddresses.length - 1,
        );
        return this.availableAddresses[index];
      }

      // Fallback to fetchedClientAddress for backward compatibility
      if (this.fetchedClientAddress) {
        return this.fetchedClientAddress;
      }

      return null;
    },
    isAdminOrLead() {
      const userType = this.loginStore.user?.user_type;
      return userType === "Admin" || userType === "Lead";
    },
    isAdminUser() {
      const userType = this.loginStore.user?.user_type;
      return userType === "Admin";
    },
    // The edit reason is only mandatory once the shift is a worked time
    // record; a future scheduled shift can be moved without one.
    requiresEditReason() {
      return shiftRequiresEditReason(this.shiftBeingEdited);
    },
    showEditReasonField() {
      return !!(this.shiftBeingEdited && this.shiftBeingEdited.id && this.isAdminUser);
    },
    isEmploymentSpecialist() {
      const userType = this.loginStore.user?.user_type;
      return userType === "Employment Specialist";
    },
    isPayerLocked() {
      // Lock payer to client's type when opening from a specific client's page
      // (shiftBeingEdited has clientId but no id = new shift from client context)
      return !this.shiftBeingEdited?.id && !!this.shiftBeingEdited?.clientId;
    },
    shiftMode() {
      // When editing an existing shift, check if we're in recurring edit mode
      if (this.shiftBeingEdited?.id) {
        return this.isRecurringMode && this.isEditingRecurringSeries
          ? "recurring"
          : "single";
      }
      return this.isRecurringMode ? "recurring" : "single";
    },
    isEditingRecurringInstance() {
      // Check if we're editing an instance of a recurring shift (future occurrence only)
      if (!this.shiftBeingEdited) return false;

      const hasParentLink = !!(
        this.shiftBeingEdited.parent_recurring_shift_id ||
        this.shiftBeingEdited.original_recurring_shift_id
      );

      // Check if the shift date is today or in the future (compare dates, not datetimes,
      // so a shift scheduled for today is still editable even if the start time has passed)
      const shiftDate = this.shiftBeingEdited.scheduled_start
        ? new Date(this.shiftBeingEdited.scheduled_start)
        : this.shiftBeingEdited.date
          ? parseDateOnly(this.shiftBeingEdited.date) // DATE-only: UTC parse would judge a today shift "past"
          : null;
      if (!shiftDate) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const shiftDay = new Date(shiftDate);
      shiftDay.setHours(0, 0, 0, 0);
      const isTodayOrFuture = shiftDay >= today;

      return hasParentLink && isTodayOrFuture;
    },
    recurringEditStartDate() {
      if (!this.shiftBeingEdited || !this.shiftBeingEdited.scheduled_start) {
        return "";
      }
      const date = new Date(this.shiftBeingEdited.scheduled_start);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    recurringEditCutoffDate() {
      if (!this.shiftBeingEdited || !this.shiftBeingEdited.scheduled_start) {
        return "";
      }
      const date = new Date(this.shiftBeingEdited.scheduled_start);
      date.setDate(date.getDate() - 1); // Day before
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    hasClientGeocode() {
      // Check if the selected client address has a geocode
      if (!this.selectedClientAddress) return true; // Don't show field if no address
      const hasGeocode = !!(
        this.selectedClientAddress.geocode &&
        this.selectedClientAddress.geocode.trim()
      );
      return hasGeocode;
    },
    isCaregiverDropdownDisabled() {
      // Check if required fields are filled for the current shift mode
      if (this.shiftMode === "single") {
        // For single shifts: need date, start time, and end time
        return (
          !this.shift.date ||
          !this.shift.scheduled_start ||
          !this.shift.scheduled_end
        );
      } else {
        // For recurring shifts: need start date, start time, end time, AND at least one weekday
        return (
          !this.shift.recurring_start_date ||
          !this.shift.scheduled_start ||
          !this.shift.scheduled_end ||
          !this.shift.selectedWeekdays ||
          this.shift.selectedWeekdays.length === 0
        );
      }
    },
    getCaregiverTooltipContent() {
      // If date/times not filled
      if (this.isCaregiverDropdownDisabled) {
        if (this.shiftMode === "single") {
          return "Please select date and times first to see available caregivers";
        } else {
          return "Please select date range, times, and days of the week first to see available caregivers";
        }
      }
      // If date/times filled but no caregivers available
      if (this.hasCheckedAvailability && this.filteredCaregivers.length === 0) {
        if (this.shiftMode === "single") {
          return "No caregivers are available for the selected date and times";
        } else {
          return "No caregivers are available for the selected recurring schedule";
        }
      }
      return "";
    },
    shouldShowCaregiverTooltip() {
      // Show tooltip when disabled OR when no caregivers are available
      return (
        this.isCaregiverDropdownDisabled ||
        (this.hasCheckedAvailability &&
          this.filteredCaregivers.length === 0 &&
          !this.isLoadingAvailableCaregivers)
      );
    },
    getCaregiverPlaceholder() {
      if (this.isLoadingAvailableCaregivers) {
        return "Loading available caregivers...";
      }
      if (
        this.hasCheckedAvailability &&
        this.filteredCaregivers.length === 0 &&
        !this.isCaregiverDropdownDisabled
      ) {
        return "No caregivers available";
      }
      return "Select caregiver";
    },
    isClientDropdownDisabled() {
      // Client should be disabled until a payer is selected
      return !this.shift.payer;
    },
    getClientTooltipContent() {
      // Show tooltip when no payer is selected
      if (!this.shift.payer) {
        return "Please select a payer first";
      }
      return "";
    },
    shouldShowClientTooltip() {
      // Show tooltip when disabled
      return this.isClientDropdownDisabled;
    },
    isServiceCodesDropdownDisabled() {
      // Service codes should be disabled until a payer is selected
      if (!this.shift.payer) {
        return true;
      }
      // For VR payer, service codes should be disabled until a client is selected
      if (this.shift.payer === "VR") {
        return !this.shift.clientId;
      }
      // For DSPD 1-on-1, service codes should be disabled until a client is selected
      if (this.shift.payer === "DSPD" && this.shift.shift_type === "1-on-1") {
        return !this.shift.clientId;
      }
      // For other payers, service codes are enabled
      return false;
    },
    getServiceCodesTooltipContent() {
      // Show tooltip when no payer is selected
      if (!this.shift.payer) {
        return "Please select a payer first";
      }
      // Show tooltip for VR or DSPD 1-on-1 when client not selected
      if (
        (this.shift.payer === "VR" || (this.shift.payer === "DSPD" && this.shift.shift_type === "1-on-1")) &&
        !this.shift.clientId
      ) {
        return "Please select a client first to see available service codes";
      }
      // Show message if no service codes available after client is selected
      if (
        (this.shift.payer === "VR" || (this.shift.payer === "DSPD" && this.shift.shift_type === "1-on-1")) &&
        this.shift.clientId &&
        this.availableServiceCodes.length === 0
      ) {
        return "No service codes available for this client's active authorizations";
      }
      return "";
    },
    shouldShowServiceCodesTooltip() {
      // Show tooltip when disabled OR when no service codes are available
      return (
        this.isServiceCodesDropdownDisabled ||
        ((this.shift.payer === "VR" || (this.shift.payer === "DSPD" && this.shift.shift_type === "1-on-1")) &&
          this.shift.clientId &&
          this.availableServiceCodes.length === 0)
      );
    },
    getServiceCodesPlaceholder() {
      if (this.shift.payer === "Non Service") {
        return "Select service code";
      }
      if (
        (this.shift.payer === "VR" || (this.shift.payer === "DSPD" && this.shift.shift_type === "1-on-1")) &&
        this.shift.clientId &&
        this.availableServiceCodes.length === 0
      ) {
        return "No service codes available";
      }
      return "Select service code";
    },
    isShiftTypeDisabled() {
      // Shift type is now controlled by toggle buttons, not a dropdown
      return false;
    },
    isDSPDGroupShift() {
      // Check if this is a DSPD group shift
      return this.shift.payer === "DSPD" && this.shift.shift_type === "Group";
    },
    hasDSPServiceCode() {
      // Check if the DSP (medication admin) service code is selected
      if (!this.shift.selectedServiceCodes || this.shift.selectedServiceCodes.length === 0) {
        return false;
      }
      return this.availableServiceCodes.some(
        (sc) => sc.code === "DSP" && this.shift.selectedServiceCodes.includes(sc.id),
      );
    },
    isClientMultiSelect() {
      // Enable multi-select for clients when DSPD group shift
      return this.isDSPDGroupShift;
    },
    isCaregiverMultiSelect() {
      // Enable multi-select for caregivers when DSPD group shift
      return this.isDSPDGroupShift;
    },
    showAdditionalCaregivers() {
      // Shadow/training assignments exist only on non-group shifts, and
      // are per-occurrence: hidden on recurring create and series edits
      const isRecurringCreate =
        this.shift.recurring && !this.shiftBeingEdited?.id;
      return (
        !!this.shift.payer &&
        this.shift.shift_type !== "Group" &&
        !isRecurringCreate &&
        !this.isEditingRecurringSeries
      );
    },
    getActionButtonText() {
      // For group shifts, show "Continue" instead of "Update" or "Create"
      if (
        this.shift.shift_type === "Group" &&
        !this.showAddressSelection &&
        !this.showCaregiverTaskAssignment
      ) {
        return "Continue";
      }
      // When in address selection mode for DSPD group shifts, show "Continue"
      if (this.showAddressSelection && this.isDSPDGroupShift) {
        return "Continue";
      }
      // When in caregiver task assignment mode, show "Save" if editing, otherwise "Create"
      if (this.showCaregiverTaskAssignment) {
        return this.shiftBeingEdited?.id ? "Save" : "Create";
      }
      return this.shiftBeingEdited?.id ? "Update" : "Create";
    },
    getDialogTitle() {
      if (this.showCaregiverTaskAssignment) {
        return "Assign Caregivers to Tasks";
      }
      if (this.showAddressSelection) {
        return "Select Address for Each Client";
      }
      return this.shiftBeingEdited?.id ? "Edit Shift" : "Create New Shift";
    },
  },
  watch: {
    "shift.clientId"() {
      if (!this.isPopulatingShift) this.checkAuthRemaining();
    },
    "shift.selectedServiceCodes"() {
      if (!this.isPopulatingShift) this.checkAuthRemaining();
    },
    "shift.scheduled_start"() {
      if (!this.isPopulatingShift) this.checkAuthRemaining();
    },
    "shift.scheduled_end"() {
      if (!this.isPopulatingShift) this.checkAuthRemaining();
    },
    isRecurringMode(newValue) {
      // Update recurring flag based on switch value
      this.shift.recurring = newValue;

      // Clear date fields when switching modes
      if (!newValue) {
        // Switching to single mode
        // Only clear recurring fields if not editing a recurring instance
        if (!this.isEditingRecurringInstance) {
          this.shift.recurring_start_date = "";
          this.shift.recurring_end_date = null;
          this.shift.selectedWeekdays = [];
        }
      } else {
        // Switching to recurring mode
        // Don't clear shift.date - we may need it when switching back to single mode
        // The UI will show/hide the appropriate fields based on shiftMode
      }
    },
    "shift.clientId": {
      async handler(newClientId) {
        // Skip if we're populating shift data (to avoid race conditions when editing)
        if (this.isPopulatingShift) {
          return;
        }

        if (Array.isArray(newClientId)) {
          // Group shift: infer timezone from the first selected client with an address.
          // Preserve an already-chosen timezone so adding more clients doesn't wipe it.
          if (newClientId.length === 0) {
            this.inferredTimezone = null;
            this.shift.timezone = null;
            return;
          }
          this.inferringTimezone = true;
          try {
            for (const cid of newClientId) {
              try {
                const c = await clientsService.getById(cid);
                const addrs = c?.addresses?.length
                  ? c.addresses
                  : c?.address
                    ? [c.address]
                    : [];
                if (addrs[0]?.id) {
                  const tz = await this.inferTimezoneFromAddress(addrs[0].id);
                  if (tz) {
                    this.inferredTimezone = tz;
                    this.shift.timezone = tz;
                    return;
                  }
                }
              } catch (e) {
                // Try next client
              }
            }
            // No client had an inferrable timezone; leave any prior manual selection intact
          } finally {
            this.inferringTimezone = false;
          }
          return;
        }

        if (newClientId) {
          this.inferringTimezone = true;
          try {
            const client = await clientsService.getById(newClientId);

            // Store all addresses for the client
            if (client.addresses && client.addresses.length > 0) {
              this.fetchedClientAddresses = client.addresses;
              this.fetchedClientAddress = client.addresses[0];
            }
            // Fallback to single address property if addresses array not available
            else if (client.address) {
              this.fetchedClientAddresses = [client.address];
              this.fetchedClientAddress = client.address;
            } else {
              this.fetchedClientAddresses = [];
              this.fetchedClientAddress = null;
            }

            // Reset address selection to first address
            this.selectedAddressIndex = 0;

            // Clear selected service codes for VR or DSPD 1-on-1 when client changes
            // This allows auto-population to work correctly
            if (this.shift.payer === "VR" || (this.shift.payer === "DSPD" && this.shift.shift_type === "1-on-1")) {
              this.$set(this.shift, "selectedServiceCodes", []);
            }

            // Load available service codes
            await this.loadAvailableServiceCodes();

            // Clear geocode field when client changes
            this.clientGeocode.geocode = "";

            // Infer timezone from client address (for 1-on-1 shifts)
            if (!Array.isArray(newClientId) && this.fetchedClientAddress && this.fetchedClientAddress.id) {
              const tz = await this.inferTimezoneFromAddress(this.fetchedClientAddress.id);
              this.inferredTimezone = tz;
              this.shift.timezone = tz;
            } else if (!Array.isArray(newClientId)) {
              this.inferredTimezone = null;
              this.shift.timezone = null;
            }
          } catch (error) {
            this.fetchedClientAddress = null;
            this.fetchedClientAddresses = [];
            this.selectedAddressIndex = 0;
            this.inferredTimezone = null;
            this.shift.timezone = null;
            // Don't clear service codes when there's an error fetching client
          } finally {
            this.inferringTimezone = false;
          }
        } else {
          this.fetchedClientAddress = null;
          this.fetchedClientAddresses = [];
          this.selectedAddressIndex = 0;
          this.clientGeocode.geocode = "";
          this.inferredTimezone = null;
          this.shift.timezone = null;
          // Don't clear service codes when no client is selected
        }
      },
      immediate: false,
    },
    "shift.payer": {
      async handler(newPayer, oldPayer) {
        // Skip updating service codes if we're populating shift data
        // (codes will be updated after loading in populateShiftData)
        if (!this.isPopulatingShift) {
          // Fetch clients for the new payer type
          await this.fetchClientsByPayerType(newPayer);

          // Auto-populate client if we have a source client from context (e.g. client calendar)
          // and that client exists in the new filtered list
          const autoClientId = this.sourceClientId
            && this.filteredClients.some((c) => c.id === this.sourceClientId)
            ? this.sourceClientId
            : null;

          this.$set(this.shift, "clientId", autoClientId);
          if (!autoClientId) {
            this.fetchedClientAddress = null;
            this.fetchedClientAddresses = [];
            this.selectedAddressIndex = 0;
            this.clientGeocode = { geocode: "" };
          }

          // Clear selected service codes when switching to VR, DSPD, or Private
          // They will be auto-populated if only one is available
          if (newPayer === "VR" || newPayer === "DSPD" || newPayer === "Private") {
            this.$set(this.shift, "selectedServiceCodes", []);
            this.$set(this.shift, "shift_type", "1-on-1");
          }

          // Clear group filtered clients when switching away from DSPD/Private
          if (newPayer !== "DSPD" && newPayer !== "Private") {
            this.dspdGroupFilteredClients = [];
          }

          // Load available service codes (will fetch by client for VR and DSPD 1-on-1)
          await this.loadAvailableServiceCodes();
        }
        // Clear alternate location when payer changes away from VR
        if (this.shift.payer !== "VR") {
          this.useAlternateLocation = false;
        }
        // When switching to Non Service, keep only the first selected code
        if (
          newPayer === "Non Service" &&
          this.shift.selectedServiceCodes &&
          this.shift.selectedServiceCodes.length > 1
        ) {
          this.shift.selectedServiceCodes = [
            this.shift.selectedServiceCodes[0],
          ];
        }
        // Clear client and address info when switching to Non Service
        if (newPayer === "Non Service") {
          this.$set(this.shift, "clientId", null);
          this.$set(this.shift, "clientAddressId", null);
          this.$set(this.shift, "id_address", null);
          this.fetchedClientAddress = null;
          this.fetchedClientAddresses = [];
          this.selectedAddressIndex = 0;
          this.clientGeocode = { geocode: "" };
          this.useAlternateLocation = false;
          this.vrAlternateLocation = {
            address1: "",
            address2: "",
            city: "",
            state: "",
            zip: "",
            geocode: "",
          };
        }
      },
      immediate: false,
    },
    "shift.shift_type": {
      async handler(newType, oldType) {
        if (this.isPopulatingShift) return;
        if (this.shift.payer !== "DSPD" && this.shift.payer !== "Private") return;

        // Clear fields when switching between 1-on-1 and Group under DSPD/PP
        this.$set(this.shift, "selectedServiceCodes", []);
        this.$set(this.shift, "caregiverId", null);
        this.$set(this.shift, "group_name", "");
        this.dspdGroupFilteredClients = [];
        this.fetchedClientAddress = null;
        this.fetchedClientAddresses = [];
        this.selectedAddressIndex = 0;
        this.clientGeocode = { geocode: "" };

        // Re-populate caregiver from calendar context in appropriate format
        if (this.sourceCaregiverId) {
          const cid = newType === "Group"
            ? [this.sourceCaregiverId]
            : this.sourceCaregiverId;
          this.$set(this.shift, "caregiverId", cid);
        }

        // For 1-on-1, auto-populate source client if available; for Group, clear (will auto-populate after service code selection)
        if (newType === "1-on-1" && this.sourceClientId
            && this.filteredClients.some((c) => c.id === this.sourceClientId)) {
          this.$set(this.shift, "clientId", this.sourceClientId);
        } else {
          this.$set(this.shift, "clientId", null);
        }

        // Load appropriate service codes for the new shift type
        await this.loadAvailableServiceCodes();

        // For Group, preload client list
        if (newType === "Group") {
          await this.fetchClientsByPayerType(this.shift.payer);
        }
      },
      immediate: false,
    },
    "shift.selectedServiceCodes": {
      async handler(newCodes) {
        if (this.isPopulatingShift) return;

        // DSPD/PP Group: when service code changes, filter clients by auth
        const isGroupPayer = this.shift.payer === "DSPD" || this.shift.payer === "Private";
        if (isGroupPayer && this.shift.shift_type === "Group" && newCodes && newCodes.length > 0) {
          const selectedCodeId = newCodes[0];
          try {
            const response = await clientsService.list({
              service_code_id: selectedCodeId,
            });
            this.dspdGroupFilteredClients = (response || []).map((client) => ({
              id: Number(client.id) || client.id,
              name: `${client.first_name} ${client.last_name}${client.payer_type === 'Private' ? ' (PP)' : ''}`,
              addresses: client.addresses,
              payer_type: client.payer_type,
            }));

            // Auto-populate source client from calendar context into group multi-select
            if (this.sourceClientId
                && this.dspdGroupFilteredClients.some((c) => c.id === this.sourceClientId)
                && (!this.shift.clientId || this.shift.clientId.length === 0)) {
              this.$set(this.shift, "clientId", [this.sourceClientId]);
            }
          } catch (error) {
            this.$notifyError("Failed to load client list. Please try again.");
            this.dspdGroupFilteredClients = [];
          }
        } else if (isGroupPayer && this.shift.shift_type === "Group") {
          // No service code selected in group mode — clear filtered clients
          this.dspdGroupFilteredClients = [];
        }

        // Clear group_name when not a group shift
        if (!isGroupPayer || this.shift.shift_type !== "Group") {
          this.$set(this.shift, "group_name", "");
        }
      },
      immediate: false,
    },
    useAlternateLocation(newValue) {
      // Clear alternate location fields when checkbox is unchecked
      if (!newValue) {
        this.vrAlternateLocation = {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
        };
      }
    },
    shiftBeingEdited: {
      async handler(newShift) {
        if (newShift) {
          await this.populateShiftData(newShift);
        } else {
          this.resetShiftData();
        }
      },
      immediate: false,
    },
    visible: {
      async handler(newVal) {
        if (newVal) {
          // Always reset multi-step view state so the main form (with toggle) shows first
          this.showAddressSelection = false;
          this.showCaregiverTaskAssignment = false;

          if (!this.shiftBeingEdited) {
            this.clientAddressSelections = {};
            this.clientTaskCaregivers = {};
          }

          // Reset includeAdmins when opening the modal
          // populateShiftData will set it correctly based on the shift's caregiver
          this.includeAdmins = false;
          this.showAllCaregivers = false;

          // Load all service codes when modal opens
          await this.loadServiceCodes();

          if (!this.shiftBeingEdited) {
            // Only set payer from available clients when creating a new shift
            // When editing, populateShiftData will set the payer from the shift data
            if (this.availableClients && this.availableClients.length > 0) {
              this.shift.payer = this.availableClients[0].payer_type || 'VR';
            }
            // Load clients based on the current payer type
            await this.fetchClientsByPayerType(this.shift.payer);
            await this.loadAvailableServiceCodes();
            this.resetShiftData();
          } else {
            // When editing, use the shift's actual payer to load clients
            const editPayer = this.shiftBeingEdited.payer || this.shift.payer || 'VR';
            await this.fetchClientsByPayerType(editPayer);
          }
          // If shiftBeingEdited exists, populateShiftData will load available codes
          // after setting the client and payer
        }
      },
      immediate: false,
    },
    // Watch for date changes in single mode
    "shift.date": {
      handler() {
        this.fetchAvailableCaregivers();
      },
      immediate: false,
    },
    // Watch for start time changes
    "shift.scheduled_start": {
      handler() {
        // Validate that start time is before end time (if end time is already set)
        if (this.shift.scheduled_start && this.shift.scheduled_end) {
          if (this.shift.scheduled_start >= this.shift.scheduled_end) {
            this.$notifyError("End time must be after start time!");
            return;
          }
        }
        this.fetchAvailableCaregivers();
      },
      immediate: false,
    },
    // Watch for end time changes
    "shift.scheduled_end": {
      handler() {
        // Validate that end time is after start time
        if (this.shift.scheduled_start && this.shift.scheduled_end) {
          if (this.shift.scheduled_start >= this.shift.scheduled_end) {
            this.$notifyError("End time must be after start time!");
            return;
          }
        }
        this.fetchAvailableCaregivers();
      },
      immediate: false,
    },
    // Watch for recurring start date changes
    "shift.recurring_start_date": {
      handler() {
        this.fetchAvailableCaregivers();
      },
      immediate: false,
    },
    // Watch for recurring end date changes
    "shift.recurring_end_date": {
      handler() {
        this.fetchAvailableCaregivers();
      },
      immediate: false,
    },
    // Watch for weekday selection changes in recurring mode
    "shift.selectedWeekdays": {
      handler() {
        this.fetchAvailableCaregivers();
      },
      immediate: false,
      deep: true,
    },
    // Watch for include admins checkbox changes
    includeAdmins: {
      handler() {
        this.fetchAvailableCaregivers();
      },
      immediate: false,
    },
    // Watch for "show all caregivers" override changes
    showAllCaregivers: {
      handler() {
        this.fetchAvailableCaregivers();
      },
      immediate: false,
    },
    // Watch for address selection changes
    selectedAddressIndex: {
      handler() {
        // Clear geocode field when address changes
        this.clientGeocode.geocode = "";
      },
      immediate: false,
    },
    // Watch for Google Maps URL or Plus Code pasted into client geocode field
    "clientGeocode.geocode": {
      handler(newVal) {
        if (newVal) {
          if (
            newVal.includes("maps.app.goo.gl") ||
            newVal.includes("google.com/maps")
          ) {
            this.parseGoogleMapsUrl(newVal, "client");
          } else if (this.isPlusCode(newVal)) {
            this.convertPlusCodeToCoordinates(newVal, "client");
          }
        }
      },
      immediate: false,
    },
    // Watch for Google Maps URL or Plus Code pasted into VR alternate geocode field
    "vrAlternateLocation.geocode": {
      handler(newVal) {
        if (newVal) {
          if (
            newVal.includes("maps.app.goo.gl") ||
            newVal.includes("google.com/maps")
          ) {
            this.parseGoogleMapsUrl(newVal, "vr");
          } else if (this.isPlusCode(newVal)) {
            this.convertPlusCodeToCoordinates(newVal, "vr");
          }
        }
      },
      immediate: false,
    },
    // Watch for useSeparateClockAddresses toggle to auto-populate addresses
    useSeparateClockAddresses(newValue) {
      if (
        newValue &&
        this.availableAddresses &&
        this.availableAddresses.length > 0
      ) {
        // Auto-populate with first available address if not already set
        if (!this.clockInClientAddressId) {
          this.clockInClientAddressId = this.availableAddresses[0].id;
        }
        if (!this.clockOutClientAddressId) {
          this.clockOutClientAddressId = this.availableAddresses[0].id;
        }
      }
    },
    // Watch for availableAddresses changes to auto-populate when addresses load
    availableAddresses(newAddresses) {
      if (
        this.useSeparateClockAddresses &&
        newAddresses &&
        newAddresses.length > 0
      ) {
        // Auto-populate with first available address if not already set
        if (!this.clockInClientAddressId) {
          this.clockInClientAddressId = newAddresses[0].id;
        }
        if (!this.clockOutClientAddressId) {
          this.clockOutClientAddressId = newAddresses[0].id;
        }
      }
    },
  },
  methods: {
    formatTimezoneLabel,
    shortTzName(iana) {
      if (!iana) return "";
      const label = formatTimezoneLabel(iana);
      // "Eastern Time (America/New_York)" → "Eastern Time"
      return label.split(" (")[0];
    },
    async inferTimezoneFromAddress(addressId) {
      if (!addressId) return null;
      try {
        const result = await timezoneService.getFromAddress(addressId);
        return result?.timezone || null;
      } catch (e) {
        return null;
      }
    },
    async inferTimezoneForGroupShift() {
      if (!Array.isArray(this.shift.clientId)) return;
      this.inferredTimezone = null;
      this.shift.timezone = null;
      this.inferringTimezone = true;
      try {
        for (const clientId of this.shift.clientId) {
          const data = this.groupClientData[clientId];
          if (data && data.fetchedClientAddresses && data.fetchedClientAddresses.length > 0) {
            const addr = data.fetchedClientAddresses[data.selectedAddressIndex || 0];
            if (addr && addr.id) {
              const tz = await this.inferTimezoneFromAddress(addr.id);
              if (tz) {
                this.inferredTimezone = tz;
                this.shift.timezone = tz;
                return;
              }
            }
          }
        }
      } finally {
        this.inferringTimezone = false;
      }
    },
    removeSelectedClient(clientId) {
      this.shift.clientId = this.shift.clientId.filter((id) => id !== clientId);
    },
    getClientNameById(clientId) {
      const client = this.dspdGroupFilteredClients.find((c) => c.id === clientId)
        || this.filteredClients.find((c) => c.id === clientId);
      return client ? client.name : `Client #${clientId}`;
    },
    async fetchClientsByPayerType(payerType) {
      try {
        const response = await clientsService.list({
          payer_type: payerType,
        });
        this.filteredClients = (response || []).map((client) => ({
          id: Number(client.id) || client.id,
          name: `${client.first_name} ${client.last_name}`,
          addresses: client.addresses,
        }));
      } catch (error) {
        this.$notifyError("Error loading clients: " + error);
        this.filteredClients = [];
      }
    },
    updateSelectedServiceCodes(selectedCodes) {
      // Ensure selectedCodes is always an array
      // When multiSelect is false, AIVSelect returns a single ID, not an array
      const codesArray = Array.isArray(selectedCodes)
        ? selectedCodes
        : selectedCodes !== null && selectedCodes !== undefined
          ? [selectedCodes]
          : [];
      this.$set(this.shift, "selectedServiceCodes", codesArray);
    },
    getClientAddresses(clientId) {
      // Get addresses for a specific client
      const client = this.filteredClients.find((c) => c.id === clientId);
      if (!client) return [];

      // Return addresses in the format expected by AIVSelect
      if (client.addresses && Array.isArray(client.addresses)) {
        return client.addresses.map((addr) => ({
          id: addr.id,
          name: this.formatAddressForSelect(addr),
        }));
      } else if (client.address) {
        // Fallback to single address if addresses array doesn't exist
        return [
          {
            id: client.address.id,
            name: this.formatAddressForSelect(client.address),
          },
        ];
      }
      return [];
    },
    formatAddressForSelect(address) {
      // Format address for display in select dropdown
      if (!address) return "";
      const parts = [
        address.address1,
        address.city,
        address.state,
        address.zip,
      ];
      return parts.filter(Boolean).join(", ");
    },
    async fetchGroupClientData() {
      // Fetch full client data for each selected client in group shift
      if (!Array.isArray(this.shift.clientId)) return;

      for (const clientId of this.shift.clientId) {
        try {
          const client = await clientsService.getById(clientId);

          // Initialize full address data for this client
          const clientAddresses =
            client.addresses && client.addresses.length > 0
              ? client.addresses
              : client.address
                ? [client.address]
                : [];

          // Don't overwrite if groupClientData already exists for this client (from populateShiftData)
          if (this.groupClientData[clientId]) {
            continue;
          }

          this.$set(this.groupClientData, clientId, {
            fetchedClientAddresses: clientAddresses,
            selectedAddressIndex: 0,
            useSeparateClockAddresses: false,
            useAlternateLocation: false,
            clockInClientAddressId:
              clientAddresses.length > 0 ? clientAddresses[0].id : null,
            clockOutClientAddressId:
              clientAddresses.length > 0 ? clientAddresses[0].id : null,
            clockInUseAlternateAddress: false,
            clockOutUseAlternateAddress: false,
            clockInLocation: {
              address1: "",
              address2: "",
              city: "",
              state: "",
              zip: "",
              geocode: "",
            },
            clockOutLocation: {
              address1: "",
              address2: "",
              city: "",
              state: "",
              zip: "",
              geocode: "",
            },
            vrAlternateLocation: {
              address1: "",
              address2: "",
              city: "",
              state: "",
              zip: "",
              geocode: "",
            },
            clientGeocode: {
              geocode: "",
            },
          });
        } catch (error) {
          this.$notifyError(`Failed to load data for client ${clientId}`);
        }
      }
    },
    getClientData(clientId) {
      // Get the address data for a specific client in group shift
      return (
        this.groupClientData[clientId] || {
          fetchedClientAddresses: [],
          selectedAddressIndex: 0,
          useSeparateClockAddresses: false,
          useAlternateLocation: false,
          clockInClientAddressId: null,
          clockOutClientAddressId: null,
          clockInUseAlternateAddress: false,
          clockOutUseAlternateAddress: false,
          clockInLocation: {},
          clockOutLocation: {},
          vrAlternateLocation: {},
          clientGeocode: {},
        }
      );
    },
    getClientSelectedAddress(clientId) {
      // Get the selected address for a specific client in group shift
      const clientData = this.getClientData(clientId);
      if (
        clientData.fetchedClientAddresses &&
        clientData.fetchedClientAddresses.length > 0
      ) {
        const index = Math.min(
          clientData.selectedAddressIndex,
          clientData.fetchedClientAddresses.length - 1,
        );
        return clientData.fetchedClientAddresses[index];
      }
      return null;
    },
    hasClientAddressGeocode(clientId) {
      // Check if the selected client address has a geocode
      const address = this.getClientSelectedAddress(clientId);
      if (!address) return true; // Don't show field if no address
      return !!(address.geocode && address.geocode.trim());
    },
    updateClientAddressIndex(clientId, index) {
      // Update the selected address index for a client
      this.$set(this.groupClientData[clientId], "selectedAddressIndex", index);
    },
    toggleClientClockInAlternateAddress(clientId) {
      // Toggle clock in alternate address for a client
      const clientData = this.getClientData(clientId);
      this.$set(
        this.groupClientData[clientId],
        "clockInUseAlternateAddress",
        !clientData.clockInUseAlternateAddress,
      );
    },
    toggleClientClockOutAlternateAddress(clientId) {
      // Toggle clock out alternate address for a client
      const clientData = this.getClientData(clientId);
      this.$set(
        this.groupClientData[clientId],
        "clockOutUseAlternateAddress",
        !clientData.clockOutUseAlternateAddress,
      );
    },
    updateClientClockInAddressId(clientId, addressId) {
      // Update clock in address ID for a client
      this.$set(
        this.groupClientData[clientId],
        "clockInClientAddressId",
        addressId,
      );
    },
    updateClientClockOutAddressId(clientId, addressId) {
      // Update clock out address ID for a client
      this.$set(
        this.groupClientData[clientId],
        "clockOutClientAddressId",
        addressId,
      );
    },
    getClientTaskCaregivers(clientId, taskType) {
      // Get the selected caregivers for a specific task for a client
      if (!this.clientTaskCaregivers[clientId]) {
        this.$set(this.clientTaskCaregivers, clientId, {
          pickUp: [],
          dropOff: [],
          shiftNotes: [],
          medications: [],
        });
      }
      return this.clientTaskCaregivers[clientId][taskType] || [];
    },
    addAdditionalCaregiver() {
      this.additionalCaregivers.push({ id: null, role: "shadow" });
    },
    removeAdditionalCaregiver(idx) {
      this.additionalCaregivers.splice(idx, 1);
    },
    additionalCaregiverOptions(idx) {
      // Everyone except the primary and the other additional rows
      const primaryIds = Array.isArray(this.shift.caregiverId)
        ? this.shift.caregiverId
        : this.shift.caregiverId
          ? [this.shift.caregiverId]
          : [];
      const takenElsewhere = this.additionalCaregivers
        .filter((_, i) => i !== idx)
        .map((e) => e.id)
        .filter(Boolean);
      const options = (this.filteredCaregivers || []).filter(
        (c) => !primaryIds.includes(c.id) && !takenElsewhere.includes(c.id),
      );
      // Keep the current selection resolvable even when the availability
      // filter excludes that caregiver (e.g., editing an existing shadow)
      const current = this.additionalCaregivers[idx];
      if (current?.id && !options.some((o) => o.id === current.id)) {
        options.push({
          id: current.id,
          name: current.name || `Caregiver #${current.id}`,
        });
      }
      return options;
    },
    updateClientTaskCaregivers(clientId, taskType, caregiverIds) {
      // Update the selected caregivers for a specific task for a client
      if (!this.clientTaskCaregivers[clientId]) {
        this.$set(this.clientTaskCaregivers, clientId, {
          pickUp: [],
          dropOff: [],
          shiftNotes: [],
          medications: [],
        });
      }
      // Always store as array for consistent payload format
      const value = Array.isArray(caregiverIds) ? caregiverIds : (caregiverIds ? [caregiverIds] : []);
      this.$set(this.clientTaskCaregivers[clientId], taskType, value);

      // Auto-populate other fields when pickup caregiver is selected
      if (taskType === 'pickUp' && value.length > 0) {
        const tasks = this.clientTaskCaregivers[clientId];
        if (!tasks.dropOff || tasks.dropOff.length === 0) {
          this.$set(tasks, 'dropOff', [...value]);
        }
        if (!tasks.shiftNotes || tasks.shiftNotes.length === 0) {
          this.$set(tasks, 'shiftNotes', [...value]);
        }
      }
    },
    toggleClientMedications(clientId, enabled) {
      this.$set(this.clientMedicationsEnabled, clientId, enabled);
      if (!enabled) {
        // Clear the medications caregiver when unchecked
        if (this.clientTaskCaregivers[clientId]) {
          this.$set(this.clientTaskCaregivers[clientId], "medications", []);
        }
      }
    },
    async fetchClientMedications() {
      if (!this.shift.clientId || !Array.isArray(this.shift.clientId)) return;
      if (this.shift.payer !== "DSPD") return;
      this.loadingClientMedications = true;
      try {
        for (const clientId of this.shift.clientId) {
          try {
            const response = await clientsService.getMedications(clientId);
            // API returns { clientId, clientName, medications: [...] }
            let meds = [];
            if (response && Array.isArray(response.medications)) {
              meds = response.medications;
            } else if (Array.isArray(response)) {
              meds = response;
            }
            this.$set(this.clientMedications, clientId, meds);
          } catch (err) {
            this.$set(this.clientMedications, clientId, []);
          }
        }
      } finally {
        this.loadingClientMedications = false;
      }
    },
    getCaregiverOptions() {
      // Return caregiver options from the selected caregivers
      if (Array.isArray(this.shift.caregiverId)) {
        return this.shift.caregiverId.map((id) => {
          const caregiver = this.availableCaregiversForShift.find(
            (c) => c.id === id,
          );
          if (caregiver) {
            return { id, name: caregiver.name || `${caregiver.first_name} ${caregiver.last_name}` };
          }
          // Fall back to shiftBeingEdited.caregivers for name resolution
          const assigned = this.shiftBeingEdited?.caregivers?.find((c) => c.id === id);
          return { id, name: assigned ? assigned.name : `Caregiver ${id}` };
        });
      }
      return [];
    },
    async generateGeocodeFromAddress(fullAddress) {
      const encodedAddress = encodeURIComponent(fullAddress);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1`,
        {
          headers: {
            "User-Agent": "OppoApp/1.0",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Geocoding service unavailable");
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        return `${lat},${lng}`;
      } else {
        this.$notifyError(
          "Could not find coordinates for this address. Please verify the address is correct.",
        );
        return null;
      }
    },
    async generateClientGeocodeFromAddressForGroup(clientId) {
      // Generate geocode from client address for group shift
      const address = this.getClientSelectedAddress(clientId);
      if (!address) {
        this.$notifyError("No client address available");
        return;
      }

      if (!address.address1 || !address.city || !address.state) {
        this.$notifyError(
          "Client address is incomplete. Cannot generate geocode.",
        );
        return;
      }

      try {
        const fullAddress =
          `${address.address1}, ${address.city}, ${address.state} ${address.zip || ""}`.trim();
        const geocode = await this.generateGeocodeFromAddress(fullAddress);
        if (geocode) {
          this.$set(
            this.groupClientData[clientId].clientGeocode,
            "geocode",
            geocode,
          );
          this.$notifySuccess("Geocode generated successfully");
        }
      } catch (error) {
        this.$notifyError("Failed to generate geocode");
      }
    },
    async generateAlternateGeocodeForGroup(clientId) {
      // Generate geocode for alternate location for group shift
      const altLocation = this.getClientData(clientId).vrAlternateLocation;
      if (!altLocation.address1 || !altLocation.city || !altLocation.state) {
        this.$notifyError(
          "Alternate location is incomplete. Cannot generate geocode.",
        );
        return;
      }

      try {
        const fullAddress =
          `${altLocation.address1}, ${altLocation.city}, ${altLocation.state} ${altLocation.zip || ""}`.trim();
        const geocode = await this.generateGeocodeFromAddress(fullAddress);
        if (geocode) {
          this.$set(
            this.groupClientData[clientId].vrAlternateLocation,
            "geocode",
            geocode,
          );
          this.$notifySuccess("Geocode generated successfully");
        }
      } catch (error) {
        this.$notifyError("Failed to generate geocode");
      }
    },
    async generateClockInGeocodeForGroup(clientId) {
      // Generate geocode for clock in location for group shift
      const clockInLocation = this.getClientData(clientId).clockInLocation;
      if (
        !clockInLocation.address1 ||
        !clockInLocation.city ||
        !clockInLocation.state
      ) {
        this.$notifyError(
          "Clock in location is incomplete. Cannot generate geocode.",
        );
        return;
      }

      try {
        const fullAddress =
          `${clockInLocation.address1}, ${clockInLocation.city}, ${clockInLocation.state} ${clockInLocation.zip || ""}`.trim();
        const geocode = await this.generateGeocodeFromAddress(fullAddress);
        if (geocode) {
          this.$set(
            this.groupClientData[clientId].clockInLocation,
            "geocode",
            geocode,
          );
          this.$notifySuccess("Geocode generated successfully");
        }
      } catch (error) {
        this.$notifyError("Failed to generate geocode");
      }
    },
    async generateClockOutGeocodeForGroup(clientId) {
      // Generate geocode for clock out location for group shift
      const clockOutLocation = this.getClientData(clientId).clockOutLocation;
      if (
        !clockOutLocation.address1 ||
        !clockOutLocation.city ||
        !clockOutLocation.state
      ) {
        this.$notifyError(
          "Clock out location is incomplete. Cannot generate geocode.",
        );
        return;
      }

      try {
        const fullAddress =
          `${clockOutLocation.address1}, ${clockOutLocation.city}, ${clockOutLocation.state} ${clockOutLocation.zip || ""}`.trim();
        const geocode = await this.generateGeocodeFromAddress(fullAddress);
        if (geocode) {
          this.$set(
            this.groupClientData[clientId].clockOutLocation,
            "geocode",
            geocode,
          );
          this.$notifySuccess("Geocode generated successfully");
        }
      } catch (error) {
        this.$notifyError("Failed to generate geocode");
      }
    },
    handleBack() {
      // Handle back navigation based on current view
      if (this.showCaregiverTaskAssignment) {
        // Go back from caregiver task assignment to address selection
        this.showCaregiverTaskAssignment = false;
        this.showAddressSelection = true;
      } else if (this.showAddressSelection) {
        // Go back from address selection to main form
        this.showAddressSelection = false;
      }
    },
    isPlusCode(input) {
      // Plus Code format: 8 characters + plus sign + 2-3 characters
      // Example: 849VCWC8+R9 or 849VCWC8+R9 Salt Lake City
      const plusCodePattern =
        /^[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}(\s|$)/i;
      return plusCodePattern.test(input.trim());
    },
    async convertPlusCodeToCoordinates(plusCode, target) {
      try {
        // Extract just the Plus Code part (in case there's a locality after it)
        const cleanPlusCode = plusCode.trim().split(/\s+/)[0];

        const olc = new OpenLocationCode();

        // Check if it's a valid Plus Code
        if (!olc.isValid(cleanPlusCode)) {
          this.$notifyError("Invalid Plus Code format.");
          return;
        }

        // Decode the Plus Code to get coordinates
        const decoded = olc.decode(cleanPlusCode);

        // Get the center coordinates
        const lat = decoded.latitudeCenter;
        const lng = decoded.longitudeCenter;
        const geocode = `${lat},${lng}`;

        if (target === "client") {
          this.clientGeocode.geocode = geocode;
        } else if (target === "vr") {
          this.vrAlternateLocation.geocode = geocode;
        }

        this.$notifySuccess(`Plus Code converted to coordinates!`);
      } catch (error) {
        this.$notifyError(
          "Error converting Plus Code. Please enter coordinates manually in format: latitude,longitude",
        );
      }
    },
    async parseGoogleMapsUrl(url, target) {
      try {
        let urlToParse = url;

        // If it's a shortened goo.gl URL, inform user to use the full URL
        if (url.includes("maps.app.goo.gl")) {
          this.$notifyError(
            "Please open the link and copy the full URL from your browser, or copy the Plus Code instead.",
          );
          // Clear the field so they can paste again
          if (target === "client") {
            this.clientGeocode.geocode = "";
          } else if (target === "vr") {
            this.vrAlternateLocation.geocode = "";
          }
          return;
        }

        // Try to extract coordinates from various Google Maps URL formats
        let lat = null;
        let lng = null;

        // Format 1: /@lat,lng,zoom or /@lat,lng
        const atMatch = urlToParse.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (atMatch) {
          lat = parseFloat(atMatch[1]);
          lng = parseFloat(atMatch[2]);
        }

        // Format 2: /place/.../@lat,lng
        if (!lat && !lng) {
          const placeMatch = urlToParse.match(
            /place\/[^@]*@(-?\d+\.\d+),(-?\d+\.\d+)/,
          );
          if (placeMatch) {
            lat = parseFloat(placeMatch[1]);
            lng = parseFloat(placeMatch[2]);
          }
        }

        // Format 3: ?q=lat,lng or &q=lat,lng
        if (!lat && !lng) {
          const qMatch = urlToParse.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
          if (qMatch) {
            lat = parseFloat(qMatch[1]);
            lng = parseFloat(qMatch[2]);
          }
        }

        // Format 4: /search/lat,lng
        if (!lat && !lng) {
          const searchMatch = urlToParse.match(
            /\/search\/(-?\d+\.\d+),(-?\d+\.\d+)/,
          );
          if (searchMatch) {
            lat = parseFloat(searchMatch[1]);
            lng = parseFloat(searchMatch[2]);
          }
        }

        if (lat !== null && lng !== null) {
          const geocode = `${lat},${lng}`;
          if (target === "client") {
            this.clientGeocode.geocode = geocode;
          } else if (target === "vr") {
            this.vrAlternateLocation.geocode = geocode;
          }
          this.$notifySuccess("Coordinates extracted from Google Maps URL!");
        } else {
          this.$notifyError(
            "Could not extract coordinates from URL. Please enter coordinates manually in format: latitude,longitude",
          );
        }
      } catch (error) {
        this.$notifyError(
          "Error parsing Google Maps URL. Please enter coordinates manually.",
        );
      }
    },
    extractDateAndTime(dateStr, timeStr) {
      if (!timeStr || !dateStr) return null;

      // Extract just the date part (YYYY-MM-DD format)
      const date = dateStr.split("T")[0];

      return { date, time: timeStr };
    },
    async fetchAvailableCaregivers() {
      // Skip if we're currently populating shift data (to avoid race conditions from watchers)
      if (this.isPopulatingShift) {
        return;
      }

      // Check if we have the required fields for the current shift mode
      const hasRequiredFields = !this.isCaregiverDropdownDisabled;

      if (!hasRequiredFields) {
        this.availableCaregiversForShift = [];
        this.hasCheckedAvailability = false;
        return;
      }

      try {
        this.isLoadingAvailableCaregivers = true;

        let params = {};

        if (this.shiftMode === "single") {
          // For single shifts
          const dateTime = this.extractDateAndTime(
            this.shift.date,
            this.shift.scheduled_start,
          );

          if (dateTime && this.shift.scheduled_end) {
            // Validate that start time is before end time
            if (this.shift.scheduled_start >= this.shift.scheduled_end) {
              // Invalid time range - don't fetch caregivers
              this.availableCaregiversForShift = [];
              this.isLoadingAvailableCaregivers = false;
              return;
            }

            // Convert local date/times to UTC before sending to backend
            const localDateTime = new Date(
              `${dateTime.date}T${this.shift.scheduled_start}:00`,
            );
            const localEndDateTime = new Date(
              `${dateTime.date}T${this.shift.scheduled_end}:00`,
            );

            // Format times in UTC
            const utcStartTime = localDateTime.toISOString();
            const utcEndTime = localEndDateTime.toISOString();

            params = {
              date: dateTime.date,
              start_time: utcStartTime,
              end_time: utcEndTime,
            };
          }
        } else {
          // For recurring shifts - send all required parameters
          if (
            this.shift.recurring_start_date &&
            this.shift.scheduled_start &&
            this.shift.scheduled_end &&
            this.shift.selectedWeekdays &&
            this.shift.selectedWeekdays.length > 0
          ) {
            // Validate that start time is before end time
            if (this.shift.scheduled_start >= this.shift.scheduled_end) {
              // Invalid time range - don't fetch caregivers
              this.availableCaregiversForShift = [];
              this.isLoadingAvailableCaregivers = false;
              return;
            }

            // Convert local date/times to UTC before sending to backend
            const localDateTime = new Date(
              `${this.shift.recurring_start_date}T${this.shift.scheduled_start}:00`,
            );
            const localEndDateTime = new Date(
              `${this.shift.recurring_start_date}T${this.shift.scheduled_end}:00`,
            );

            // Format times in UTC
            const utcStartTime = localDateTime.toISOString();
            const utcEndTime = localEndDateTime.toISOString();

            params = {
              start_time: utcStartTime,
              end_time: utcEndTime,
              recurring_start_date: this.shift.recurring_start_date,
              ...(this.shift.recurring_end_date ? { recurring_end_date: this.shift.recurring_end_date } : {}),
              selected_weekdays: JSON.stringify(this.shift.selectedWeekdays),
            };
          }
        }

        if (Object.keys(params).length > 0) {
          // Add include_admins parameter based on the checkbox state
          params.include_admins = this.includeAdmins;
          if (this.showAllCaregivers) {
            params.ignore_availability = true;
          }

          // If editing a shift, pass the shift ID to exclude it from conflict checks
          if (this.shiftBeingEdited && this.shiftBeingEdited.id) {
            params.exclude_shift_id = this.shiftBeingEdited.id;
          }

          // Pass client ID for ABI/TBI certification filtering
          const clientId = Array.isArray(this.shift.clientId) ? this.shift.clientId[0] : this.shift.clientId;
          if (clientId) {
            params.client_id = clientId;
          }

          const availableCaregivers =
            await caregiversService.getAvailable(params);
          this.availableCaregiversForShift = availableCaregivers || [];
          this.hasCheckedAvailability = true;

          // For recurring shifts, the backend's cross-date availability check may exclude the
          // pre-selected caregiver even when they have no actual conflicts. Re-add them so they
          // stay selectable in the dropdown; real conflicts will be caught on save.
          if (this.shiftMode === "recurring" && this.sourceCaregiverId) {
            const alreadyIncluded = this.availableCaregiversForShift.some(
              (c) => c.id === this.sourceCaregiverId,
            );
            if (!alreadyIncluded) {
              const sourceCaregiver = this.availableCaregivers.find(
                (c) => c.id === this.sourceCaregiverId,
              );
              if (sourceCaregiver) {
                this.availableCaregiversForShift = [
                  sourceCaregiver,
                  ...this.availableCaregiversForShift,
                ];
              }
            }
          }

          // Auto-populate Employment Specialist's name if they are available
          if (
            this.isEmploymentSpecialist &&
            !this.shiftBeingEdited?.id &&
            !this.shift.caregiverId
          ) {
            const currentUser = this.loginStore.user;
            const isAvailable = this.availableCaregiversForShift.some(
              (caregiver) => caregiver.id === currentUser.id,
            );
            if (isAvailable) {
              this.shift.caregiverId = currentUser.id;
            }
          }

          // Only clear selected caregiver if they're no longer available and we're not editing
          // When editing, the caregiver should stay selected even if they appear to not be "available"
          // because they're assigned to this shift
          if (this.shift.caregiverId && !this.shiftBeingEdited?.id) {
            if (Array.isArray(this.shift.caregiverId)) {
              // For group shifts with multi-select, filter out caregivers that are no longer available
              const availableIds = this.availableCaregiversForShift.map(
                (c) => c.id,
              );
              const stillAvailable = this.shift.caregiverId.filter((id) =>
                availableIds.includes(id),
              );
              if (stillAvailable.length !== this.shift.caregiverId.length) {
                this.shift.caregiverId = stillAvailable.length
                  ? stillAvailable
                  : null;
              }
            } else {
              const isStillAvailable = this.availableCaregiversForShift.some(
                (caregiver) => caregiver.id === this.shift.caregiverId,
              );
              if (!isStillAvailable) {
                if (this.sourceCaregiverId) {
                  const conflictCaregiver = this.availableCaregivers.find(
                    (c) => c.id === this.sourceCaregiverId,
                  );
                  const caregiverName = conflictCaregiver?.name ?? "The selected caregiver";
                  this.$notifyError(`${caregiverName} has a scheduling conflict at this time.`);
                }
                this.shift.caregiverId = null;
              }
            }
          }
        }
      } catch (error) {
        this.availableCaregiversForShift = [];
        this.hasCheckedAvailability = true;
      } finally {
        this.isLoadingAvailableCaregivers = false;
      }
    },
    handleTimeOkClick(fieldKey) {
      // Auto-populate end time when start time OK is clicked, but only if end time is empty
      if (
        fieldKey === "scheduled_start" &&
        this.shift.scheduled_start &&
        !this.shift.scheduled_end
      ) {
        // Parse the start time and add 6 hours for DSPD, 2 hours for everything else
        const [hours, minutes] = this.shift.scheduled_start
          .split(":")
          .map(Number);
        const startDate = new Date();
        startDate.setHours(hours, minutes, 0, 0);

        const hoursToAdd = this.shift.payer === "DSPD" && this.shift.shift_type === "Group" ? 6 : 2;
        const endDate = new Date(startDate.getTime() + hoursToAdd * 60 * 60 * 1000);

        // Format back to HH:MM
        const endTime = endDate.toTimeString().slice(0, 5);

        this.shift.scheduled_end = endTime;

        // Trigger available caregivers fetch after auto-populating end time
        this.$nextTick(() => {
          this.fetchAvailableCaregivers();
        });
      }
    },
    async loadServiceCodes() {
      try {
        const [serviceCodes, nonServiceCodes] = await Promise.all([
          serviceCodesService.listVR(), // Only load VR service codes for shifts
          nonServiceCodesService.list(),
        ]);
        this.serviceCodes = serviceCodes || [];
        this.nonServiceCodes = nonServiceCodes || [];
        // Use loadAvailableServiceCodes to properly handle all payer types including DSPD
        await this.loadAvailableServiceCodes();
      } catch (error) {
        this.serviceCodes = [];
        this.nonServiceCodes = [];
        this.availableServiceCodes = [];
      }
    },
    async loadAvailableServiceCodes() {
      try {
        // Load appropriate service codes based on payer type
        if (this.shift.payer === "Non Service") {
          // Use already-loaded non-service codes (no API call needed)
          this.availableServiceCodes = this.nonServiceCodes || [];
        } else if (this.shift.payer === "VR" && this.shift.clientId) {
          // For VR payer with a selected client, fetch only service codes from active authorizations
          const serviceCodes = await serviceCodesService.getByClient({
            clientId: this.shift.clientId,
            payer_type: "VR",
          });
          this.availableServiceCodes = serviceCodes || [];

          // Auto-populate if only one service code is available and nothing is currently selected
          if (
            serviceCodes.length === 1 &&
            (!this.shift.selectedServiceCodes ||
              this.shift.selectedServiceCodes.length === 0)
          ) {
            this.$set(this.shift, "selectedServiceCodes", [serviceCodes[0].id]);
          }
        } else if (this.shift.payer === "VR" && !this.shift.clientId) {
          // For VR payer without a client, don't load any service codes yet
          this.availableServiceCodes = [];
        } else if (this.shift.payer === "DSPD") {
          if (this.shift.shift_type === "1-on-1" && this.shift.clientId) {
            // DSPD 1-on-1 with client selected: fetch service codes by client auth
            // Exclude group-only codes (DSP, DSG)
            const serviceCodes = await serviceCodesService.getByClient({
              clientId: this.shift.clientId,
              payer_type: "DSPD",
            });
            const groupCodes = ["DSP", "DSG"];
            this.availableServiceCodes = (serviceCodes || []).filter(
              sc => !groupCodes.includes(sc.code)
            );

            // Auto-populate if only one service code is available and nothing is currently selected
            if (
              this.availableServiceCodes.length === 1 &&
              (!this.shift.selectedServiceCodes ||
                this.shift.selectedServiceCodes.length === 0)
            ) {
              this.$set(this.shift, "selectedServiceCodes", [this.availableServiceCodes[0].id]);
            }
          } else if (this.shift.shift_type === "1-on-1" && !this.shift.clientId) {
            // DSPD 1-on-1 without client: no service codes yet
            this.availableServiceCodes = [];
          } else if (this.shift.shift_type === "Group") {
            // DSPD Group: show only group service codes (DSP, DSG)
            const dspdCodes = await serviceCodesService.listDSPD();
            const groupCodes = ["DSP", "DSG"];
            this.availableServiceCodes = (dspdCodes || []).filter(
              sc => groupCodes.includes(sc.code)
            );
          } else {
            // Fallback for DSPD
            const dspdCodes = await serviceCodesService.listDSPD();
            this.availableServiceCodes = dspdCodes || [];
          }
        } else if (this.shift.payer === "Private") {
          if (this.shift.shift_type === "1-on-1" && this.shift.clientId) {
            // PP 1-on-1 with client selected: fetch service codes by client auth
            const serviceCodes = await serviceCodesService.getByClient({
              clientId: this.shift.clientId,
              payer_type: "PP",
            });
            const groupCodes = ["PP-DSP", "PP-DSG"];
            this.availableServiceCodes = (serviceCodes || []).filter(
              sc => !groupCodes.includes(sc.code)
            );

            if (
              this.availableServiceCodes.length === 1 &&
              (!this.shift.selectedServiceCodes ||
                this.shift.selectedServiceCodes.length === 0)
            ) {
              this.$set(this.shift, "selectedServiceCodes", [this.availableServiceCodes[0].id]);
            }
          } else if (this.shift.shift_type === "1-on-1" && !this.shift.clientId) {
            // PP 1-on-1 without client: no service codes yet
            this.availableServiceCodes = [];
          } else if (this.shift.shift_type === "Group") {
            // PP Group: show only PP group service codes (PP-DSP, PP-DSG)
            const ppCodes = await serviceCodesService.listPP();
            const groupCodes = ["PP-DSP", "PP-DSG"];
            this.availableServiceCodes = (ppCodes || []).filter(
              sc => groupCodes.includes(sc.code)
            );
          } else {
            // Fallback for PP
            const ppCodes = await serviceCodesService.listPP();
            this.availableServiceCodes = ppCodes || [];
          }
        } else {
          // For other payers, use already-loaded service codes (no API call needed)
          this.availableServiceCodes = this.serviceCodes || [];
        }

        // Don't clear selected service codes - user may want to select them before choosing a client
      } catch (error) {
        this.availableServiceCodes = [];
        // Don't clear selected service codes on error
      }
    },
    async checkAuthRemaining() {
      this.authWarning = false;
      this.authWarningMessage = "";
      this.authAcknowledged = false;

      if (this.shift.payer === "Non Service" || !this.shift.clientId) return;
      if (!this.shift.selectedServiceCodes || this.shift.selectedServiceCodes.length === 0) return;
      if (!this.shift.scheduled_start || !this.shift.scheduled_end) return;

      // Calculate shift duration in hours
      const [startH, startM] = this.shift.scheduled_start.split(":").map(Number);
      const [endH, endM] = this.shift.scheduled_end.split(":").map(Number);
      let startTotal = startH * 60 + startM;
      let endTotal = endH * 60 + endM;
      if (endTotal <= startTotal) endTotal += 24 * 60;
      const durationHours = (endTotal - startTotal) / 60;

      try {
        // For group shifts, check the first client; for single, use the one client
        const clientId = Array.isArray(this.shift.clientId)
          ? this.shift.clientId[0]
          : this.shift.clientId;
        if (!clientId) return;

        const auths = await authorizationsService.getByClientId(clientId);
        if (!auths || auths.length === 0) return;

        const warnings = [];
        for (const scId of this.shift.selectedServiceCodes) {
          const matchingAuths = auths.filter(a => a.id_service_codes === scId && a.status === "active");
          if (matchingAuths.length === 0) continue;

          const scName = this.availableServiceCodes.find(sc => sc.id === scId)?.code || "Unknown";
          const isDSPD = this.shift.payer === "DSPD" || this.shift.payer === "Private";

          if (isDSPD) {
            const totalUnitsRemaining = matchingAuths.reduce((sum, a) => {
              return sum + Math.max(0, (parseFloat(a.units) || 0) - (parseFloat(a.units_used) || 0));
            }, 0);
            const unitsNeeded = Array.isArray(this.shift.clientId) ? this.shift.clientId.length : 1;
            if (unitsNeeded > totalUnitsRemaining) {
              warnings.push(`Service code ${scName}: ${totalUnitsRemaining} units remaining, but this shift requires ${unitsNeeded} unit(s).`);
            }
          } else {
            const totalHoursRemaining = matchingAuths.reduce((sum, a) => sum + (a.hours_remaining || 0), 0);
            if (durationHours > totalHoursRemaining) {
              warnings.push(`Service code ${scName}: ${totalHoursRemaining.toFixed(1)} hours remaining, but this shift is ${durationHours.toFixed(1)} hours.`);
            }
          }
        }

        if (warnings.length > 0) {
          this.authWarning = true;
          this.authWarningMessage = "Warning: " + warnings.join(" ");
        }
      } catch (error) {
      }
    },
    updateAvailableServiceCodes() {
      if (this.shift.payer === "Non Service") {
        this.availableServiceCodes = this.nonServiceCodes || [];
      } else {
        this.availableServiceCodes = this.serviceCodes || [];
      }
    },
    async populateShiftData(shift) {
      // Set flag to prevent watchers from triggering during population
      this.isPopulatingShift = true;

      let caregiverId = null;
      let isAdminCaregiver = false;

      // Check if this is a DSPD group shift (needs array format for multi-select dropdowns)
      const isDSPDGroup = shift.payer === "DSPD" && (shift.shift_type === "Group");

      // Non-group shifts: shadow/training assignments populate the
      // additional-caregivers editor; only primaries drive the main field
      this.additionalCaregivers = [];
      let caregiversForField = shift.caregivers || [];
      if (shift.shift_type !== "Group" && caregiversForField.length > 0) {
        this.additionalCaregivers = caregiversForField
          .filter((c) => (c.role || "primary") !== "primary")
          .map((c) => ({
            id: Number(c.id) || c.id,
            role: c.role,
            name:
              c.name ||
              `${c.first_name || ""} ${c.last_name || ""}`.trim(),
          }));
        const primaries = caregiversForField.filter(
          (c) => (c.role || "primary") === "primary",
        );
        caregiversForField =
          primaries.length > 0 ? primaries : [caregiversForField[0]];
      }

      // Check if this shift has multiple caregivers (for DSPD group shifts)
      if (caregiversForField && caregiversForField.length > 1) {
        // Store as array for multi-select
        caregiverId = caregiversForField.map((c) => Number(c.id) || c.id);
        // Check if any caregiver is an admin
        isAdminCaregiver = caregiversForField.some(
          (c) => c.user_type === "Admin" || c.user_type === "Lead",
        );
      } else if (caregiversForField && caregiversForField.length === 1) {
        // For DSPD group shifts, always use array format for multi-select compatibility
        caregiverId = isDSPDGroup
          ? [Number(caregiversForField[0].id) || caregiversForField[0].id]
          : Number(caregiversForField[0].id) || caregiversForField[0].id;
        // Check if the caregiver is an admin
        isAdminCaregiver =
          caregiversForField[0].user_type === "Admin" ||
          caregiversForField[0].user_type === "Lead";
      } else if (shift.caregiverId) {
        // Direct caregiverId (e.g., creating from caregiver calendar)
        caregiverId = Number(shift.caregiverId);
      }

      let clientId = null;
      // Check if this shift has multiple clients (for DSPD group shifts)
      if (shift.clients && shift.clients.length > 1) {
        // Store as array for multi-select
        clientId = shift.clients.map((c) => Number(c.id) || c.id);
      } else if (shift.clients && shift.clients.length === 1) {
        // For DSPD group shifts, always use array format for multi-select compatibility
        clientId = isDSPDGroup
          ? [Number(shift.clients[0].id) || shift.clients[0].id]
          : Number(shift.clients[0].id) || shift.clients[0].id;
      } else if (shift.clientId) {
        clientId = isDSPDGroup ? [Number(shift.clientId)] : Number(shift.clientId);
      }

      // Set the includeAdmins flag based on whether the caregiver is an admin
      // The parent should have already loaded caregivers with admins if needed
      this.includeAdmins = isAdminCaregiver;

      // Fetch client addresses FIRST before we check for alternate location or set shift data
      // Only fetch addresses for single client selection (not for multi-select group shifts)
      if (clientId && !Array.isArray(clientId)) {
        try {
          const client = await clientsService.getById(clientId);

          // Store all addresses for the client
          if (client.addresses && client.addresses.length > 0) {
            this.fetchedClientAddresses = client.addresses;
          } else if (client.address) {
            this.fetchedClientAddresses = [client.address];
          } else {
            this.fetchedClientAddresses = [];
          }
        } catch (error) {
          this.fetchedClientAddresses = [];
        }
      } else {
        // Clear addresses for multi-client selection
        this.fetchedClientAddresses = [];
      }

      // Convert UTC times back to local time for editing
      const convertUTCToLocal = (timeStr, dateStr) => {
        if (!timeStr) return timeStr;

        // Handle new format where timeStr is already a full UTC datetime
        if (timeStr.includes("T")) {
          const utcDate = new Date(timeStr);
          if (!isNaN(utcDate.getTime())) {
            // Format as HH:MM for the time input
            return utcDate.toLocaleTimeString("en-US", {
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
            });
          }
          return timeStr;
        }

        // Legacy format: combine date and time
        if (!dateStr) return timeStr;
        const dateOnly = dateStr.split("T")[0];
        const utcDateTime = `${dateOnly}T${timeStr}Z`; // Add 'Z' to indicate UTC
        const utcDate = new Date(utcDateTime);

        // Check if the date is valid
        if (!isNaN(utcDate.getTime())) {
          // Format as HH:MM for the time input
          return utcDate.toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
          });
        }
        return timeStr;
      };

      const dateOnly = shift.date ? shift.date.split("T")[0] : null;
      this.shift = {
        id: shift.id,
        date: shift.date,
        scheduled_start: convertUTCToLocal(
          shift.scheduled_start || shift.startTime,
          dateOnly,
        ),
        scheduled_end: convertUTCToLocal(
          shift.scheduled_end || shift.endTime,
          dateOnly,
        ),
        clientId: clientId,
        caregiverId: caregiverId,
        shift_type: shift.shift_type || "1-on-1",
        payer: shift.payer || "DSPD",
        shift_notes: shift.shift_notes || shift.notes || "",
        group_name: shift.group_name || "",
        virtual_link: shift.virtual_link || "",
        isPotential: shift.isPotential || false,
        recurring: shift.recurring || false,
        recurrencePattern: shift.recurrencePattern || "weekly",
        selectedWeekdays: shift.selectedWeekdays || [],
        customDates: shift.customDates || [],
        recurring_start_date:
          shift.recurringStartDate || shift.recurring_start_date || "",
        recurring_end_date:
          shift.recurringEndDate || shift.recurring_end_date || null,
        parent_recurring_shift_id: shift.parent_recurring_shift_id || null,
        is_exception: shift.is_exception || false,
        timezone: shift.timezone || null,
      };

      // Populate timezone: use stored value or infer from client address
      if (this.shift.timezone) {
        this.inferredTimezone = this.shift.timezone;
      } else if (clientId && !Array.isArray(clientId) && this.fetchedClientAddresses.length > 0) {
        const addr = this.fetchedClientAddresses[0];
        if (addr && addr.id) {
          this.inferringTimezone = true;
          try {
            const tz = await this.inferTimezoneFromAddress(addr.id);
            this.inferredTimezone = tz;
            this.shift.timezone = tz;
          } finally {
            this.inferringTimezone = false;
          }
        }
      }

      // Remember source client for auto-population when creating new shifts from client context
      if (!shift.id && clientId && !Array.isArray(clientId)) {
        this.sourceClientId = clientId;
      } else {
        this.sourceClientId = null;
      }

      // Remember source caregiver for auto-population when creating new shifts from caregiver calendar
      if (!shift.id && caregiverId && !Array.isArray(caregiverId)) {
        this.sourceCaregiverId = caregiverId;
      } else {
        this.sourceCaregiverId = null;
      }

      // When editing an existing shift, always set to single mode
      this.isRecurringMode = false;

      // Set virtual shift toggle based on whether the shift has a virtual_link
      this.isVirtualShift = !!(shift.virtual_link);

      // Handle alternate location data if present
      // Treat it as an alternate location if:
      // 1. There's an alternateAddress object that is NOT one of the client's saved addresses OR
      // 2. There's an id_address that is NOT one of the client's saved addresses
      let isAlternateLocation = false;
      if (shift.alternateAddress) {
        // Check if this alternate address ID matches any of the client's saved addresses
        const isClientAddress = this.fetchedClientAddresses.some(
          (addr) => addr.id === shift.alternateAddress.id,
        );
        if (!isClientAddress) {
          // This is a true alternate location, not a saved client address
          isAlternateLocation = true;
        }
      } else if (shift.id_address) {
        // Check if this address ID belongs to the client's saved addresses
        const isClientAddress = this.fetchedClientAddresses.some(
          (addr) => addr.id === shift.id_address,
        );
        if (!isClientAddress) {
          // This is a one-time alternate location, not a saved client address
          isAlternateLocation = true;
        }
      }

      if (isAlternateLocation) {
        this.useAlternateLocation = true;
        this.vrAlternateLocation = {
          address1: shift.alternateAddress?.address1 || "",
          address2: shift.alternateAddress?.address2 || "",
          city: shift.alternateAddress?.city || "",
          state: shift.alternateAddress?.state || "",
          zip: shift.alternateAddress?.zip || "",
          geocode: shift.alternateAddress?.geocode || "",
        };
      } else {
        this.useAlternateLocation = false;
        this.vrAlternateLocation = {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
          geocode: "",
        };
      }

      // Handle selected service codes from the shift response
      // Check non_service_codes for Non Service payer, otherwise check service_codes
      if (
        shift.payer === "Non Service" &&
        shift.non_service_codes &&
        shift.non_service_codes.length > 0
      ) {
        // Map the non-service codes to just their IDs for the select component
        const selectedCodes = shift.non_service_codes.map((sc) => sc.id);
        this.$set(this.shift, "selectedServiceCodes", selectedCodes);
      } else if (shift.service_codes && shift.service_codes.length > 0) {
        // Map the service codes to just their IDs for the multiselect component
        const selectedCodes = shift.service_codes.map((sc) => sc.id);
        this.$set(this.shift, "selectedServiceCodes", selectedCodes);
      } else if (
        shift.selectedServiceCodes &&
        shift.selectedServiceCodes.length > 0
      ) {
        // Fallback to selectedServiceCodes if present
        this.$set(this.shift, "selectedServiceCodes", [
          ...shift.selectedServiceCodes,
        ]);
      } else if (shift.serviceCodeId) {
        // Handle legacy single serviceCodeId (convert to array for backward compatibility)
        this.$set(this.shift, "selectedServiceCodes", [shift.serviceCodeId]);
      } else {
        this.$set(this.shift, "selectedServiceCodes", []);
      }

      // Service codes were already loaded when modal became visible
      // (see visible watcher above)

      // Load available service codes based on payer and client
      await this.loadAvailableServiceCodes();

      // For DSPD Group edits, populate filtered clients by service code auth
      if (shift.payer === "DSPD" && (shift.shift_type === "Group" || this.shift.shift_type === "Group")) {
        const selectedCodes = this.shift.selectedServiceCodes;
        if (selectedCodes && selectedCodes.length > 0) {
          try {
            const response = await clientsService.list({
              payer_type: "DSPD",
              service_code_id: selectedCodes[0],
            });
            this.dspdGroupFilteredClients = (response || []).map((client) => ({
              id: Number(client.id) || client.id,
              name: `${client.first_name} ${client.last_name}`,
              addresses: client.addresses,
            }));
          } catch (error) {
            this.dspdGroupFilteredClients = [];
          }
        }
      }

      if (clientId) {
        // If the shift has an associated address ID, set the selected address index
        // (addresses were already fetched earlier in this method)
        if (shift.id_address || shift.clientAddressId) {
          const addressId = shift.id_address || shift.clientAddressId;
          // Find the index of the address with this ID
          const addressIndex = this.fetchedClientAddresses.findIndex(
            (addr) => addr.id === addressId,
          );
          if (addressIndex !== -1) {
            this.selectedAddressIndex = addressIndex;
          }
        }
      }

      // Available service codes were already loaded above by loadAvailableServiceCodes()
      // Don't call updateAvailableServiceCodes() here as it would overwrite client-specific codes

      // Handle separate clock-in/out addresses if present
      if (shift.use_separate_clock_addresses) {
        this.useSeparateClockAddresses = true;

        // Handle clock-in address
        if (shift.clock_in_use_client_address && shift.clock_in_address_id) {
          this.clockInUseAlternateAddress = false;
          this.clockInClientAddressId = shift.clock_in_address_id;
        } else if (shift.clock_in_address_id) {
          // This is an alternate address, populate it from the included data
          this.clockInUseAlternateAddress = true;
          if (shift.clockInAddress) {
            this.clockInLocation = {
              address1: shift.clockInAddress.address1 || "",
              address2: shift.clockInAddress.address2 || "",
              city: shift.clockInAddress.city || "",
              state: shift.clockInAddress.state || "",
              zip: shift.clockInAddress.zip || "",
              geocode: shift.clockInAddress.geocode || "",
            };
          }
        }

        // Handle clock-out address
        if (shift.clock_out_use_client_address && shift.clock_out_address_id) {
          this.clockOutUseAlternateAddress = false;
          this.clockOutClientAddressId = shift.clock_out_address_id;
        } else if (shift.clock_out_address_id) {
          // This is an alternate address, populate it from the included data
          this.clockOutUseAlternateAddress = true;
          if (shift.clockOutAddress) {
            this.clockOutLocation = {
              address1: shift.clockOutAddress.address1 || "",
              address2: shift.clockOutAddress.address2 || "",
              city: shift.clockOutAddress.city || "",
              state: shift.clockOutAddress.state || "",
              zip: shift.clockOutAddress.zip || "",
              geocode: shift.clockOutAddress.geocode || "",
            };
          }
        }
      } else {
        this.useSeparateClockAddresses = false;
      }

      // Populate clientTaskCaregivers for DSPD group shifts
      if (
        shift.clientDetails &&
        Array.isArray(shift.clientDetails) &&
        shift.clientDetails.length > 0
      ) {
        this.clientTaskCaregivers = {};
        shift.clientDetails.forEach((detail) => {
          const clientId = detail.id_clients;
          if (!this.clientTaskCaregivers[clientId]) {
            this.$set(this.clientTaskCaregivers, clientId, {
              pickUp: [],
              dropOff: [],
              shiftNotes: [],
              medications: [],
            });
          }
          // Add caregiver IDs to respective task arrays (if they exist)
          if (detail.pickup_caregiver_id) {
            this.clientTaskCaregivers[clientId].pickUp.push(
              detail.pickup_caregiver_id,
            );
          }
          if (detail.dropoff_caregiver_id) {
            this.clientTaskCaregivers[clientId].dropOff.push(
              detail.dropoff_caregiver_id,
            );
          }
          if (detail.shift_notes_caregiver_id) {
            this.clientTaskCaregivers[clientId].shiftNotes.push(
              detail.shift_notes_caregiver_id,
            );
          }
          if (detail.medications_caregiver_id) {
            this.clientTaskCaregivers[clientId].medications.push(
              detail.medications_caregiver_id,
            );
            this.$set(this.clientMedicationsEnabled, clientId, true);
          }
        });

        // Also populate groupClientData with address information from clientDetails
        // This is needed so that address updates are saved properly
        shift.clientDetails.forEach((detail) => {
          const clientId = detail.id_clients;

          // Find the client from the shift's clients array to get their addresses
          const client = shift.clients?.find((c) => c.id === clientId);
          if (client) {
            const clientAddresses =
              client.addresses && client.addresses.length > 0
                ? client.addresses
                : client.address
                  ? [client.address]
                  : [];

            // Find the currently selected address index based on id_address in clientDetails
            let selectedAddressIndex = 0;
            let isAlternateLocation = false;
            if (detail.id_address && clientAddresses.length > 0) {
              const foundIndex = clientAddresses.findIndex(
                (addr) => addr.id === detail.id_address,
              );
              if (foundIndex !== -1) {
                selectedAddressIndex = foundIndex;
              } else {
                // id_address doesn't match any client profile address - it's an alternate location
                isAlternateLocation = true;
              }
            } else if (detail.id_address && clientAddresses.length === 0) {
              // Client has no profile addresses but has an id_address - alternate location
              isAlternateLocation = true;
            }

            // Determine if this client has separate pickup/dropoff addresses
            const hasSeparateAddresses =
              detail.pickup_address_id != null || detail.dropoff_address_id != null;

            // Check if pickup/dropoff addresses are client profile addresses or alternate locations
            const pickupIsClientAddress = hasSeparateAddresses && detail.pickup_address_id
              ? clientAddresses.some((addr) => addr.id === detail.pickup_address_id)
              : true;
            const dropoffIsClientAddress = hasSeparateAddresses && detail.dropoff_address_id
              ? clientAddresses.some((addr) => addr.id === detail.dropoff_address_id)
              : true;

            // Build clock-in location from pickupAddress if it's an alternate (non-client-profile) address
            const clockInLocation = !pickupIsClientAddress && detail.pickupAddress
              ? {
                  address1: detail.pickupAddress.address1 || "",
                  address2: detail.pickupAddress.address2 || "",
                  city: detail.pickupAddress.city || "",
                  state: detail.pickupAddress.state || "",
                  zip: detail.pickupAddress.zip || "",
                  geocode: detail.pickupAddress.geocode || "",
                }
              : { address1: "", address2: "", city: "", state: "", zip: "", geocode: "" };

            // Build clock-out location from dropoffAddress if it's an alternate (non-client-profile) address
            const clockOutLocation = !dropoffIsClientAddress && detail.dropoffAddress
              ? {
                  address1: detail.dropoffAddress.address1 || "",
                  address2: detail.dropoffAddress.address2 || "",
                  city: detail.dropoffAddress.city || "",
                  state: detail.dropoffAddress.state || "",
                  zip: detail.dropoffAddress.zip || "",
                  geocode: detail.dropoffAddress.geocode || "",
                }
              : { address1: "", address2: "", city: "", state: "", zip: "", geocode: "" };

            // Build alternate location from address record if this is an alternate location
            const vrAlternateLocation = isAlternateLocation && detail.address
              ? {
                  address1: detail.address.address1 || "",
                  address2: detail.address.address2 || "",
                  city: detail.address.city || "",
                  state: detail.address.state || "",
                  zip: detail.address.zip || "",
                  geocode: detail.address.geocode || "",
                }
              : { address1: "", address2: "", city: "", state: "", zip: "", geocode: "" };

            const defaultAddressId = clientAddresses.length > 0 ? clientAddresses[0].id : null;

            this.$set(this.groupClientData, clientId, {
              fetchedClientAddresses: clientAddresses,
              selectedAddressIndex: selectedAddressIndex,
              useSeparateClockAddresses: hasSeparateAddresses,
              useAlternateLocation: isAlternateLocation && !hasSeparateAddresses,
              clockInClientAddressId: hasSeparateAddresses && detail.pickup_address_id
                ? detail.pickup_address_id
                : defaultAddressId,
              clockOutClientAddressId: hasSeparateAddresses && detail.dropoff_address_id
                ? detail.dropoff_address_id
                : defaultAddressId,
              clockInUseAlternateAddress: hasSeparateAddresses && !pickupIsClientAddress,
              clockOutUseAlternateAddress: hasSeparateAddresses && !dropoffIsClientAddress,
              clockInLocation: clockInLocation,
              clockOutLocation: clockOutLocation,
              vrAlternateLocation: vrAlternateLocation,
              clientGeocode: {
                geocode: "",
              },
            });
          }
        });
      }

      // Store the originally assigned caregiverId to preserve it
      const originalCaregiverId = caregiverId;

      // Clear the flag before fetching so the fetch can proceed
      this.isPopulatingShift = false;

      // Pre-fetch medications data for DSPD group shifts so it's ready
      // when the user navigates to the task assignment view
      if (this.isDSPDGroupShift && this.shift.clientId && Array.isArray(this.shift.clientId)) {
        this.fetchClientMedications();
      }

      // Fetch available caregivers for the populated shift data
      await this.fetchAvailableCaregivers();

      // If we're editing and have date/time, mark that we've checked availability
      if (!this.isCaregiverDropdownDisabled) {
        this.hasCheckedAvailability = true;
      }

      // Ensure the caregiver stays selected after fetching available caregivers
      // Use $nextTick to ensure the dropdown options are updated first
      if (originalCaregiverId) {
        this.$nextTick(() => {
          this.shift.caregiverId = originalCaregiverId;
        });
      }
    },
    resetShiftData() {
      this.isRecurringMode = false;
      this.showRecurringEditExplanation = false;
      this.isEditingRecurringSeries = false;
      this.showExceptionShiftsModal = false;
      this.exceptionShifts = [];
      this.selectedExceptionShifts = [];
      this.pendingSaveData = null;
      this.additionalCaregivers = [];
      this.shift = {
        date: "",
        scheduled_start: "",
        scheduled_end: "",
        clientId: null,
        caregiverId: null,
        shift_type: "1-on-1",
        payer: "VR",
        shift_notes: "",
        group_name: "",
        selectedServiceCodes: [],
        recurring: false,
        recurrencePattern: "weekly",
        selectedWeekdays: [],
        customDates: [],
        recurring_start_date: "",
        recurring_end_date: null,
        parent_recurring_shift_id: null,
        is_exception: false,
        timezone: null,
      };
      this.inferredTimezone = null;
      this.inferringTimezone = false;
      this.includeAdmins = false;
      this.showAllCaregivers = false;
      this.sourceClientId = null;
      this.sourceCaregiverId = null;
      // Reset VR alternate location data
      this.useAlternateLocation = false;
      this.vrAlternateLocation = {
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        geocode: "",
      };
      // Reset fetched client address
      this.fetchedClientAddress = null;
      this.fetchedClientAddresses = [];
      this.selectedAddressIndex = 0;
      // Reset DSPD group filtered clients
      this.dspdGroupFilteredClients = [];
      // Reset geocode field
      this.clientGeocode = {
        geocode: "",
      };
      // Reset separate clock-in/out address data
      this.useSeparateClockAddresses = false;
      this.clockInUseAlternateAddress = false;
      this.clockOutUseAlternateAddress = false;
      this.clockInClientAddressId = null;
      this.clockOutClientAddressId = null;
      this.clockInLocation = {
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        geocode: "",
      };
      this.clockOutLocation = {
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        geocode: "",
      };
      // Reset group shift data
      this.showAddressSelection = false;
      this.showCaregiverTaskAssignment = false;
      this.clientAddressSelections = {};
      this.clientTaskCaregivers = {};
      this.clientMedications = {};
      this.clientMedicationsEnabled = {};
      this.loadingClientMedications = false;
      this.groupClientData = {};
      // Reset available caregivers for shift
      this.availableCaregiversForShift = [];
      this.isLoadingAvailableCaregivers = false;
      this.hasCheckedAvailability = false;
      // Don't reset available service codes - they should stay loaded
    },
    onIncludeAdminsChange() {
      // Don't emit to parent - we handle everything internally with fetchAvailableCaregivers
      // Just clear selected caregiver when turning off admin inclusion if an admin was selected
      if (!this.includeAdmins && this.shift.caregiverId) {
        if (Array.isArray(this.shift.caregiverId)) {
          // For group shifts with multi-select, remove only admin caregivers
          const adminIds = this.availableCaregiversForShift
            .filter((c) => c.user_type === "Admin")
            .map((c) => c.id);
          const filtered = this.shift.caregiverId.filter(
            (id) => !adminIds.includes(id),
          );
          this.shift.caregiverId = filtered.length ? filtered : null;
        } else {
          const selectedCaregiver = this.availableCaregiversForShift.find(
            (c) => c.id === this.shift.caregiverId,
          );
          if (selectedCaregiver && selectedCaregiver.user_type === "Admin") {
            this.shift.caregiverId = null;
          }
        }
      }
      // The watcher on includeAdmins will automatically call fetchAvailableCaregivers
    },
    onShowAllCaregiversChange() {
      // Turning the override off must not leave an unavailable caregiver
      // silently selected — drop anyone who only appeared because of it.
      if (!this.showAllCaregivers && this.shift.caregiverId) {
        const unavailableIds = this.availableCaregiversForShift
          .filter((c) => c.unavailable)
          .map((c) => c.id);
        if (Array.isArray(this.shift.caregiverId)) {
          const kept = this.shift.caregiverId.filter(
            (id) => !unavailableIds.includes(id),
          );
          this.shift.caregiverId = kept.length ? kept : null;
        } else if (unavailableIds.includes(this.shift.caregiverId)) {
          this.shift.caregiverId = null;
        }
      }
      // The watcher on showAllCaregivers refetches the list
    },
    applyPlaceToAddressObj(addrObj, place) {
      this.$set(addrObj, "address1", place.address1);
      this.$set(addrObj, "city", place.city);
      this.$set(addrObj, "state", place.state);
      this.$set(addrObj, "zip", place.zip);
      this.$set(addrObj, "geocode", place.geocode);
    },
    async generateVRGeocodeFromAddress() {
      // Validate that we have enough address information
      if (
        !this.vrAlternateLocation.address1 ||
        !this.vrAlternateLocation.city ||
        !this.vrAlternateLocation.state
      ) {
        this.$notifyError(
          "Please fill in Address1, City, and State before generating geocode",
        );
        return;
      }

      try {
        // Build the address string for geocoding
        const addressParts = [
          this.vrAlternateLocation.address1,
          this.vrAlternateLocation.city,
          this.vrAlternateLocation.state,
          this.vrAlternateLocation.zip || "",
        ]
          .filter((part) => part)
          .join(", ");

        const encodedAddress = encodeURIComponent(addressParts);

        // Use Nominatim (OpenStreetMap) geocoding API
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1`,
          {
            headers: {
              "User-Agent": "OppoApp/1.0", // Nominatim requires a user agent
            },
          },
        );

        if (!response.ok) {
          throw new Error("Geocoding service unavailable");
        }

        const data = await response.json();

        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          this.$set(this.vrAlternateLocation, "geocode", `${lat},${lng}`);
          this.$notifySuccess("Geocode generated successfully!");
        } else {
          this.$notifyError(
            "Could not find coordinates for this address. Please verify the address is correct.",
          );
        }
      } catch (error) {
        this.$notifyError(
          "Failed to generate geocode. Please try again or enter manually.",
        );
      }
    },
    async generateClockInGeocodeFromAddress() {
      // Validate that we have enough address information
      if (
        !this.clockInLocation.address1 ||
        !this.clockInLocation.city ||
        !this.clockInLocation.state
      ) {
        this.$notifyError(
          "Please fill in Address1, City, and State before generating geocode",
        );
        return;
      }

      try {
        // Build the address string for geocoding
        const addressParts = [
          this.clockInLocation.address1,
          this.clockInLocation.city,
          this.clockInLocation.state,
          this.clockInLocation.zip || "",
        ]
          .filter((part) => part)
          .join(", ");

        const encodedAddress = encodeURIComponent(addressParts);

        // Use Nominatim (OpenStreetMap) geocoding API
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1`,
          {
            headers: {
              "User-Agent": "OppoApp/1.0", // Nominatim requires a user agent
            },
          },
        );

        if (!response.ok) {
          throw new Error("Geocoding service unavailable");
        }

        const data = await response.json();

        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          this.$set(this.clockInLocation, "geocode", `${lat},${lng}`);
          this.$notifySuccess("Clock-in geocode generated successfully!");
        } else {
          this.$notifyError(
            "Could not find coordinates for this address. Please verify the address is correct.",
          );
        }
      } catch (error) {
        this.$notifyError(
          "Failed to generate geocode. Please try again or enter manually.",
        );
      }
    },
    async generateClockOutGeocodeFromAddress() {
      // Validate that we have enough address information
      if (
        !this.clockOutLocation.address1 ||
        !this.clockOutLocation.city ||
        !this.clockOutLocation.state
      ) {
        this.$notifyError(
          "Please fill in Address1, City, and State before generating geocode",
        );
        return;
      }

      try {
        // Build the address string for geocoding
        const addressParts = [
          this.clockOutLocation.address1,
          this.clockOutLocation.city,
          this.clockOutLocation.state,
          this.clockOutLocation.zip || "",
        ]
          .filter((part) => part)
          .join(", ");

        const encodedAddress = encodeURIComponent(addressParts);

        // Use Nominatim (OpenStreetMap) geocoding API
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1`,
          {
            headers: {
              "User-Agent": "OppoApp/1.0", // Nominatim requires a user agent
            },
          },
        );

        if (!response.ok) {
          throw new Error("Geocoding service unavailable");
        }

        const data = await response.json();

        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          this.$set(this.clockOutLocation, "geocode", `${lat},${lng}`);
          this.$notifySuccess("Clock-out geocode generated successfully!");
        } else {
          this.$notifyError(
            "Could not find coordinates for this address. Please verify the address is correct.",
          );
        }
      } catch (error) {
        this.$notifyError(
          "Failed to generate geocode. Please try again or enter manually.",
        );
      }
    },
    async generateClientGeocodeFromAddress() {
      // Validate that we have address information
      if (!this.selectedClientAddress) {
        this.$notifyError("No client address available");
        return;
      }

      if (
        !this.selectedClientAddress.address1 ||
        !this.selectedClientAddress.city ||
        !this.selectedClientAddress.state
      ) {
        this.$notifyError(
          "Client address is incomplete. Cannot generate geocode.",
        );
        return;
      }

      try {
        // Build the address string for geocoding
        const addressParts = [
          this.selectedClientAddress.address1,
          this.selectedClientAddress.city,
          this.selectedClientAddress.state,
          this.selectedClientAddress.zip || "",
        ]
          .filter((part) => part)
          .join(", ");

        const encodedAddress = encodeURIComponent(addressParts);

        // Use Nominatim (OpenStreetMap) geocoding API
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodedAddress}&format=json&limit=1`,
          {
            headers: {
              "User-Agent": "OppoApp/1.0", // Nominatim requires a user agent
            },
          },
        );

        if (!response.ok) {
          throw new Error("Geocoding service unavailable");
        }

        const data = await response.json();

        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          this.clientGeocode.geocode = `${lat},${lng}`;
          this.$notifySuccess("Geocode generated successfully!");
        } else {
          this.$notifyError(
            "Could not find coordinates for this address. Please verify the address is correct.",
          );
        }
      } catch (error) {
        this.$notifyError(
          "Failed to generate geocode. Please try again or enter manually.",
        );
      }
    },
    openVRMapLink() {
      const addressString = this.formatVRAddress(this.vrAlternateLocation);
      const encodedAddress = encodeURIComponent(addressString);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    openGroupVRMapLink(clientId) {
      const clientData = this.getClientData(clientId);
      const addressString = this.formatVRAddress(clientData.vrAlternateLocation);
      const encodedAddress = encodeURIComponent(addressString);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    formatVRAddress(address) {
      if (!address) return "No address available";
      const parts = [];
      if (address.address1) parts.push(address.address1);
      if (address.address2) parts.push(address.address2);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zip) parts.push(address.zip);
      return parts.join(", ");
    },
    formatClientAddress(address) {
      if (!address) return "No address available";
      const parts = [];
      if (address.address1) parts.push(address.address1);
      if (address.address2) parts.push(address.address2);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zip) parts.push(address.zip);
      return parts.join(", ");
    },
    formatClockInAddress(address) {
      if (!address) return "No address available";
      const parts = [];
      if (address.address1) parts.push(address.address1);
      if (address.address2) parts.push(address.address2);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zip) parts.push(address.zip);
      return parts.join(", ");
    },
    formatClockOutAddress(address) {
      if (!address) return "No address available";
      const parts = [];
      if (address.address1) parts.push(address.address1);
      if (address.address2) parts.push(address.address2);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.zip) parts.push(address.zip);
      return parts.join(", ");
    },
    openClientMapLink() {
      if (!this.selectedClientAddress) return;
      const addressString = this.formatClientAddress(
        this.selectedClientAddress,
      );
      const encodedAddress = encodeURIComponent(addressString);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    openClientMapLinkForGroup(clientId) {
      const address = this.getClientSelectedAddress(clientId);
      if (!address) return;
      const addressString = this.formatClientAddress(address);
      const encodedAddress = encodeURIComponent(addressString);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    openClockInMapLink() {
      if (this.clockInUseClientAddress) {
        if (!this.selectedClientAddress) return;
        const addressString = this.formatClientAddress(
          this.selectedClientAddress,
        );
        const encodedAddress = encodeURIComponent(addressString);
        const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        window.open(mapUrl, "_blank");
      } else if (this.clockInLocation.address1) {
        const addressParts = [
          this.clockInLocation.address1,
          this.clockInLocation.city,
          this.clockInLocation.state,
          this.clockInLocation.zip || "",
        ]
          .filter((part) => part)
          .join(", ");
        const encodedAddress = encodeURIComponent(addressParts);
        const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        window.open(mapUrl, "_blank");
      }
    },
    openClockOutMapLink() {
      if (this.clockOutUseClientAddress) {
        if (!this.selectedClientAddress) return;
        const addressString = this.formatClientAddress(
          this.selectedClientAddress,
        );
        const encodedAddress = encodeURIComponent(addressString);
        const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        window.open(mapUrl, "_blank");
      } else if (this.clockOutLocation.address1) {
        const addressParts = [
          this.clockOutLocation.address1,
          this.clockOutLocation.city,
          this.clockOutLocation.state,
          this.clockOutLocation.zip || "",
        ]
          .filter((part) => part)
          .join(", ");
        const encodedAddress = encodeURIComponent(addressParts);
        const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        window.open(mapUrl, "_blank");
      }
    },
    openClockInSelectedMapLink() {
      // Get the selected address for clock in
      let address = null;
      if (this.clockInClientAddressId) {
        // Find the address from availableAddresses by ID
        address = this.availableAddresses.find(
          (addr) => addr.id === this.clockInClientAddressId,
        );
      } else {
        // Use the default selected client address
        address = this.selectedClientAddress;
      }

      if (!address) return;
      const addressString = this.formatClientAddress(address);
      const encodedAddress = encodeURIComponent(addressString);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    openClockOutSelectedMapLink() {
      // Get the selected address for clock out
      let address = null;
      if (this.clockOutClientAddressId) {
        // Find the address from availableAddresses by ID
        address = this.availableAddresses.find(
          (addr) => addr.id === this.clockOutClientAddressId,
        );
      } else {
        // Use the default selected client address
        address = this.selectedClientAddress;
      }

      if (!address) return;
      const addressString = this.formatClientAddress(address);
      const encodedAddress = encodeURIComponent(addressString);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    openClockInMapLinkForGroup(clientId) {
      const address = this.getClientSelectedAddress(clientId);
      if (!address) return;
      const addressString = this.formatClientAddress(address);
      const encodedAddress = encodeURIComponent(addressString);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    openClockOutMapLinkForGroup(clientId) {
      const address = this.getClientSelectedAddress(clientId);
      if (!address) return;
      const addressString = this.formatClientAddress(address);
      const encodedAddress = encodeURIComponent(addressString);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    openClockInAlternateMapLinkForGroup(clientId) {
      const clientData = this.getClientData(clientId);
      if (!clientData.clockInLocation.address1) return;
      const addressParts = [
        clientData.clockInLocation.address1,
        clientData.clockInLocation.address2,
        clientData.clockInLocation.city,
        clientData.clockInLocation.state,
        clientData.clockInLocation.zip || "",
      ]
        .filter((part) => part)
        .join(", ");
      const encodedAddress = encodeURIComponent(addressParts);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    openClockOutAlternateMapLinkForGroup(clientId) {
      const clientData = this.getClientData(clientId);
      if (!clientData.clockOutLocation.address1) return;
      const addressParts = [
        clientData.clockOutLocation.address1,
        clientData.clockOutLocation.address2,
        clientData.clockOutLocation.city,
        clientData.clockOutLocation.state,
        clientData.clockOutLocation.zip || "",
      ]
        .filter((part) => part)
        .join(", ");
      const encodedAddress = encodeURIComponent(addressParts);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank");
    },
    handleCancel() {
      // Reset address selection state
      this.showAddressSelection = false;
      this.showCaregiverTaskAssignment = false;
      this.clientAddressSelections = {};
      this.clientTaskCaregivers = {};
      this.groupClientData = {};
      this.adminChangeReason = "";
      this.$emit("cancel");
    },
    handleDelete() {
      this.$emit("delete", this.shiftBeingEdited);
    },
    handleSingleModeClick() {
      // Switch to single mode
      this.isRecurringMode = false;
      this.isEditingRecurringSeries = false;
    },
    handleRecurringModeClick() {
      // If we're editing a recurring instance, show the explanation modal
      if (this.isEditingRecurringInstance) {
        this.showRecurringEditExplanation = true;
      } else {
        // Creating a new shift, just toggle the mode
        this.isRecurringMode = true;
      }
    },
    cancelRecurringEdit() {
      // User canceled, stay in single mode
      this.showRecurringEditExplanation = false;
      this.isRecurringMode = false;
      this.isEditingRecurringSeries = false;
    },
    confirmRecurringEdit() {
      // User confirmed, switch to recurring mode
      this.showRecurringEditExplanation = false;
      this.isRecurringMode = true;
      this.isEditingRecurringSeries = true;

      // Populate recurring fields from the shift being edited if available
      if (this.shiftBeingEdited) {
        // Use the date field from shiftBeingEdited which represents the actual occurrence date (Mountain Time)
        // NOT scheduled_start which is UTC and can be a different date for evening shifts
        let dateString;
        if (this.shiftBeingEdited.date) {
          dateString =
            typeof this.shiftBeingEdited.date === "string"
              ? this.shiftBeingEdited.date.split("T")[0]
              : this.shiftBeingEdited.date;
        } else {
          // Fallback: extract date from scheduled_start in Mountain Time
          const shiftDate = this.shiftBeingEdited.scheduled_start
            ? new Date(this.shiftBeingEdited.scheduled_start)
            : new Date();

          // Convert to Mountain Time before extracting date
          const formatter = new Intl.DateTimeFormat("en-CA", {
            timeZone: "America/Denver",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
          dateString = formatter.format(shiftDate);
        }

        // Always set recurring start date to the instance's date
        // (the new series starts from the date of the instance we clicked on)
        this.shift.recurring_start_date = dateString;

        // Keep the original end date (already set from populateShiftData)
        // If not set, leave it blank — blank means indefinite (no end date)

        // selectedWeekdays should already be populated from the parent shift
        // Only set to current day if somehow empty
        if (
          !this.shift.selectedWeekdays ||
          this.shift.selectedWeekdays.length === 0
        ) {
          const instanceDate = new Date(dateString + "T00:00:00");
          const dayOfWeek = instanceDate.getDay();
          this.shift.selectedWeekdays = [dayOfWeek];
        }
      }
    },
    async handleSave() {
      // For group shifts, show address selection UI on first Continue click
      if (
        this.shift.shift_type === "Group" &&
        !this.showAddressSelection &&
        !this.showCaregiverTaskAssignment
      ) {
        // Validate that clients are selected before proceeding to address selection
        if (
          !this.shift.clientId ||
          (Array.isArray(this.shift.clientId) &&
            this.shift.clientId.length === 0)
        ) {
          this.$notifyError("Please select at least one client!");
          return;
        }

        if (
          this.shift.payer === "DSPD" &&
          (!Array.isArray(this.shift.clientId) ||
            this.shift.clientId.length < 2)
        ) {
          this.$notifyError(
            "DSPD shifts require at least 2 clients to be selected."
          );
          return;
        }

        if (
          !this.shift.caregiverId ||
          (Array.isArray(this.shift.caregiverId) &&
            this.shift.caregiverId.length === 0)
        ) {
          this.$notifyError("Please select at least one caregiver!");
          return;
        }

        // Validate DSPD shift length based on service code for group shifts
        if (this.shift.payer === "DSPD") {
          // Get the selected service codes to check which ones are being used
          const selectedCodes = this.availableServiceCodes.filter((sc) =>
            this.shift.selectedServiceCodes.includes(sc.id),
          );

          // Check if DSP or DSG is selected
          const hasDSP = selectedCodes.some((sc) => sc.code === "DSP");
          const hasDSG = selectedCodes.some((sc) => sc.code === "DSG");

          if (hasDSP || hasDSG) {
            // Validate that times are set
            if (!this.shift.scheduled_start || !this.shift.scheduled_end) {
              this.$notifyError("Please set start and end times!");
              return;
            }

            // Calculate shift duration in hours using time strings
            // scheduled_start and scheduled_end are in format "HH:MM"
            const [startHours, startMinutes] = this.shift.scheduled_start
              .split(":")
              .map(Number);
            const [endHours, endMinutes] = this.shift.scheduled_end
              .split(":")
              .map(Number);

            const startTotalMinutes = startHours * 60 + startMinutes;
            let endTotalMinutes = endHours * 60 + endMinutes;

            // Handle overnight shifts
            if (endTotalMinutes <= startTotalMinutes) {
              endTotalMinutes += 24 * 60;
            }

            const durationMinutes = endTotalMinutes - startTotalMinutes;
            const durationHours = durationMinutes / 60;

            // Validate based on service code
            if (hasDSP) {
              // DSP: at least 3 hours and at most 6 hours (inclusive)
              if (durationHours < 3) {
                this.$notifyError(
                  "DSP shifts must be at least 3 hours in length.",
                );
                return;
              }
              if (durationHours > 6) {
                this.$notifyError(
                  "DSP shifts must be at most 6 hours in length.",
                );
                return;
              }
            }

            if (hasDSG) {
              // DSG: at least 6 hours and at most 10 hours (inclusive)
              if (durationHours < 6) {
                this.$notifyError(
                  "DSG shifts must be at least 6 hours in length.",
                );
                return;
              }
              if (durationHours > 10) {
                this.$notifyError(
                  "DSG shifts must be at most 10 hours in length.",
                );
                return;
              }
            }
          }
        }

        // Initialize address data for each client
        if (Array.isArray(this.shift.clientId)) {
          // Fetch full client data for each selected client
          await this.fetchGroupClientData();
          // Infer timezone from first client with an address
          await this.inferTimezoneForGroupShift();
        }
        // Show address selection UI
        this.showAddressSelection = true;
        return;
      }

      // For group shifts in address selection mode, validate addresses are configured
      if (this.shift.shift_type === "Group" && this.showAddressSelection) {
        // Validate that all clients have proper address configuration
        if (Array.isArray(this.shift.clientId)) {
          for (const clientId of this.shift.clientId) {
            const clientData = this.getClientData(clientId);
            const selectedAddress = this.getClientSelectedAddress(clientId);
            const clientName = this.getClientNameById(clientId);

            // Check if using alternate location
            if (clientData.useAlternateLocation) {
              // Validate alternate location has required fields
              if (
                !clientData.vrAlternateLocation.address1 ||
                !clientData.vrAlternateLocation.city ||
                !clientData.vrAlternateLocation.state ||
                !clientData.vrAlternateLocation.geocode
              ) {
                this.$notifyError(
                  `Please complete alternate location for ${clientName}!`,
                );
                return;
              }
            } else if (clientData.useSeparateClockAddresses) {
              // Validate clock in/out addresses
              if (clientData.clockInUseAlternateAddress) {
                if (
                  !clientData.clockInLocation.address1 ||
                  !clientData.clockInLocation.city ||
                  !clientData.clockInLocation.state ||
                  !clientData.clockInLocation.geocode
                ) {
                  this.$notifyError(
                    `Please complete clock in location for ${clientName}!`,
                  );
                  return;
                }
              }
              if (clientData.clockOutUseAlternateAddress) {
                if (
                  !clientData.clockOutLocation.address1 ||
                  !clientData.clockOutLocation.city ||
                  !clientData.clockOutLocation.state ||
                  !clientData.clockOutLocation.geocode
                ) {
                  this.$notifyError(
                    `Please complete clock out location for ${clientName}!`,
                  );
                  return;
                }
              }
            } else {
              // Validate that client has a selected address
              if (!selectedAddress) {
                this.$notifyError(
                  `Please select an address for ${clientName}!`,
                );
                return;
              }
              // Validate geocode if address doesn't have one
              if (
                !this.hasClientAddressGeocode(clientId) &&
                (!clientData.clientGeocode ||
                  !clientData.clientGeocode.geocode ||
                  !clientData.clientGeocode.geocode.trim())
              ) {
                this.$notifyError(
                  `Please provide a geocode for ${clientName}'s address!`,
                );
                return;
              }
            }
          }
        }
        // Validation passed, for DSPD group shifts, proceed to caregiver task assignment
        if (this.isDSPDGroupShift) {
          this.showCaregiverTaskAssignment = true;
          this.showAddressSelection = false;
          await this.fetchClientMedications();

          // Auto-fill all task selects when there is exactly one caregiver
          if (Array.isArray(this.shift.caregiverId) && this.shift.caregiverId.length === 1) {
            const singleCaregiverId = this.shift.caregiverId[0];
            const clientIds = Array.isArray(this.shift.clientId)
              ? this.shift.clientId
              : [this.shift.clientId];
            for (const clientId of clientIds) {
              this.$set(this.clientTaskCaregivers, clientId, {
                pickUp: [singleCaregiverId],
                dropOff: [singleCaregiverId],
                shiftNotes: [singleCaregiverId],
                medications: [singleCaregiverId],
              });
            }
          }

          return;
        }
        // For non-DSPD group shifts, continue with normal save flow
      }

      // For DSPD group shifts in caregiver task assignment mode, validate medications then save
      if (this.showCaregiverTaskAssignment && this.isDSPDGroupShift) {
        // Validate: if a client has medications, a caregiver must be selected
        if (this.shift.clientId && Array.isArray(this.shift.clientId)) {
          for (const clientId of this.shift.clientId) {
            if (this.clientMedications[clientId] && this.clientMedications[clientId].length > 0) {
              const medsCaregivers = this.getClientTaskCaregivers(clientId, "medications");
              if (!medsCaregivers || medsCaregivers.length === 0) {
                const clientName = this.getClientNameById(clientId);
                this.$notifyError(
                  `Please select a caregiver for medications for ${clientName}.`,
                );
                return;
              }
            }
          }
        }
        // Skip other validations and jump straight to the save logic below
        // The validations were already done in the address selection step
      } else {
        // Validate based on shift mode
        if (this.shiftMode === "single") {
          if (!this.shift.date) {
            this.$notifyError("Date is required!");
            return;
          }
        } else {
          // Recurring shift validation
          if (!this.shift.recurring_start_date) {
            this.$notifyError("Start date is required for recurring shifts!");
            return;
          }
          const selectedDays = Array.isArray(this.shift.selectedWeekdays)
            ? this.shift.selectedWeekdays
            : [];
          if (selectedDays.length === 0) {
            this.$notifyError(
              "Please select at least one day for recurring shifts!",
            );
            return;
          }
          // Validate that end date is after start date (only when end date is provided)
          if (
            this.shift.recurring_end_date &&
            new Date(this.shift.recurring_end_date) <=
              new Date(this.shift.recurring_start_date)
          ) {
            this.$notifyError("End date must be after the start date!");
            return;
          }
        }

        if (!this.shift.scheduled_start) {
          this.$notifyError("Start time is required!");
          return;
        }
        if (!this.shift.scheduled_end) {
          this.$notifyError("End time is required!");
          return;
        }
        // Validate that start time is before end time
        if (this.shift.scheduled_start >= this.shift.scheduled_end) {
          this.$notifyError("End time must be after start time!");
          return;
        }

        if (this.isVirtualShift && (!this.shift.virtual_link || !this.shift.virtual_link.trim())) {
          this.$notifyError("Virtual Link is required for virtual shifts!");
          return;
        }

        // Skip single-shift specific validations when in caregiver task assignment mode
        // (those validations were already done in the address selection step)
        if (!this.showCaregiverTaskAssignment) {
          if (!this.shift.clientId && this.shift.payer !== "Non Service") {
            this.$notifyError("Client is required!");
            return;
          }
          if (!this.shift.timezone && this.shift.clientId && this.shift.payer !== "Non Service") {
            this.$notifyError("Timezone is required. Please select a timezone.");
            return;
          }
          // Validate that an address is selected for the client (only if not using alternate location)
          if (
            this.shift.clientId &&
            this.shift.payer !== "Non Service" &&
            !this.useAlternateLocation
          ) {
            if (!this.selectedClientAddress) {
              this.$notifyError("Client address is required!");
              return;
            }
          }
          // Check if geocode is required and provided
          if (this.useAlternateLocation) {
            // Validate required alternate location fields
            if (
              !this.vrAlternateLocation.address1 ||
              !this.vrAlternateLocation.address1.trim()
            ) {
              this.$notifyError(
                "Address is required for the alternate location!",
              );
              return;
            }
            if (
              !this.vrAlternateLocation.city ||
              !this.vrAlternateLocation.city.trim()
            ) {
              this.$notifyError("City is required for the alternate location!");
              return;
            }
            if (
              !this.vrAlternateLocation.state ||
              !this.vrAlternateLocation.state.trim()
            ) {
              this.$notifyError(
                "State is required for the alternate location!",
              );
              return;
            }
            if (
              !this.vrAlternateLocation.zip ||
              !this.vrAlternateLocation.zip.trim()
            ) {
              this.$notifyError(
                "Zip code is required for the alternate location!",
              );
              return;
            }
            // For VR shifts with alternate location, require alternate location geocode
            if (
              !this.vrAlternateLocation.geocode ||
              !this.vrAlternateLocation.geocode.trim()
            ) {
              this.$notifyError(
                "Geocode is required for the alternate location!",
              );
              return;
            }
            // Validate geocode format for VR alternate location
            const vrGeocodeValidation = validateGeocodeFormat(
              this.vrAlternateLocation.geocode,
            );
            if (!vrGeocodeValidation.valid) {
              this.$notifyError(
                `Invalid geocode format for alternate location! ${vrGeocodeValidation.error}`,
              );
              return;
            }
          } else if (!this.hasClientGeocode) {
            // For other shifts, require client geocode
            if (
              !this.clientGeocode.geocode ||
              !this.clientGeocode.geocode.trim()
            ) {
              this.$notifyError("Geocode is required for this client address!");
              return;
            }
            // Validate geocode format for client address
            const clientGeocodeValidation = validateGeocodeFormat(
              this.clientGeocode.geocode,
            );
            if (!clientGeocodeValidation.valid) {
              this.$notifyError(
                `Invalid geocode format for client address! ${clientGeocodeValidation.error}`,
              );
              return;
            }
          }

          // Validate separate clock addresses if enabled
          if (this.useSeparateClockAddresses) {
            // Validate clock-in address
            if (this.clockInUseAlternateAddress) {
              // Check if geocode is provided for alternate clock-in address
              if (
                !this.clockInLocation.geocode ||
                !this.clockInLocation.geocode.trim()
              ) {
                this.$notifyError(
                  "Geocode is required for the clock-in alternate address! Please use the 'Generate Geocode' button.",
                );
                return;
              }
              // Validate geocode format for clock-in address
              const clockInGeocodeValidation = validateGeocodeFormat(
                this.clockInLocation.geocode,
              );
              if (!clockInGeocodeValidation.valid) {
                this.$notifyError(
                  `Invalid geocode format for clock-in address! ${clockInGeocodeValidation.error}`,
                );
                return;
              }
            }

            // Validate clock-out address
            if (this.clockOutUseAlternateAddress) {
              // Check if geocode is provided for alternate clock-out address
              if (
                !this.clockOutLocation.geocode ||
                !this.clockOutLocation.geocode.trim()
              ) {
                this.$notifyError(
                  "Geocode is required for the clock-out alternate address! Please use the 'Generate Geocode' button.",
                );
                return;
              }
              // Validate geocode format for clock-out address
              const clockOutGeocodeValidation = validateGeocodeFormat(
                this.clockOutLocation.geocode,
              );
              if (!clockOutGeocodeValidation.valid) {
                this.$notifyError(
                  `Invalid geocode format for clock-out address! ${clockOutGeocodeValidation.error}`,
                );
                return;
              }
            }
          }
        } // End of single-shift specific validations

        // Validate DSPD shift duration based on service code (for non-group shifts)
        if (this.shift.payer === "DSPD" && this.shift.shift_type !== "Group" && this.shift.scheduled_start && this.shift.scheduled_end) {
          const selectedCodes = this.availableServiceCodes.filter((sc) =>
            this.shift.selectedServiceCodes && this.shift.selectedServiceCodes.includes(sc.id),
          );
          const hasDSP = selectedCodes.some((sc) => sc.code === "DSP");
          const hasDSG = selectedCodes.some((sc) => sc.code === "DSG");

          if (hasDSP || hasDSG) {
            const [startH, startM] = this.shift.scheduled_start.split(":").map(Number);
            const [endH, endM] = this.shift.scheduled_end.split(":").map(Number);
            let startTotal = startH * 60 + startM;
            let endTotal = endH * 60 + endM;
            if (endTotal <= startTotal) endTotal += 24 * 60;
            const durationHours = (endTotal - startTotal) / 60;

            if (hasDSP) {
              if (durationHours < 3) {
                this.$notifyError("DSP shifts must be at least 3 hours in length.");
                return;
              }
              if (durationHours > 6) {
                this.$notifyError("DSP shifts must be at most 6 hours in length.");
                return;
              }
            }
            if (hasDSG) {
              if (durationHours < 6) {
                this.$notifyError("DSG shifts must be at least 6 hours in length.");
                return;
              }
              if (durationHours > 10) {
                this.$notifyError("DSG shifts must be at most 10 hours in length.");
                return;
              }
            }
          }
        }

        // Validate service codes are selected (for all payers including Non Service)
        if (
          !this.shift.selectedServiceCodes ||
          this.shift.selectedServiceCodes.length === 0
        ) {
          const message =
            this.shift.payer === "Non Service"
              ? "Service code is required!"
              : "At least one service code must be selected!";
          this.$notifyError(message);
          return;
        }
      } // End of validation block (skipped for DSPD task assignment mode)

      // Check auth remaining before saving
      if (this.authWarning && !this.authAcknowledged) {
        this.$notifyError("Please acknowledge the authorization hours/units warning before proceeding.");
        return;
      }

      // Require a reason for admin edits once the shift is a time record
      // (mirrors the API rule in utils/editReason.js)
      const isEditing = !!(this.shiftBeingEdited && this.shiftBeingEdited.id);
      if (isEditing && this.isAdminUser && this.requiresEditReason) {
        if (!this.adminChangeReason || !this.adminChangeReason.trim()) {
          this.$notifyError("Please provide a reason for this edit");
          return;
        }
      }

      const shiftData = { ...this.shift };

      // Include admin change reason for edits
      if (isEditing && this.adminChangeReason && this.adminChangeReason.trim()) {
        shiftData.adminChangeReason = this.adminChangeReason.trim();
      }

      // Clear virtual_link if not a virtual shift
      if (!this.isVirtualShift) {
        shiftData.virtual_link = null;
      }

      // Set recurring flag based on switch value
      shiftData.recurring = this.isRecurringMode;

      // Convert time strings to full ISO datetime strings for non-recurring shifts
      if (
        !this.isRecurringMode &&
        shiftData.date &&
        shiftData.scheduled_start &&
        shiftData.scheduled_end
      ) {
        // Check if times are in HH:MM format (not already ISO format)
        if (!shiftData.scheduled_start.includes("T")) {
          // Combine date and time, then convert to UTC ISO string
          const startDateTime = new Date(
            `${shiftData.date}T${shiftData.scheduled_start}:00`,
          );
          const endDateTime = new Date(
            `${shiftData.date}T${shiftData.scheduled_end}:00`,
          );

          shiftData.scheduled_start = startDateTime.toISOString();
          shiftData.scheduled_end = endDateTime.toISOString();
        }
      }

      // Include parent_recurring_shift_id if editing an occurrence
      if (this.shift.parent_recurring_shift_id) {
        shiftData.parent_recurring_shift_id =
          this.shift.parent_recurring_shift_id;
        shiftData.is_exception = this.shift.is_exception;
      }

      // If editing a recurring series from this date forward
      if (this.isEditingRecurringSeries && this.shiftBeingEdited) {
        // Use the date field which represents the actual occurrence date the user clicked on
        const dateString = this.shiftBeingEdited.date
          ? typeof this.shiftBeingEdited.date === "string"
            ? this.shiftBeingEdited.date.split("T")[0]
            : this.shiftBeingEdited.date
          : null;

        if (dateString) {
          shiftData.editEntireSeriesFromDate = dateString;
        }
      }

      // Add geocode information if provided for client address without geocode
      if (
        this.clientGeocode.geocode &&
        this.clientGeocode.geocode.trim() &&
        !this.useAlternateLocation
      ) {
        shiftData.clientGeocode = this.clientGeocode.geocode.trim();
        // Include the address ID with the geocode so backend knows which address to update
        if (this.selectedClientAddress && this.selectedClientAddress.id) {
          shiftData.clientGeocodeAddressId = this.selectedClientAddress.id;
        }
      }

      // Add alternate location information
      if (this.useAlternateLocation) {
        shiftData.vrAlternateLocation = {
          address1: this.vrAlternateLocation.address1 || "",
          address2: this.vrAlternateLocation.address2 || "",
          city: this.vrAlternateLocation.city || "",
          state: this.vrAlternateLocation.state || "",
          zip: this.vrAlternateLocation.zip || "",
          geocode: this.vrAlternateLocation.geocode || "",
          formatted: this.formatVRAddress(this.vrAlternateLocation),
        };
        shiftData.useAlternateLocation = true;
      }

      // Add separate clock-in/out address information
      shiftData.use_separate_clock_addresses = this.useSeparateClockAddresses;

      if (this.useSeparateClockAddresses) {
        // Clock-in address
        if (!this.clockInUseAlternateAddress) {
          // Using client address
          if (this.clockInClientAddressId) {
            shiftData.clockInClientAddressId = this.clockInClientAddressId;
          } else if (
            this.selectedClientAddress &&
            this.selectedClientAddress.id
          ) {
            // Use the selected client address if no specific clock-in address is selected
            shiftData.clockInClientAddressId = this.selectedClientAddress.id;
          }
        } else {
          // Using alternate clock-in location
          shiftData.clockInLocation = {
            address1: this.clockInLocation.address1 || "",
            address2: this.clockInLocation.address2 || "",
            city: this.clockInLocation.city || "",
            state: this.clockInLocation.state || "",
            zip: this.clockInLocation.zip || "",
            geocode: this.clockInLocation.geocode || "",
          };
        }

        // Clock-out address
        if (!this.clockOutUseAlternateAddress) {
          // Using client address
          if (this.clockOutClientAddressId) {
            shiftData.clockOutClientAddressId = this.clockOutClientAddressId;
          } else if (
            this.selectedClientAddress &&
            this.selectedClientAddress.id
          ) {
            // Use the selected client address if no specific clock-out address is selected
            shiftData.clockOutClientAddressId = this.selectedClientAddress.id;
          }
        } else {
          // Using alternate clock-out location
          shiftData.clockOutLocation = {
            address1: this.clockOutLocation.address1 || "",
            address2: this.clockOutLocation.address2 || "",
            city: this.clockOutLocation.city || "",
            state: this.clockOutLocation.state || "",
            zip: this.clockOutLocation.zip || "",
            geocode: this.clockOutLocation.geocode || "",
          };
        }
      } else {
        // Clear out clock address fields when unchecked
        shiftData.clock_in_address_id = null;
        shiftData.clock_out_address_id = null;
        shiftData.clock_in_use_client_address = false;
        shiftData.clock_out_use_client_address = false;
      }

      // Convert clientId and caregiverId to arrays for backend compatibility
      if (shiftData.clientId) {
        // Check if it's already an array (for DSPD group shifts with multi-select)
        if (Array.isArray(shiftData.clientId)) {
          shiftData.clientIds = shiftData.clientId;
        } else {
          shiftData.clientIds = [shiftData.clientId];
        }
        delete shiftData.clientId;
      }
      if (shiftData.caregiverId) {
        // Check if it's already an array (for DSPD group shifts with multi-select)
        if (Array.isArray(shiftData.caregiverId)) {
          shiftData.caregiverIds = shiftData.caregiverId;
        } else {
          shiftData.caregiverIds = [shiftData.caregiverId];
        }
        delete shiftData.caregiverId;
      }

      // Additional caregivers in roles (shadow/training) on non-group
      // shifts: append their ids and send the role map. Roles are
      // per-occurrence only — never on a new recurring series and never
      // when editing the entire series.
      const extras = (this.additionalCaregivers || []).filter(
        (e) => e.id && e.role,
      );
      const isRecurringCreate =
        !!shiftData.recurring && !this.shiftBeingEdited?.id;
      if (
        this.shift.shift_type !== "Group" &&
        extras.length > 0 &&
        !isRecurringCreate &&
        !this.isEditingRecurringSeries
      ) {
        const baseIds = shiftData.caregiverIds || [];
        const extraIds = extras
          .map((e) => e.id)
          .filter((id) => !baseIds.includes(id));
        shiftData.caregiverIds = [...baseIds, ...extraIds];
        shiftData.caregiverRoles = Object.fromEntries(
          extras.map((e) => [e.id, e.role]),
        );
      }

      // Include the selected address ID if available (for all shifts, not just when geocode is provided)
      // BUT: Don't send clientAddressId when using alternate location - the alternate address should be used instead
      if (
        this.selectedClientAddress &&
        this.selectedClientAddress.id &&
        !this.useAlternateLocation
      ) {
        shiftData.clientAddressId = this.selectedClientAddress.id;
      }

      // Include selected service codes in the payload
      // Backend handles Non Service vs regular codes based on payer field
      if (
        shiftData.selectedServiceCodes &&
        shiftData.selectedServiceCodes.length > 0
      ) {
        shiftData.serviceCodeIds = [...shiftData.selectedServiceCodes];
      } else {
        shiftData.serviceCodeIds = [];
        shiftData.selectedServiceCodes = [];
      }
      delete shiftData.selectedServiceCodes;

      // Include recurring shift data if applicable
      if (shiftData.recurring) {
        // Keep the recurrence fields in the payload
        shiftData.recurring = true;
        shiftData.recurrencePattern = "weekly"; // Always weekly for the new simplified system
        shiftData.selectedWeekdays = [...this.shift.selectedWeekdays];
        shiftData.customDates = []; // Not used in new system
        shiftData.recurring_start_date = this.shift.recurring_start_date;
        // Clearing the date input yields "", which means open-ended.
        shiftData.recurring_end_date = this.shift.recurring_end_date || null;
        // Ensure scheduled times are preserved for recurring shifts
        shiftData.scheduled_start = this.shift.scheduled_start;
        shiftData.scheduled_end = this.shift.scheduled_end;
      }

      // Check for exception shifts before saving if editing a recurring shift
      // isEditing already declared above

      // For DSPD group shifts (DSG/DSP), create ONE shift with client details
      if (
        this.shift.shift_type === "Group" &&
        this.isDSPDGroupShift &&
        !isEditing &&
        Array.isArray(shiftData.clientIds) &&
        shiftData.clientIds.length > 0
      ) {
        // Prepare client addresses mapping
        const clientAddresses = {};
        const clientGeocodes = {};
        shiftData.clientIds.forEach((clientId) => {
          const selectedAddress = this.getClientSelectedAddress(clientId);
          if (selectedAddress && selectedAddress.id) {
            clientAddresses[clientId] = selectedAddress.id;

            // Include geocode if provided for address without geocode
            const clientData = this.getClientData(clientId);
            if (
              clientData.clientGeocode &&
              clientData.clientGeocode.geocode &&
              clientData.clientGeocode.geocode.trim()
            ) {
              clientGeocodes[clientId] = {
                geocode: clientData.clientGeocode.geocode.trim(),
                addressId: selectedAddress.id,
              };
            }
          }
        });

        // Collect per-client pickup/dropoff address data
        const clientPickupDropoffAddresses = {};
        shiftData.clientIds.forEach((clientId) => {
          const clientData = this.getClientData(clientId);
          if (clientData.useSeparateClockAddresses) {
            const entry = {};

            // Clock in (pickup): profile address or alternate location
            if (clientData.clockInUseAlternateAddress) {
              entry.pickupLocation = clientData.clockInLocation || {};
              entry.pickupUseClientAddress = false;
            } else {
              entry.pickupAddressId = clientData.clockInClientAddressId || null;
              entry.pickupUseClientAddress = true;
            }

            // Clock out (dropoff): profile address or alternate location
            if (clientData.clockOutUseAlternateAddress) {
              entry.dropoffLocation = clientData.clockOutLocation || {};
              entry.dropoffUseClientAddress = false;
            } else {
              entry.dropoffAddressId =
                clientData.clockOutClientAddressId || null;
              entry.dropoffUseClientAddress = true;
            }

            clientPickupDropoffAddresses[clientId] = entry;
          }
        });

        // Collect per-client alternate location data (single alternate address for both pickup/dropoff)
        const clientAlternateLocations = {};
        shiftData.clientIds.forEach((clientId) => {
          const clientData = this.getClientData(clientId);
          if (clientData.useAlternateLocation && !clientData.useSeparateClockAddresses) {
            clientAlternateLocations[clientId] = {
              address1: clientData.vrAlternateLocation.address1 || "",
              address2: clientData.vrAlternateLocation.address2 || "",
              city: clientData.vrAlternateLocation.city || "",
              state: clientData.vrAlternateLocation.state || "",
              zip: clientData.vrAlternateLocation.zip || "",
              geocode: clientData.vrAlternateLocation.geocode || "",
            };
            // Don't include profile address for clients using alternate location
            delete clientAddresses[clientId];
          }
        });

        // Include client task caregivers, addresses, and geocodes in the shift data
        shiftData.clientTaskCaregivers = this.clientTaskCaregivers;
        shiftData.clientAddresses = clientAddresses;
        if (Object.keys(clientGeocodes).length > 0) {
          shiftData.clientGeocodes = clientGeocodes;
        }
        if (Object.keys(clientPickupDropoffAddresses).length > 0) {
          shiftData.clientPickupDropoffAddresses = clientPickupDropoffAddresses;
        }
        if (Object.keys(clientAlternateLocations).length > 0) {
          shiftData.clientAlternateLocations = clientAlternateLocations;
        }

        // Emit single shift with client details
        // The parent will close the modal on success, or show an error and keep it open
        this.$emit("save", {
          shift: shiftData,
          isEditing: false,
          isDSPDGroupShift: true,
        });

        return; // Important: return here to prevent further execution
      } else if (
        this.shift.shift_type === "Group" &&
        !isEditing &&
        Array.isArray(shiftData.clientIds) &&
        shiftData.clientIds.length > 1
      ) {
        // For non-DSPD group shifts, create separate shifts for each client (old behavior)
        const shiftsToCreate = shiftData.clientIds.map((clientId) => {
          const individualShiftData = { ...shiftData };
          // Set single client
          individualShiftData.clientIds = [clientId];

          // Get the client-specific address data
          const clientData = this.getClientData(clientId);
          const selectedAddress = this.getClientSelectedAddress(clientId);

          // Set client-specific address
          if (selectedAddress && selectedAddress.id) {
            individualShiftData.clientAddressId = selectedAddress.id;
          }

          // Add geocode information if provided for client address without geocode
          if (
            clientData.clientGeocode &&
            clientData.clientGeocode.geocode &&
            clientData.clientGeocode.geocode.trim() &&
            !clientData.useAlternateLocation
          ) {
            individualShiftData.clientGeocode =
              clientData.clientGeocode.geocode.trim();
            // Include the address ID with the geocode so backend knows which address to update
            if (selectedAddress && selectedAddress.id) {
              individualShiftData.clientGeocodeAddressId = selectedAddress.id;
            }
          }

          // Add alternate location information
          if (clientData.useAlternateLocation) {
            individualShiftData.vrAlternateLocation = {
              address1: clientData.vrAlternateLocation.address1 || "",
              address2: clientData.vrAlternateLocation.address2 || "",
              city: clientData.vrAlternateLocation.city || "",
              state: clientData.vrAlternateLocation.state || "",
              zip: clientData.vrAlternateLocation.zip || "",
              geocode: clientData.vrAlternateLocation.geocode || "",
              formatted: this.formatVRAddress(clientData.vrAlternateLocation),
            };
            individualShiftData.useAlternateLocation = true;
          }

          // Add separate clock-in/out address information
          individualShiftData.use_separate_clock_addresses =
            clientData.useSeparateClockAddresses;

          if (clientData.useSeparateClockAddresses) {
            // Clock-in address
            if (!clientData.clockInUseAlternateAddress) {
              // Using client address
              if (clientData.clockInClientAddressId) {
                individualShiftData.clockInClientAddressId =
                  clientData.clockInClientAddressId;
              } else if (selectedAddress && selectedAddress.id) {
                // Use the selected client address if no specific clock-in address is selected
                individualShiftData.clockInClientAddressId = selectedAddress.id;
              }
            } else {
              // Using alternate clock-in location
              individualShiftData.clockInLocation = {
                address1: clientData.clockInLocation.address1 || "",
                address2: clientData.clockInLocation.address2 || "",
                city: clientData.clockInLocation.city || "",
                state: clientData.clockInLocation.state || "",
                zip: clientData.clockInLocation.zip || "",
                geocode: clientData.clockInLocation.geocode || "",
              };
            }

            // Clock-out address
            if (!clientData.clockOutUseAlternateAddress) {
              // Using client address
              if (clientData.clockOutClientAddressId) {
                individualShiftData.clockOutClientAddressId =
                  clientData.clockOutClientAddressId;
              } else if (selectedAddress && selectedAddress.id) {
                // Use the selected client address if no specific clock-out address is selected
                individualShiftData.clockOutClientAddressId =
                  selectedAddress.id;
              }
            } else {
              // Using alternate clock-out location
              individualShiftData.clockOutLocation = {
                address1: clientData.clockOutLocation.address1 || "",
                address2: clientData.clockOutLocation.address2 || "",
                city: clientData.clockOutLocation.city || "",
                state: clientData.clockOutLocation.state || "",
                zip: clientData.clockOutLocation.zip || "",
                geocode: clientData.clockOutLocation.geocode || "",
              };
            }
          }

          return individualShiftData;
        });

        // Emit multiple shifts to be created
        this.$emit("save", {
          shifts: shiftsToCreate,
          isEditing: false,
          isGroupShift: true,
        });
      } else if (
        isEditing &&
        shiftData.recurring &&
        shiftData.recurring_start_date
      ) {
        // For DSPD group shifts being edited, include client task caregivers and addresses
        if (
          this.shift.shift_type === "Group" &&
          this.isDSPDGroupShift &&
          Array.isArray(shiftData.clientIds) &&
          shiftData.clientIds.length > 0
        ) {
          // Prepare client addresses mapping
          const clientAddresses = {};
          const clientGeocodes = {};
          shiftData.clientIds.forEach((clientId) => {
            const selectedAddress = this.getClientSelectedAddress(clientId);
            if (selectedAddress && selectedAddress.id) {
              clientAddresses[clientId] = selectedAddress.id;

              // Include geocode if provided for address without geocode
              const clientData = this.getClientData(clientId);
              if (
                clientData.clientGeocode &&
                clientData.clientGeocode.geocode &&
                clientData.clientGeocode.geocode.trim()
              ) {
                clientGeocodes[clientId] = {
                  geocode: clientData.clientGeocode.geocode.trim(),
                  addressId: selectedAddress.id,
                };
              }
            }
          });

          // Collect per-client pickup/dropoff address data
          const clientPickupDropoffAddresses = {};
          shiftData.clientIds.forEach((clientId) => {
            const clientData = this.getClientData(clientId);
            if (clientData.useSeparateClockAddresses) {
              const entry = {};
              if (clientData.clockInUseAlternateAddress) {
                entry.pickupLocation = clientData.clockInLocation || {};
                entry.pickupUseClientAddress = false;
              } else {
                entry.pickupAddressId =
                  clientData.clockInClientAddressId || null;
                entry.pickupUseClientAddress = true;
              }
              if (clientData.clockOutUseAlternateAddress) {
                entry.dropoffLocation = clientData.clockOutLocation || {};
                entry.dropoffUseClientAddress = false;
              } else {
                entry.dropoffAddressId =
                  clientData.clockOutClientAddressId || null;
                entry.dropoffUseClientAddress = true;
              }
              clientPickupDropoffAddresses[clientId] = entry;
            }
          });

          // Collect per-client alternate location data (single alternate address for both pickup/dropoff)
          const clientAlternateLocations = {};
          shiftData.clientIds.forEach((clientId) => {
            const clientData = this.getClientData(clientId);
            if (clientData.useAlternateLocation && !clientData.useSeparateClockAddresses) {
              clientAlternateLocations[clientId] = {
                address1: clientData.vrAlternateLocation.address1 || "",
                address2: clientData.vrAlternateLocation.address2 || "",
                city: clientData.vrAlternateLocation.city || "",
                state: clientData.vrAlternateLocation.state || "",
                zip: clientData.vrAlternateLocation.zip || "",
                geocode: clientData.vrAlternateLocation.geocode || "",
              };
              delete clientAddresses[clientId];
            }
          });

          // Include client task caregivers, addresses, and geocodes in the shift data
          shiftData.clientTaskCaregivers = this.clientTaskCaregivers;
          shiftData.clientAddresses = clientAddresses;
          if (Object.keys(clientGeocodes).length > 0) {
            shiftData.clientGeocodes = clientGeocodes;
          }
          if (Object.keys(clientPickupDropoffAddresses).length > 0) {
            shiftData.clientPickupDropoffAddresses =
              clientPickupDropoffAddresses;
          }
          if (Object.keys(clientAlternateLocations).length > 0) {
            shiftData.clientAlternateLocations = clientAlternateLocations;
          }
        }

        // Store the shift data and check for exception shifts
        this.pendingSaveData = {
          shift: shiftData,
          isEditing: isEditing,
        };
        this.checkForExceptionShifts(shiftData);
      } else {
        // For DSPD group shifts being edited, include client task caregivers and addresses
        if (
          isEditing &&
          this.shift.shift_type === "Group" &&
          this.isDSPDGroupShift &&
          Array.isArray(shiftData.clientIds) &&
          shiftData.clientIds.length > 0
        ) {
          // Prepare client addresses mapping
          const clientAddresses = {};
          const clientGeocodes = {};
          shiftData.clientIds.forEach((clientId) => {
            const selectedAddress = this.getClientSelectedAddress(clientId);
            if (selectedAddress && selectedAddress.id) {
              clientAddresses[clientId] = selectedAddress.id;

              // Include geocode if provided for address without geocode
              const clientData = this.getClientData(clientId);
              if (
                clientData.clientGeocode &&
                clientData.clientGeocode.geocode &&
                clientData.clientGeocode.geocode.trim()
              ) {
                clientGeocodes[clientId] = {
                  geocode: clientData.clientGeocode.geocode.trim(),
                  addressId: selectedAddress.id,
                };
              }
            }
          });

          // Collect per-client pickup/dropoff address data
          const clientPickupDropoffAddresses = {};
          shiftData.clientIds.forEach((clientId) => {
            const clientData = this.getClientData(clientId);
            if (clientData.useSeparateClockAddresses) {
              const entry = {};
              if (clientData.clockInUseAlternateAddress) {
                entry.pickupLocation = clientData.clockInLocation || {};
                entry.pickupUseClientAddress = false;
              } else {
                entry.pickupAddressId =
                  clientData.clockInClientAddressId || null;
                entry.pickupUseClientAddress = true;
              }
              if (clientData.clockOutUseAlternateAddress) {
                entry.dropoffLocation = clientData.clockOutLocation || {};
                entry.dropoffUseClientAddress = false;
              } else {
                entry.dropoffAddressId =
                  clientData.clockOutClientAddressId || null;
                entry.dropoffUseClientAddress = true;
              }
              clientPickupDropoffAddresses[clientId] = entry;
            }
          });

          // Collect per-client alternate location data (single alternate address for both pickup/dropoff)
          const clientAlternateLocations = {};
          shiftData.clientIds.forEach((clientId) => {
            const clientData = this.getClientData(clientId);
            if (clientData.useAlternateLocation && !clientData.useSeparateClockAddresses) {
              clientAlternateLocations[clientId] = {
                address1: clientData.vrAlternateLocation.address1 || "",
                address2: clientData.vrAlternateLocation.address2 || "",
                city: clientData.vrAlternateLocation.city || "",
                state: clientData.vrAlternateLocation.state || "",
                zip: clientData.vrAlternateLocation.zip || "",
                geocode: clientData.vrAlternateLocation.geocode || "",
              };
              delete clientAddresses[clientId];
            }
          });

          // Include client task caregivers, addresses, and geocodes in the shift data
          shiftData.clientTaskCaregivers = this.clientTaskCaregivers;
          shiftData.clientAddresses = clientAddresses;
          if (Object.keys(clientGeocodes).length > 0) {
            shiftData.clientGeocodes = clientGeocodes;
          }
          if (Object.keys(clientPickupDropoffAddresses).length > 0) {
            shiftData.clientPickupDropoffAddresses =
              clientPickupDropoffAddresses;
          }
          if (Object.keys(clientAlternateLocations).length > 0) {
            shiftData.clientAlternateLocations = clientAlternateLocations;
          }
        }

        // No need to check for exception shifts, proceed with save
        this.$emit("save", {
          shift: shiftData,
          isEditing: isEditing,
        });
      }
    },

    async checkForExceptionShifts(shiftData) {
      try {
        // Determine the recurring shift ID to check
        let recurringShiftId = this.shiftBeingEdited?.id;

        // If editing an instance of a recurring shift, get the parent ID
        if (this.shiftBeingEdited?.parent_recurring_shift_id) {
          recurringShiftId = this.shiftBeingEdited.parent_recurring_shift_id;
        } else if (this.shiftBeingEdited?.original_recurring_shift_id) {
          recurringShiftId = this.shiftBeingEdited.original_recurring_shift_id;
        }

        if (!recurringShiftId) {
          // No recurring shift to check, proceed with save
          this.proceedWithSave();
          return;
        }

        // Fetch exception shifts in the date range
        const response = await shiftsService.getExceptionShiftsInDateRange(
          recurringShiftId,
          shiftData.recurring_start_date,
          shiftData.recurring_end_date,
        );

        if (response.count > 0) {
          // Found exception shifts, show modal
          this.exceptionShifts = response.exceptionShifts;
          this.showExceptionShiftsModal = true;

          // Select all exceptions by default (safer default - keeps everything)
          this.$nextTick(() => {
            if (this.$refs.exceptionShiftsTable) {
              this.exceptionShifts.forEach((row) => {
                this.$refs.exceptionShiftsTable.toggleRowSelection(row, true);
              });
            }
          });
        } else {
          // No exception shifts, proceed with save
          this.proceedWithSave();
        }
      } catch (error) {
        // On error, proceed with save anyway
        this.proceedWithSave();
      }
    },

    proceedWithSave() {
      if (this.pendingSaveData) {
        this.$emit("save", this.pendingSaveData);
        this.pendingSaveData = null;
      }
    },

    handleExceptionSelectionChange(selection) {
      this.selectedExceptionShifts = selection;
    },

    async handleExceptionShiftsSelection() {
      try {
        // Get IDs of selected (to keep) and unselected (to delete)
        const selectedIds = this.selectedExceptionShifts.map(
          (shift) => shift.id,
        );
        const allIds = this.exceptionShifts.map((shift) => shift.id);
        const idsToDelete = allIds.filter((id) => !selectedIds.includes(id));

        // Delete unselected exception shifts
        if (idsToDelete.length > 0) {
          await shiftsService.handleExceptionShifts({
            action: "clear",
            exceptionShiftIds: idsToDelete,
          });
        }

        // Store selected exception shift IDs to move to new recurring shift after save
        if (selectedIds.length > 0) {
          this.pendingSaveData.exceptionShiftIds = selectedIds;
        }

        // Show success message
        const messages = [];
        if (idsToDelete.length > 0) {
          messages.push(
            `${idsToDelete.length} exception shift(s) will be deleted`,
          );
        }
        if (selectedIds.length > 0) {
          messages.push(
            `${selectedIds.length} exception shift(s) will be kept`,
          );
        }
        if (messages.length > 0) {
          this.$notifySuccess(messages.join(", "));
        }

        this.showExceptionShiftsModal = false;
        this.exceptionShifts = [];
        this.selectedExceptionShifts = [];
        this.proceedWithSave();
      } catch (error) {
        this.$notifyError("Failed to handle exception shifts");
      }
    },

    cancelSave() {
      this.showExceptionShiftsModal = false;
      this.exceptionShifts = [];
      this.selectedExceptionShifts = [];
      this.pendingSaveData = null;
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      // Parse date string manually to avoid timezone issues
      const [year, month, day] = dateString.split("T")[0].split("-");
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatTime(dateTimeString) {
      if (!dateTimeString) return "N/A";
      const date = new Date(dateTimeString);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },

    formatNames(peopleArray) {
      if (!peopleArray || peopleArray.length === 0) return "None";
      return peopleArray.map((person) => person.name).join(", ");
    },
  },
};
</script>

<style scoped>
/* Auth warning banner */
.auth-warning-banner {
  margin: 1rem 0;
  padding: 0 1rem;
}
.auth-warning-banner .alert-warning {
  background-color: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.4);
  color: #ffc107;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
.auth-warning-banner .alert-warning i {
  margin-top: 2px;
}
.auth-acknowledge-checkbox {
  color: #e0e0e0 !important;
  margin-top: 0.25rem;
}

/* Recurring Edit Explanation Modal */
.recurring-edit-explanation {
  font-size: 15px;
  line-height: 1.6;
  color: white;
}

.recurring-edit-explanation p {
  margin-bottom: 15px;
  color: white;
}

.recurring-edit-explanation strong {
  color: white;
}

.recurring-edit-explanation ul {
  margin-top: 10px;
  margin-bottom: 15px;
  padding-left: 25px;
  color: white;
}

.recurring-edit-explanation li {
  margin-bottom: 8px;
  color: white;
}

.text-center {
  text-align: center;
  margin-top: 1rem;
}

.el-button {
  margin: 0 0.5rem;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  /* Make modal more mobile-friendly */
  :deep(.shift-modal .el-dialog) {
    width: 95% !important;
    max-height: 90vh !important;
  }

  /* Add consistent vertical spacing between all field sections */
  .shift-type-toggle-section,
  .date-fields-section,
  .shift-details-row,
  .client-caregiver-row,
  .client-address-section,
  .geocode-input-row,
  .vr-alternate-fields,
  .separate-addresses-section,
  :deep(.row.mb-3) {
    margin-bottom: 1.25rem !important;
  }

  /* Stack columns vertically on mobile */
  .shift-details-row,
  .client-caregiver-row,
  .address1-address2-row,
  .date-time-row,
  .date-fields-flex,
  .city-state-zip-row {
    flex-direction: column;
    gap: 0;
  }

  .date-field-col {
    width: 100%;
    padding: 0 4px !important;
    margin-bottom: 1.25rem;
  }

  .date-field-col:last-child {
    margin-bottom: 0;
  }

  /* Recurring shift date/time fields - make them stack with proper spacing */
  .date-time-row > * {
    margin-bottom: 1.25rem;
  }

  .date-time-row > *:last-child {
    margin-bottom: 0;
  }

  /* Make fields within date-time-row stack vertically with spacing */
  .date-time-row :deep(.row.mb-3) {
    flex-direction: column;
    gap: 1.25rem;
  }

  .date-time-row :deep(.row.mb-3 > div) {
    width: 100% !important;
    max-width: 100% !important;
    flex: none !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .shift-details-row .field-section,
  .client-section,
  .caregiver-section,
  .service-codes-section,
  .address1-section,
  .address2-section {
    width: 100%;
  }

  /* Make fields wider on mobile by reducing side padding */
  .shift-details-row .field-section,
  .client-section,
  .caregiver-section,
  .service-codes-section,
  .address1-section,
  .address2-section,
  .service-codes-field,
  .client-address-section,
  .vr-address-display,
  .weekdays-selection,
  .geocode-section-spacing,
  .date-fields-section,
  .vr-alternate-fields,
  .full-width-field {
    padding: 0 4px !important;
  }

  /* Add spacing between fields within the same row when they stack */
  .shift-details-row .field-section:not(:last-child),
  .client-section,
  .service-codes-section,
  .address1-section {
    margin-bottom: 1.25rem;
  }

  /* Make date/time fields stack with spacing */
  .date-fields-section :deep(.row.mb-3) {
    flex-direction: column;
    gap: 1.25rem;
  }

  .date-fields-section :deep(.row.mb-3 > div) {
    width: 100% !important;
    max-width: 100% !important;
    flex: none !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  /* Make city, state, zip stack vertically on mobile */
  .city-state-zip-row :deep(.row.mb-3) {
    flex-direction: column;
    gap: 1.25rem;
  }

  .city-state-zip-row :deep(.row.mb-3 > div) {
    width: 100% !important;
    max-width: 100% !important;
    flex: none !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  /* Make notes field full width */
  :deep(.el-form > .row.mb-3:last-of-type) {
    padding: 0 4px !important;
  }

  :deep(.el-form > .row.mb-3:last-of-type > div) {
    width: 100% !important;
    max-width: 100% !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  /* Stack geocode input and button */
  .geocode-input-row {
    flex-direction: column;
    align-items: stretch;
    gap: 1.25rem;
    padding: 0 4px !important;
  }

  .geocode-input-field {
    width: 100%;
    padding: 0 !important;
  }

  .geocode-input-field :deep(.row.mb-3) {
    margin-bottom: 0 !important;
  }

  .geocode-input-field :deep(.row.mb-3 > div) {
    width: 100% !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .generate-geocode-button {
    width: 100%;
    height: 44px;
    margin-left: 4px;
    margin-right: 4px;
  }

  /* Make address header responsive */
  .address-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 0 4px;
  }

  .client-header {
    padding: 0 4px;
  }

  .caregiver-header {
    padding: 0 4px;
    justify-content: flex-start;
  }

  .address-header-actions {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  /* Weekdays label alignment */
  .weekdays-label {
    padding: 0 4px;
  }

  /* Service codes field label */
  .service-codes-field .field-label {
    padding: 0 4px;
    display: block;
  }

  /* Adjust address2 header positioning */
  .address2-section {
    position: relative;
  }

  .address2-header {
    padding: 0 4px;
  }

  /* Hide desktop checkbox on mobile */
  .alternate-location-toggle-repositioned {
    display: none !important;
  }

  /* Show mobile-top checkbox on mobile */
  .alternate-location-toggle-mobile-top {
    display: block !important;
  }

  /* Make weekdays wrap better on mobile */
  :deep(.weekday-checkboxes .el-checkbox-group) {
    gap: 1rem;
    justify-content: flex-start;
    padding: 0 4px;
  }

  .weekdays-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .weekday-checkboxes {
    width: 100%;
  }

  /* Improve button sizes for touch */
  .toggle-button {
    min-height: 44px;
    font-size: 16px;
  }

  .el-button {
    min-height: 44px;
    font-size: 16px;
    padding: 12px 20px;
  }

  /* Make text more readable */
  .field-label {
    font-size: 16px;
  }

  .address-text {
    font-size: 16px;
  }

  :deep(.include-admins-toggle .el-checkbox__label),
  :deep(.alternate-location-toggle .el-checkbox__label),
  :deep(.alternate-location-toggle-repositioned .el-checkbox__label),
  :deep(.separate-addresses-toggle .el-checkbox__label),
  :deep(.use-client-address-toggle .el-checkbox__label),
  :deep(.use-alternate-address-toggle .el-checkbox__label) {
    font-size: 16px !important;
  }

  /* Improve form field sizing */
  :deep(.el-input__inner),
  :deep(.el-textarea__inner) {
    font-size: 16px !important;
    min-height: 44px;
  }

  /* Stack footer buttons on very small screens */
  .text-center {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
    padding: 0 !important;
  }

  .text-center .el-button {
    width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box;
  }

  /* Adjust dialog footer padding on mobile to prevent button cutoff */
  :deep(.shift-modal .el-dialog__footer) {
    padding: 15px 15px 20px 15px !important;
    position: sticky;
    bottom: 0;
    background-color: #2c2c2c;
    border-top: 1px solid #444;
    z-index: 10;
  }

  /* Add bottom padding to dialog body to prevent content from being hidden behind footer */
  :deep(.shift-modal .el-dialog__body) {
    padding-bottom: 20px !important;
    overflow-y: auto;
    max-height: calc(90vh - 120px) !important;
  }

  /* Adjust map link buttons */
  .map-link-button {
    font-size: 16px !important;
    padding: 8px 0 !important;
  }

  /* Make address content responsive */
  .address-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-left: 4px;
    margin-right: 4px;
  }

  /* Address selector dropdown */
  .address-selector {
    padding: 0 4px !important;
  }

  /* VR alternate address display */
  .vr-address-display .address-content {
    margin-left: 0;
    margin-right: 0;
  }

  .vr-address-header {
    padding: 0 4px;
  }

  /* Adjust toggle buttons for mobile - stack vertically */
  .button-toggle-row {
    width: calc(100% - 8px);
    flex-direction: column;
    margin-left: 4px;
    margin-right: 4px;
  }

  .toggle-button {
    width: 100%;
    min-width: 0;
  }

  .shift-type-toggle-section,
  .payer-toggle-section {
    padding: 0 !important;
  }

  .toggle-button:first-child {
    border-radius: 4px 4px 0 0;
  }

  .toggle-button:not(:first-child) {
    border-left: 1px solid;
    border-top: none;
  }

  .toggle-button:last-child {
    border-radius: 0 0 4px 4px;
  }

  /* Make date field full width on its own line */
  :deep(.date-fields-section .row.mb-3) {
    flex-wrap: wrap;
  }

  :deep(.date-fields-section .row.mb-3 > div) {
    flex-basis: 100%;
    max-width: 100%;
  }

  /* Adjust time picker width to prevent am/pm from being cut off */
  :deep(.el-time-picker) {
    width: 100% !important;
  }

  :deep(.el-date-editor.el-input) {
    width: 100% !important;
  }

  :deep(.el-time-panel) {
    width: 100% !important;
    max-width: 320px !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
  }

  :deep(.el-time-panel__content) {
    max-height: 240px !important;
    overflow: auto !important;
  }

  :deep(.el-time-spinner__wrapper) {
    max-height: 200px !important;
    width: 33.33% !important;
  }

  :deep(.el-time-spinner) {
    width: 100% !important;
  }

  :deep(.el-time-panel__footer) {
    padding: 8px !important;
  }
}

/* Shift Details Row (Shift Type, Payer, Service Codes) */
.shift-details-row {
  display: flex;
  gap: 0;
  margin-bottom: 1rem;
}

.shift-details-row .field-section {
  flex: 1;
  padding: 0 8px;
}

/* Remove double padding from AIVRow columns inside field-section */
:deep(.shift-details-row .field-section .row.mb-3 > div) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.service-codes-field {
  padding: 0 8px;
}

.service-codes-field .field-label {
  display: block;
  margin-bottom: 2px;
}

/* Client and Caregiver Row */
.client-caregiver-row {
  display: flex;
  gap: 0;
  margin-bottom: 1rem;
}

.client-section {
  flex: 1;
  padding: 0 8px;
}

.selected-clients-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.client-chip {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.client-help-text {
  display: block;
  margin-top: 4px;
  color: #909399;
  font-size: 11px;
  line-height: 1.3;
}

.caregiver-section {
  flex: 1;
  padding: 0 8px;
}

.service-codes-section {
  flex: 1;
  padding: 0 8px;
}

.client-header,
.caregiver-header {
  display: flex;
  align-items: center;
  min-height: 22px;
  margin-bottom: 0;
  padding-bottom: 0;
}

.client-header {
  justify-content: flex-start;
}

.caregiver-header {
  justify-content: space-between;
  gap: 1rem;
}

.field-label {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}

.include-admins-toggle {
  font-size: 14px;
  margin-bottom: 0;
}

:deep(.include-admins-toggle .el-checkbox__label) {
  font-weight: 500;
  color: #fff !important;
  font-size: 14px;
}

.full-width-field {
  padding: 0 8px;
  margin-bottom: 1rem;
}

.full-width-field .field-label {
  display: block;
  margin-bottom: 0;
}

.alternate-location-toggle {
  font-size: 14px;
  margin-bottom: 0;
}

:deep(.alternate-location-toggle .el-checkbox__label) {
  font-weight: 500;
  color: #fff !important;
  font-size: 14px;
}

.vr-alternate-fields {
  margin-bottom: 1rem;
  margin-top: 0;
  padding: 0;
}

/* Separate Clock-In/Out Addresses Section */
.separate-addresses-toggle-section {
  margin-bottom: 0;
  margin-top: 0;
  padding: 0 8px;
  text-align: right;
}

.client-header-row .separate-addresses-toggle-section {
  flex-shrink: 0;
}

.separate-addresses-section {
  margin-bottom: 1rem;
  margin-top: 1rem;
  padding: 0 8px;
}

.separate-addresses-toggle {
  font-size: 14px;
  margin-bottom: 0;
}

:deep(.separate-addresses-toggle .el-checkbox__label) {
  font-weight: 500 !important;
  color: #fff !important;
  font-size: 14px !important;
}

.separate-addresses-toggle :deep(.el-checkbox__label) {
  font-weight: 500 !important;
  color: #fff !important;
  font-size: 14px !important;
}

.separate-addresses-fields {
  margin-top: 1rem;
}

.clock-address-subsection {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.subsection-label {
  font-weight: 600;
  color: #fff;
  font-size: 14px;
}

.use-client-address-toggle {
  font-size: 14px;
  margin-bottom: 0;
}

:deep(.use-client-address-toggle .el-checkbox__label) {
  font-weight: 500 !important;
  color: #fff !important;
  font-size: 14px !important;
}

.use-client-address-toggle :deep(.el-checkbox__label) {
  font-weight: 500 !important;
  color: #fff !important;
  font-size: 14px !important;
}

.use-alternate-address-toggle {
  font-size: 14px;
  margin-bottom: 0;
}

:deep(.use-alternate-address-toggle .el-checkbox__label) {
  font-weight: 500 !important;
  color: #fff !important;
  font-size: 14px !important;
}

.use-alternate-address-toggle :deep(.el-checkbox__label) {
  font-weight: 500 !important;
  color: #fff !important;
  font-size: 14px !important;
}

.alternate-address-fields {
  margin-top: 0;
}

/* Restore spacing between rows within VR alternate fields */
:deep(.vr-alternate-fields .row.mb-3) {
  margin-bottom: 1rem !important;
}

/* Address1 and Address2 Row */
.address1-address2-row {
  display: flex;
  gap: 0;
  margin-bottom: 1rem;
}

.address1-section {
  flex: 1;
  padding: 0 8px;
}

.address2-section {
  flex: 1;
  padding: 0 8px;
  position: relative;
}

.address2-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  padding-bottom: 0;
}

.alternate-location-toggle-repositioned {
  position: absolute;
  top: 0;
  right: 8px;
  font-size: 14px;
  margin-bottom: 0;
}

:deep(.alternate-location-toggle-repositioned .el-checkbox__label) {
  font-weight: 500;
  color: #fff !important;
  font-size: 14px;
}

/* Mobile-only checkbox at top of VR alternate fields - hidden on desktop */
.alternate-location-toggle-mobile-top {
  display: none;
  font-size: 14px;
  margin-bottom: 1rem;
  padding: 0 8px;
}

:deep(.alternate-location-toggle-mobile-top .el-checkbox__label) {
  font-weight: 500;
  color: #fff !important;
  font-size: 14px;
}

/* Remove double padding from AIVRow in address sections */
:deep(.address1-section .row.mb-3),
:deep(.address2-section .row.mb-3) {
  margin-bottom: 0 !important;
}

:deep(.address1-section .row.mb-3 > div),
:deep(.address2-section .row.mb-3 > div) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.vr-address-display {
  margin-top: 1rem;
  margin-bottom: 0;
  padding: 0 8px;
}

.vr-address-header {
  margin-bottom: 0;
  padding-bottom: 0;
}

.map-link-button {
  color: #409eff !important;
  padding: 0 !important;
  font-size: 14px;
}

.map-link-button:hover {
  color: #66b1ff !important;
}

.map-link-button i {
  margin-right: 4px;
}

.client-address-section {
  margin-bottom: 1rem;
  margin-top: 0;
  padding: 0 8px;
}

.client-address-section.no-bottom-margin {
  margin-bottom: 0;
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  padding-bottom: 0;
}

.address-header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.address-selector {
  margin-bottom: 0;
  padding: 0;
}

.address-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background-color: #444;
  border-radius: 4px;
  border: 1px solid #666;
}

.address-text {
  color: #fff;
  font-size: 14px;
  flex-grow: 1;
  margin-right: 1rem;
}

.geocode-section-spacing {
  margin-top: 0;
  margin-bottom: 1rem;
  padding: 0;
}

/* Make placeholder text darker when textarea is focused/selected */
:deep(.el-textarea__inner:focus::placeholder) {
  color: #999 !important;
}

/* Force textarea focus colors to stay consistent */
:deep(.el-form .el-form-item .el-textarea .el-textarea__inner:focus),
:deep(.el-textarea .el-textarea__inner:focus),
:deep(textarea.el-textarea__inner:focus) {
  border-color: #ffffff !important;
  box-shadow: none !important;
  background-color: #656565 !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: normal !important;
  -webkit-text-fill-color: #ffffff !important;
}

/* Shift type toggle section styles */
.shift-type-toggle-section {
  margin-bottom: 1rem;
  padding: 0;
  background-color: transparent;
  border: none;
}

.button-toggle-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin: 0;
}

.toggle-button {
  border-radius: 0;
  min-width: 120px;
  margin: 0;
}

.toggle-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.toggle-button:not(:first-child) {
  border-left: none;
}

.toggle-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

:deep(.toggle-button.el-button--default) {
  background-color: #606266;
  border-color: #606266;
  color: #fff;
}

:deep(.toggle-button.el-button--default:hover) {
  background-color: #737373;
  border-color: #737373;
  color: #fff;
}

:deep(.toggle-button.el-button--primary) {
  background-color: #409eff;
  border-color: #409eff;
  color: #fff;
}

/* Payer type toggle section styles */
.payer-toggle-section {
  margin-bottom: 1rem;
  padding: 0;
  background-color: transparent;
  border: none;
}

/* Date fields section */
.date-fields-section {
  margin-bottom: 1rem;
}

.date-fields-flex {
  display: flex;
  gap: 0;
}

.date-field-col {
  flex: 1;
  padding: 0 8px;
}

.date-field-col .field-label {
  display: block;
  margin-bottom: 2px;
}

.date-time-row {
  display: flex;
  gap: 1rem;
}

.date-time-row > * {
  flex: 1;
}

/* Ensure all field rows consistently fill the full width */
:deep(.row.mb-3) {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
  margin-bottom: 0 !important;
}

:deep(.row.mb-3 > div) {
  padding-left: 8px;
  padding-right: 8px;
}

/* Override bootstrap column classes to ensure consistent field sizing */
:deep(.col-md-4),
:deep(.col-md-6),
:deep(.col-md-12) {
  flex: 1;
  max-width: none;
}

/* Make sure all input elements fill their container and have consistent height */
:deep(.w-100),
:deep(.el-input),
:deep(.el-input__inner),
:deep(.el-date-editor),
:deep(.el-date-editor.el-input) {
  width: 100% !important;
}

/* Ensure consistent height for all inputs to match AIVSelect (40px) */
:deep(.el-input__inner),
:deep(.el-date-editor .el-input__inner),
:deep(.el-date-editor.el-input .el-input__inner),
:deep(.el-select .el-input__inner) {
  height: 40px !important;
  line-height: 40px !important;
}

/* Ensure all form fields have consistent box-sizing */
:deep(.el-input),
:deep(.el-select),
:deep(.el-date-editor),
:deep(.aiv-select) {
  box-sizing: border-box !important;
}

/* Weekdays selection styles */
.weekdays-selection {
  margin: 0.75rem 0 0;
  padding: 0 8px;
}

.weekdays-row {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.weekdays-label {
  flex-shrink: 0;
  min-width: 120px;
}

.weekday-checkboxes {
  padding: 0;
  flex: 1;
}

:deep(.weekday-checkboxes .el-checkbox-group) {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
}

:deep(.weekday-checkboxes .el-checkbox) {
  margin: 0;
  padding: 0.5rem;
}

:deep(.weekday-checkboxes .el-checkbox__label) {
  color: #fff !important;
  padding-left: 8px;
}

/* City, State, ZIP row */
.city-state-zip-row {
  display: flex;
  width: 100%;
}

/* Add spacing after City/State/ZIP row ONLY for Clock In/Out alternate addresses */
.alternate-address-fields .city-state-zip-row {
  margin-bottom: 1rem;
}

/* Geocode input row with button */
.geocode-input-row {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
}

.geocode-input-field {
  flex: 1;
}

/* Override the margin-bottom from the row inside geocode-input-field */
:deep(.geocode-input-field .row.mb-3) {
  margin-bottom: 0 !important;
}

.generate-geocode-button {
  flex-shrink: 0;
  height: 40px;
  margin-bottom: 4px;
  white-space: nowrap;
}

.generate-geocode-button i {
  margin-right: 4px;
}

/* Address Selection View Styles */
.address-selection-view {
  padding: 2rem;
  min-height: 300px;
}

.address-selection-content {
  max-width: 800px;
  margin: 0 auto;
}

.instruction-text {
  font-size: 16px;
  color: white;
  margin-bottom: 2rem;
  text-align: center;
}

.client-address-section {
  margin-bottom: 2rem;
}

.client-name {
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin-bottom: 1rem;
}

/* Group client styles */
.client-address-group {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.client-address-group:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.client-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.client-name-label {
  font-size: 16px;
  font-weight: 600;
  color: white;
  margin-bottom: 0;
  padding-left: 0;
  flex-shrink: 0;
}

/* Caregiver Task Assignment View Styles */
.caregiver-task-assignment-view {
  padding: 2rem;
  min-height: 300px;
}

.client-task-grid {
  display: grid;
  gap: 1.5rem;
  width: 100%;
  grid-template-columns: 1fr;
}

@media (min-width: 1400px) {
  .client-task-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 900px) and (max-width: 1399px) {
  .client-task-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.client-task-assignment-group {
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.client-task-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.client-task-header h4 {
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin: 0;
}

.task-assignment-row {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
}

.task-assignment-row:last-child {
  margin-bottom: 0;
}

.task-label {
  min-width: 120px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  margin-bottom: 0;
}

.medications-task-row {
  min-height: 40px;
}

.medications-checkbox {
  min-width: 120px;
  margin-bottom: 0;
}

.medications-checkbox ::v-deep .el-checkbox__label {
  color: white !important;
}

.medications-checkbox.is-checked ::v-deep .el-checkbox__label {
  color: white !important;
}

/* Additional caregivers (shadow/training) editor */
.additional-caregiver-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.additional-caregiver-select {
  flex: 1;
  min-width: 0;
}

.additional-caregiver-remove {
  flex: 0 0 auto;
  padding: 4px 8px;
}

.add-additional-caregiver {
  padding: 0;
}
</style>
